import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { toast, Id as ToastId } from 'react-toastify';
import { Container, Row, Col, ListGroup, Form, InputGroup, Badge, Button, Tab, Tabs, Modal, Card, Tooltip, OverlayTrigger, Offcanvas, Popover } from 'react-bootstrap';
import { 
  Search, 
  Envelope, 
  EnvelopeFill,
  Lightning,
  ExclamationTriangle,
  Send,
  Paperclip,
  Link,
  EmojiSmile,
  Image,
  Lock,
  Calendar,
  Trash,
  X,
  PersonDash,
  Archive,
  EnvelopeCheck,
  EnvelopeOpen,
  Collection,
  Folder,
  FolderPlus,
  ArrowClockwise,
  InfoCircle,
  QuestionCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ThreeDotsVertical,
  Plus,
  List,
  ArrowLeft,
  SlashCircle,
  GearFill,
  TrashFill
} from 'react-bootstrap-icons';
import {
  useGetEmailAccountsQuery,
  useSendEmailMutation,
  useGetEmailsQuery,
  useGetEmailSentStatsQuery,
  useLazyGetEmailOriginalQuery,
  useLazyGetEmailAttachmentQuery,
  useGetDomainsQuery,
  useUpdateEmailMutation,
  useGetRulesEngineConfigQuery,
  useGetUserPreferencesQuery,
  useUpdateRulesEngineConfigMutation,
  useUpdateUserPreferencesMutation,
  usePurgeTrashMutation,
  usePermanentDeleteEmailsMutation,
  useCreateLeadMutation,
  useCreateContactMutation,
  useCreateClientMutation,
  useGetLeadsQuery,
  useLazyGetLeadsQuery,
  useGetContactsQuery,
  useLazyGetContactsQuery,
  useGetClientsQuery,
  useLazyGetClientsQuery,
  useUpdateLeadMutation,
  useUpdateContactMutation,
  useUpdateClientMutation
} from '../services/api';
import { useAccount } from '../contexts/AccountContext';
import {
  getTenantTimezoneFromPreferencesData,
  toLocaleStringInTimezone,
} from '../utils/tenantTimezone';
import { preferencesTenantWriteSpread, useTenantIdForPreferences } from '../hooks/useTenantIdForPreferences';
import { extractEmailAddresses } from '../utils/crmEmailThreads';
import ScriptLibraryPanel from './ScriptLibraryPanel';
import { extractFirstNameFromAddressOrName } from '../utils/scriptTemplates';

// TypeScript interfaces
interface EmailAccount {
  id: string;
  email: string;
  username: string;
  displayName?: string;
  domain: string;
  status: string;
  unreadCount: number;
  lastActivity: string;
  avatar?: string; // Optional profile picture URL
}

interface AttachmentMeta {
  name: string;
  s3Key: string;
  contentType?: string;
  size?: number;
  contentId?: string;
}

interface Email {
  id: string;
  accountId?: string;
  emailAccount?: string;
  unifiedPk?: string;
  unifiedSk?: string;
  messageId?: string;
  inReplyTo?: string;
  references?: string;
  s3Key?: string;
  s3Bucket?: string;
  folder?: string; // Custom user-defined folder (e.g., "Warmup")
  sender?: string;
  from?: string;
  senderAddress?: string;
  senderName?: string;
  fromHeader?: string;
  senderHeader?: string;
  returnPath?: string;
  replyTo?: string;
  envelopeFrom?: string;
  to?: string[] | string;
  cc?: string[] | string;
  bcc?: string[] | string;
  subject: string;
  preview?: string;
  text?: string;
  html?: string;
  date: string;
  isRead: boolean;
  isImportant: boolean;
  hasAttachment?: boolean;
  attachmentCount?: number;
  attachments?: AttachmentMeta[];
  isSystemEmail?: boolean; // Flag to identify system/notification emails
  isSent?: boolean; // Flag to identify sent emails
  isDeleted?: boolean; // Flag to identify deleted emails (in trash)
  isArchived?: boolean; // Flag to identify archived emails
  isSpam?: boolean; // Flag to identify spam emails
  isQuarantined?: boolean; // Flag for quarantined inbound (recipient not in KG)
  quarantineReason?: string; // Reason for quarantine (e.g. recipient_not_in_kross_global)
  // Phase A (2026-05-14): per-row classification stamped at write time by
  // the central classifier. Possible values are the EmailKind union from
  // src/lib/classify-send-kind.ts on the API side: 'warmup' | 'campaign'
  // | 'transactional' | 'unknown'. Optional because (a) older rows
  // pre-date the stamping and (b) some non-email row types in the
  // unified table never carry it. The Warm Up system folder filter
  // keys on `emailKind === 'warmup'` directly so legacy rows without
  // the stamp are simply not surfaced — no migration required.
  emailKind?: string | null;
  status?: string; // Status label (e.g., "Out of office", "Lead", etc.)
}

interface BlocklistEntry {
  id?: string;
  value?: string;
  type?: 'email' | 'domain' | string;
  addedAt?: string;
  source?: string;
}

const sanitizeHeaderToken = (value: any): string => {
  const v = String(value || '').trim();
  if (!v) return '';
  const lowered = v.toLowerCase();
  if (lowered === '[object object]' || lowered === 'object object') return '';
  return v;
};

const normalizeSenderToken = (value: any): string => {
  const v = sanitizeHeaderToken(value);
  if (!v) return '';
  if (v.toLowerCase() === 'unknown') return '';
  return v;
};

const deriveSenderLabel = (email: any): string => {
  const candidates = [
    email?.sender,
    email?.from,
    email?.fromHeader,
    email?.senderHeader,
    email?.senderAddress,
    email?.envelopeFrom,
    email?.returnPath,
  ];
  for (const candidate of candidates) {
    const normalized = normalizeSenderToken(candidate);
    if (normalized) return normalized;
  }
  return 'unknown';
};

/** Join header fields that may carry the mailbox address (RFC5322 From, etc.). */
const collectSentFromAddressProbe = (email: any): string =>
  [
    email?.from,
    email?.fromHeader,
    email?.senderAddress,
    email?.senderHeader,
    email?.sender,
  ]
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join(' ');

const AUTO_REPLY_SIGNAL_PATTERNS = [
  'automatic reply',
  'auto-reply',
  'autoreply',
  'out of office',
  'out of the office',
  'vacation reply',
];

/**
 * One full refresh-icon rotation; must match `--kg-unibox-refresh-spin-duration` in index.css.
 * QA: Re-check spinner duration feels right under production-sized inboxes or DevTools network throttling (localhost may only show ~one rotation).
 */
const UNIBOX_REFRESH_MIN_SPIN_MS = 1000;

const isReplyLikeSubject = (value: unknown): boolean =>
  /^(re|aw|sv|odp|antw)\s*:/i.test(String(value || '').trim());

const isMeaningfulMessageId = (value: unknown): boolean => {
  const raw = String(value || '').trim();
  if (!raw) return false;
  const lowered = raw.toLowerCase();
  if (lowered === 'undefined' || lowered === 'null' || lowered === '[object object]' || lowered === 'object object') {
    return false;
  }
  if (lowered.includes('undefined@')) return false;
  return raw.includes('@');
};

const isReplyOrAutomaticInboundEmail = (email: any): boolean => {
  if (!email || typeof email !== 'object') return false;
  const subject = String(email.subject || '');
  if (isReplyLikeSubject(subject)) return true;
  if (isMeaningfulMessageId(email.inReplyTo)) return true;
  const combined = `${subject}\n${String(email.preview || '')}\n${String(email.text || '')}\n${String(email.html || '')}`.toLowerCase();
  return AUTO_REPLY_SIGNAL_PATTERNS.some((signal) => combined.includes(signal));
};

const isMailboxEmailLikeRecord = (email: any): boolean => {
  const emailAccount = String(email?.emailAccount || '').toLowerCase().trim();
  const unifiedPk = String(email?.PK || email?.pk || email?.unifiedPk || '').trim();
  const recordType = String(email?.type || '').toLowerCase().trim();
  const mailboxLike = emailAccount.includes('@') || unifiedPk.startsWith('EA#');
  if (!mailboxLike) return false;
  if (recordType === 'ndr_delivery_audit' || recordType === 'delay_bounce_suppression') return false;
  const from = normalizeSenderToken(
    email?.from || email?.sender || email?.senderAddress || email?.fromHeader || email?.senderHeader
  );
  const subject = String(email?.subject || '').trim();
  const text = String(email?.text || '').trim();
  const html = String(email?.html || '').trim();
  const hasTo = Array.isArray(email?.to)
    ? email.to.some((entry: any) => String(entry || '').trim().length > 0)
    : String(email?.to || '').trim().length > 0;
  return Boolean(from || subject || text || html || hasTo);
};

const extractEmailsFromValue = (value: unknown): string[] => {
  const source = String(value || '').trim();
  if (!source) return [];
  const matches = source.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || [];
  return Array.from(new Set(matches.map((m) => m.toLowerCase().trim()).filter(Boolean)));
};

const getSenderEmailCandidates = (email: Email): string[] => {
  const candidates = [
    ...(email.senderAddress ? [String(email.senderAddress)] : []),
    ...(email.envelopeFrom ? [String(email.envelopeFrom)] : []),
    ...(email.returnPath ? [String(email.returnPath)] : []),
    ...(email.replyTo ? [String(email.replyTo)] : []),
    ...(email.fromHeader ? [String(email.fromHeader)] : []),
    ...(email.senderHeader ? [String(email.senderHeader)] : []),
    ...(email.from ? [String(email.from)] : []),
    ...(email.sender ? [String(email.sender)] : []),
  ];
  return Array.from(
    new Set(
      candidates
        .flatMap((value) => extractEmailsFromValue(value))
        .map((value) => value.toLowerCase().trim())
        .filter(Boolean)
    )
  );
};

const parseRecipientList = (value: string): string[] => extractEmailsFromValue(value);

const deriveDisplayNameFromEmailAddress = (email: string): string => {
  const localPart = String(email || '').split('@')[0] || '';
  const cleaned = localPart
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return toTitleCaseWords(cleaned);
};

const normalizeAttachmentMetaList = (raw: any): AttachmentMeta[] => {
  const source = Array.isArray(raw) ? raw : [];
  const seen = new Set<string>();
  const out: AttachmentMeta[] = [];
  for (let i = 0; i < source.length; i += 1) {
    const item = source[i] || {};
    const s3Key = String(item.s3Key || item.key || item.S3Key || '').trim();
    if (!s3Key || seen.has(s3Key)) continue;
    seen.add(s3Key);
    const rawName = String(item.name || item.fileName || item.filename || '').trim();
    const fallbackName = (() => {
      const lastPart = String(s3Key).split('/').pop() || '';
      const decoded = (() => {
        try {
          return decodeURIComponent(lastPart);
        } catch {
          return lastPart;
        }
      })();
      return decoded.replace(/^\d{2,4}-/, '') || `attachment-${i + 1}.bin`;
    })();
    const contentType = String(item.contentType || item.ContentType || '').trim();
    const sizeRaw = Number(item.size ?? item.contentLength ?? item.ContentLength ?? 0);
    const contentId = String(item.contentId || item.ContentId || item.cid || '').trim();
    out.push({
      name: rawName || fallbackName,
      s3Key,
      ...(contentType ? { contentType } : {}),
      ...(Number.isFinite(sizeRaw) && sizeRaw > 0 ? { size: sizeRaw } : {}),
      ...(contentId ? { contentId } : {}),
    });
  }
  return out;
};

const formatAttachmentSize = (bytes: number | undefined): string => {
  const size = Number(bytes || 0);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

const COMPOSE_CONFIDENTIAL_SUBJECT_PREFIX = '[Confidential]';
const MAX_COMPOSE_ATTACHMENTS = 10;
const MAX_COMPOSE_ATTACHMENT_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_COMPOSE_TOTAL_ATTACHMENT_SIZE_BYTES = 6 * 1024 * 1024;

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || '');
      const commaIdx = raw.indexOf(',');
      resolve(commaIdx >= 0 ? raw.slice(commaIdx + 1) : raw);
    };
    reader.onerror = () => reject(reader.error || new Error(`Failed to read file ${file.name}`));
    reader.readAsDataURL(file);
  });

interface ComposeData {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  fromAccount: string;
}

interface ComposeAttachment {
  id: string;
  name: string;
  contentType: string;
  size: number;
  contentBase64: string;
  lastModified: number;
}

interface EmailOutboundAttachment {
  name: string;
  contentType: string;
  contentBase64: string;
  size?: number;
}

type AutoFilterField = 'subject' | 'body' | 'subject_or_body';
type AutoFilterMatchType = 'contains' | 'regex';
interface AutoFilterRule {
  id: string;
  enabled: boolean;
  field: AutoFilterField;
  matchType: AutoFilterMatchType;
  pattern: string;
  folder: string;
}

interface SystemAlertFilterRule {
  id: string;
  type: 'system_alert_filter';
  enabled: boolean;
  condition: {
    from: string;
  };
  action: {
    move_to_folder: string;
  };
  createdAt?: string;
}

interface EmailPayload {
  from: string;
  fromName?: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
  bodyType: string;
  messageClass?: 'cold_outreach' | 'transactional' | 'newsletter';
  accountId?: string;
  region?: string;
  attachments?: EmailOutboundAttachment[];
  // Set ONLY when the user clicks Reply / Reply All in Unibox. Backend skips
  // the Rules Engine (lifecycle gating like "Active Opportunity" / "Dead
  // Opportunity") for manual replies so human conversation isn't blocked. This
  // bypass is honoured for BOTH soft-block and hard-stop tiers (replies are
  // always allowed; only future Do Not Contact / Blocked compliance labels
  // will hard-block replies in a later hotfix). Automation (campaigns / EA
  // scheduled sends / Instantly / kg-smtp) MUST NOT set this — the backend
  // enforces a JWT gate that ignores the flag from service-to-service callers.
  // See hotfix/rules-engine-allow-replies + hotfix/rules-engine-harden.
  isManualReply?: boolean;
  // Set ONLY when the user explicitly clicks "Send anyway" on the Rules Engine
  // block confirmation modal. Honoured by the backend ONLY for SOFT-BLOCK tier
  // stages (Active Opportunity, Current Client). HARD-STOP-NEW-OUTREACH tier
  // stages (Dead Opportunity, Do Not Prospect) cannot be overridden by this
  // flag — only an actual Reply (isManualReply: true) bypasses them. The
  // backend also enforces the same JWT gate as isManualReply: this flag is
  // ignored for service-to-service callers. See hotfix/rules-engine-harden.
  manualSendAnywayOverride?: boolean;
}

// Compose origin: drives the isManualReply flag on send. Only 'reply' and
// 'reply-all' bypass the Rules Engine; 'forward' and 'new' remain subject
// to lifecycle rules because they initiate outreach to a chosen recipient.
type ComposeMode = 'new' | 'reply' | 'reply-all' | 'forward';

interface LeadDraftFormData {
  firstName: string;
  lastName: string;
  email: string;
  officePhone: string;
  cellPhone: string;
  title: string;
  organization: string;
  address: string;
  website: string;
  linkedInProfile: string;
  visibility: 'global' | 'personal';
  sourceFromLine: string;
}

type DuplicateEntityType = 'Lead' | 'Contact' | 'Client';
type CreateEntityType = 'Lead' | 'Contact' | 'Client';
interface StatusOption {
  name: string;
  color: string;
}

const CREATE_ENTITY_OPTIONS: CreateEntityType[] = ['Lead', 'Contact', 'Client'];
const CREATE_ENTITY_COLORS: Record<CreateEntityType, string> = {
  Lead: '#6c757d',
  Contact: '#0d6efd',
  Client: '#198754',
};
const getCreateEntityCollectionLabel = (entityType: CreateEntityType): string => {
  if (entityType === 'Lead') return 'Leads';
  if (entityType === 'Contact') return 'Contacts';
  return 'Clients';
};

interface DuplicateCandidate {
  key: string;
  id: string;
  type: DuplicateEntityType;
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  organization: string;
  reason: string;
  raw: any;
}

const ALL_EMAILS_FOLDER_LABEL = 'All Emails';
const ALL_EMAILS_FOLDER_API_VALUE = 'all-emails';
const SYSTEM_ALERTS_FOLDER_LABEL = 'System Alerts';
const SYSTEM_ALERTS_FOLDER_API_VALUE = 'system_alerts';
// Phase A (2026-05-13): protected `Warm Up` system folder. Same class
// as `Bounce` and `System Alerts` — locked from rename/delete, rendered
// with the gray `<Badge bg="secondary">System</Badge>`. Backed by a
// virtual API folder keyed on `emailKind === 'warmup'` (see
// `src/emails/get-emails.ts` Folder='warmup' branch). Surfaces both
// inbound and outbound warm-up traffic in one view, so the user no
// longer needs to maintain a per-tenant warm-up folder by hand.
// The legacy user-created `Warm up: KrossKastChannels` folder remains
// untouched during verification — both views can show the same row at
// once. See WARMUP_SYSTEM_FOLDER_ARCHITECTURE_PROPOSAL.md.
const WARMUP_SYSTEM_FOLDER_LABEL = 'Warm Up';
const DEFAULT_UNIBOX_FOLDERS = ['Bounce', SYSTEM_ALERTS_FOLDER_LABEL, WARMUP_SYSTEM_FOLDER_LABEL];
const FALLBACK_EMAIL_DATE_ISO = '1970-01-01T00:00:00.000Z';
const wholeNumberFormatter = new Intl.NumberFormat('en-US');
const compactWholeNumberFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const RESERVED_CUSTOM_FOLDER_NAMES = new Set([
  'inbox',
  'sent',
  'spam',
  'trash',
  'archive',
  'quarantined',
  'all',
  'all emails',
  'all-emails',
  'bounce',
  'system alerts',
  'system_alerts',
  // Phase A: protected `Warm Up` system folder name. Users cannot
  // create a custom folder with this name (would conflict with the
  // virtual system view keyed on `emailKind === 'warmup'`).
  'warm up',
  'warmup',
  'warm-up',
]);
const formatSentStatsCount = (value: unknown): string => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return '0';
  if (numericValue >= 100000) {
    return compactWholeNumberFormatter
      .format(numericValue)
      .replace(/K/g, 'k')
      .replace(/M/g, 'm')
      .replace(/B/g, 'b');
  }
  return wholeNumberFormatter.format(numericValue);
};
const normalizeFolderLabel = (value: unknown): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s*:\s*/g, ':')
    .replace(/\s+/g, ' ');
const isAllEmailsFolder = (value: unknown): boolean => {
  const normalized = normalizeFolderLabel(value);
  return normalized === normalizeFolderLabel(ALL_EMAILS_FOLDER_LABEL) || normalized === ALL_EMAILS_FOLDER_API_VALUE;
};
const isSystemFolderSelection = (value: unknown): boolean => {
  const normalized = normalizeFolderLabel(value);
  return normalized === 'quarantined' || isAllEmailsFolder(normalized);
};
const normalizeStatusLabel = (value: unknown): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
const DEFAULT_PRIMARY_STATUS_OPTIONS: StatusOption[] = [
  { name: 'Lead', color: '#6c757d' },
  { name: 'Contact', color: '#0d6efd' },
  { name: 'Client', color: '#198754' },
  { name: 'Interested', color: '#28a745' },
  { name: 'Meeting booked', color: '#6f42c1' },
  { name: 'Meeting completed', color: '#fd7e14' },
  { name: 'Won', color: '#20c997' },
];
const DEFAULT_MORE_STATUS_OPTIONS: StatusOption[] = [
  { name: 'Unknown', color: '#6c757d' },
  { name: 'Out of office', color: '#0dcaf0' },
  { name: 'Wrong person', color: '#6c757d' },
  { name: 'Not interested', color: '#dc3545' },
  { name: 'Lost', color: '#dc3545' },
  { name: 'Not interested at the moment', color: '#495057' },
];
const DEFAULT_STATUS_OPTIONS: StatusOption[] = [...DEFAULT_PRIMARY_STATUS_OPTIONS, ...DEFAULT_MORE_STATUS_OPTIONS];
const DEFAULT_STATUS_KEYS = new Set(DEFAULT_STATUS_OPTIONS.map((status) => normalizeStatusLabel(status.name)));
const UNKNOWN_STATUS_LABEL = 'Unknown';
const PROTECTED_STATUS_KEYS = new Set([normalizeStatusLabel(UNKNOWN_STATUS_LABEL)]);
const DEFAULT_STATUS_COLOR = '#6c757d';
type FolderAliasMap = Record<string, string[]>;

const normalizeFolderAliasMap = (raw: unknown): FolderAliasMap => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const result: FolderAliasMap = {};
  Object.entries(raw as Record<string, unknown>).forEach(([folderNameRaw, aliasListRaw]) => {
    const folderName = String(folderNameRaw || '').trim();
    const folderKey = normalizeFolderLabel(folderName);
    if (!folderName || !folderKey) return;
    const aliasList = Array.isArray(aliasListRaw) ? aliasListRaw : [];
    const dedup = new Map<string, string>();
    aliasList.forEach((aliasRaw) => {
      const alias = String(aliasRaw || '').trim();
      const aliasKey = normalizeFolderLabel(alias);
      if (!alias || !aliasKey || aliasKey === folderKey || dedup.has(aliasKey)) return;
      dedup.set(aliasKey, alias);
    });
    if (dedup.size > 0) {
      result[folderName] = Array.from(dedup.values()).slice(0, 20);
    }
  });
  return result;
};

const getAliasesForFolder = (folderAliases: FolderAliasMap, folderName: string): string[] => {
  const key = normalizeFolderLabel(folderName);
  if (!key) return [];
  for (const [name, aliases] of Object.entries(folderAliases || {})) {
    if (normalizeFolderLabel(name) !== key) continue;
    return Array.from(new Set((aliases || []).map((alias) => String(alias || '').trim()).filter(Boolean)));
  }
  return [];
};

const buildFolderAliasMapForRename = (
  current: FolderAliasMap,
  oldName: string,
  nextName: string
): FolderAliasMap => {
  const oldKey = normalizeFolderLabel(oldName);
  const nextKey = normalizeFolderLabel(nextName);
  const result: FolderAliasMap = {};
  const nextAliasDedup = new Map<string, string>();
  const addAlias = (value: unknown) => {
    const alias = String(value || '').trim();
    const aliasKey = normalizeFolderLabel(alias);
    if (!alias || !aliasKey || aliasKey === nextKey || nextAliasDedup.has(aliasKey)) return;
    nextAliasDedup.set(aliasKey, alias);
  };

  Object.entries(current || {}).forEach(([folderName, aliases]) => {
    const folderKey = normalizeFolderLabel(folderName);
    if (folderKey === oldKey || folderKey === nextKey) {
      (aliases || []).forEach(addAlias);
      return;
    }
    const dedup = new Map<string, string>();
    (aliases || []).forEach((aliasRaw) => {
      const alias = String(aliasRaw || '').trim();
      const aliasKey = normalizeFolderLabel(alias);
      if (!alias || !aliasKey || dedup.has(aliasKey)) return;
      if (aliasKey === oldKey) {
        addAlias(alias);
        return;
      }
      dedup.set(aliasKey, alias);
    });
    if (dedup.size > 0) {
      result[folderName] = Array.from(dedup.values());
    }
  });

  addAlias(oldName);
  if (nextAliasDedup.size > 0) {
    result[nextName] = Array.from(nextAliasDedup.values());
  }
  return result;
};

const pruneFolderAliases = (aliases: FolderAliasMap, folders: string[]): FolderAliasMap => {
  const knownByKey = new Map<string, string>();
  (folders || []).forEach((folder) => {
    const value = String(folder || '').trim();
    const key = normalizeFolderLabel(value);
    if (!value || !key || knownByKey.has(key)) return;
    knownByKey.set(key, value);
  });
  const result: FolderAliasMap = {};
  Object.entries(aliases || {}).forEach(([folderName, aliasList]) => {
    const folderKey = normalizeFolderLabel(folderName);
    const canonicalFolder = knownByKey.get(folderKey);
    if (!canonicalFolder) return;
    const dedup = new Map<string, string>();
    (aliasList || []).forEach((aliasRaw) => {
      const alias = String(aliasRaw || '').trim();
      const aliasKey = normalizeFolderLabel(alias);
      if (!alias || !aliasKey || aliasKey === folderKey || dedup.has(aliasKey)) return;
      dedup.set(aliasKey, alias);
    });
    if (dedup.size > 0) {
      result[canonicalFolder] = Array.from(dedup.values());
    }
  });
  return result;
};
const mergeUniqueFolders = (input: unknown[]): string[] => {
  const byKey = new Map<string, string>();
  (input || []).forEach((entry) => {
    const value = String(entry || '').trim();
    const key = normalizeFolderLabel(value);
    if (!value || !key || byKey.has(key)) return;
    byKey.set(key, value);
  });
  return Array.from(byKey.values());
};

const normalizeSystemAlertFilters = (raw: unknown): SystemAlertFilterRule[] => {
  const source = Array.isArray(raw) ? raw : [];
  const dedupBySender = new Map<string, SystemAlertFilterRule>();

  source.forEach((entry: any, idx) => {
    const row = entry && typeof entry === 'object' ? entry : {};
    const typeRaw = String(row.type || '').trim().toLowerCase();
    if (typeRaw && typeRaw !== 'system_alert_filter') return;
    const enabled = row.enabled === undefined ? true : Boolean(row.enabled);
    if (!enabled) return;

    const fromCandidates = extractEmailsFromValue(row?.condition?.from || row?.from || '');
    const from = String(fromCandidates[0] || '').toLowerCase().trim();
    if (!from) return;

    const moveToRaw = String(row?.action?.move_to_folder || '').trim().toLowerCase();
    if (moveToRaw && moveToRaw !== SYSTEM_ALERTS_FOLDER_API_VALUE) return;

    const id = String(row.id || `system-alert-filter-${idx}`).trim() || `system-alert-filter-${idx}`;
    const createdAt = String(row.createdAt || '').trim();
    const senderKey = from.toLowerCase();
    if (dedupBySender.has(senderKey)) return;

    dedupBySender.set(senderKey, {
      id,
      type: 'system_alert_filter',
      enabled: true,
      condition: { from },
      action: { move_to_folder: SYSTEM_ALERTS_FOLDER_API_VALUE },
      ...(createdAt ? { createdAt } : {}),
    });
  });

  return Array.from(dedupBySender.values()).slice(0, 200);
};

// Helper function to make links clickable in email content and ensure they open in new tab
const makeLinksClickable = (text: string): string => {
  if (!text) return '';
  
  let processedText = text;
  
  // If text already contains HTML links, ensure they have target="_blank"
  if (text.includes('<a ') || text.includes('<A ')) {
    // Replace all <a> tags to ensure they have target="_blank"
    processedText = processedText.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
      // Check if target="_blank" already exists
      if (attributes.includes('target=')) {
        // Replace existing target attribute
        return match.replace(/target=["'][^"']*["']/gi, 'target="_blank"');
      } else {
        // Add target="_blank" to existing attributes
        return `<a ${attributes} target="_blank" rel="noopener noreferrer">`;
      }
    });
    
    // Also ensure rel="noopener noreferrer" is present for security
    processedText = processedText.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
      if (!attributes.includes('rel=')) {
        return `<a ${attributes} rel="noopener noreferrer">`;
      }
      return match;
    });
    
    return processedText;
  }
  
  // URL regex pattern - matches http:// or https:// URLs
  // Matches URLs that start with http:// or https:// and continue until whitespace or common punctuation
  const urlPattern = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+[^\s<>"{}|\\^`\[\].,;:!?])/gi;
  
  // Replace URLs with clickable links
  return processedText.replace(urlPattern, (url) => {
    // Remove trailing punctuation that might not be part of URL
    const cleanUrl = url.replace(/[.,;:!?]+$/, '');
    return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" style="color: #0d6efd; text-decoration: underline; word-break: break-all;">${cleanUrl}</a>`;
  });
};

const buildIsolatedEmailHtml = (rawHtml: string): string => {
  const clickableHtml = makeLinksClickable(rawHtml || '');
  const iframeBaseStyle = `
    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      max-width: 100%;
      overflow-x: hidden !important;
      overflow-y: hidden !important;
      word-break: break-word;
    }
    * {
      box-sizing: border-box;
      max-width: 100%;
    }
    img {
      max-width: 100%;
      height: auto;
    }
    table {
      max-width: 100%;
    }
  `;

  if (/<\s*html[\s>]/i.test(clickableHtml)) {
    if (/<\s*head[\s>]/i.test(clickableHtml)) {
      return clickableHtml.replace(
        /<\s*head([^>]*)>/i,
        `<head$1><base target="_blank" /><style>${iframeBaseStyle}</style>`
      );
    }
    return clickableHtml.replace(
      /<\s*html([^>]*)>/i,
      `<html$1><head><base target="_blank" /><style>${iframeBaseStyle}</style></head>`
    );
  }

  return `<!doctype html><html><head><meta charset="utf-8" /><base target="_blank" /><style>${iframeBaseStyle}</style></head><body>${clickableHtml}</body></html>`;
};

// Preview/snippet: prefer text/plain; strip tracking pixels and known tracking URLs at render only (no storage change).
// Keeps stored content intact; cleans UX so raw <img> URLs (e.g. track.senderbulk.com) don't show in preview.
function getPreviewSnippet(email: { text?: string; html?: string }, maxLen: number): string {
  const TRACKING_HOST_PATTERN = /https?:\/\/[^\s<>"']*track\.senderbulk\.com[^\s<>"']*/gi;
  const IMG_1X1_PATTERN = /<img[^>]*(?:\s(?:width|height)=["']?1["']?|\s(?:width|height)=["']?1\s*px["']?)[^>]*\/?>/gi;
  const stripTracking = (s: string) =>
    s.replace(TRACKING_HOST_PATTERN, '').replace(IMG_1X1_PATTERN, '').replace(/\s+/g, ' ').trim();
  const stripAllTags = (s: string) => s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  let raw = '';
  if (email.text) {
    raw = stripTracking(email.text);
  } else if (email.html) {
    const noTracking = email.html.replace(TRACKING_HOST_PATTERN, '').replace(IMG_1X1_PATTERN, '');
    raw = stripAllTags(noTracking);
  }
  return raw.slice(0, maxLen);
}

const EMPTY_LEAD_DRAFT: LeadDraftFormData = {
  firstName: '',
  lastName: '',
  email: '',
  officePhone: '',
  cellPhone: '',
  title: '',
  organization: '',
  address: '',
  website: '',
  linkedInProfile: '',
  visibility: 'global',
  sourceFromLine: ''
};

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const EMAIL_PATTERN_GLOBAL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_PATTERN_GLOBAL = /(?:\+?\d[\d().\-\s]{7,}\d)/g;
const US_PHONE_PATTERN_GLOBAL = /(?:\+?1[\s().-]*)?\d{3}[\s().-]*\d{3}[\s().-]*\d{4}/g;
const URL_PATTERN_GLOBAL = /\b((?:https?:\/\/|www\.)[^\s<>"'()]+)\b/gi;
const LINKEDIN_URL_PATTERN = /(?:https?:\/\/)?(?:[\w-]+\.)?linkedin\.com\/[^\s<>"']+/i;
const ADDRESS_STREET_SEGMENT_PATTERN =
  /\b\d{1,6}\s+[A-Z0-9][A-Z0-9 .,'#-]*(?:\b(?:st|street|rd|road|dr|drive|ave|avenue|blvd|boulevard|ln|lane|way|ct|court|cir|circle|trl|trail|pkwy|parkway|pl|place|hwy|highway)\b)\.?/i;
const CITY_STATE_ZIP_PATTERN = /\b[A-Za-z .'-]+,?\s*[A-Z]{2}\s*\d{5}(?:-\d{4})?\b/;
const STATE_ZIP_PATTERN = /\b[A-Z]{2}\s*\d{5}(?:-\d{4})?\b/;
const ORGANIZATION_KEYWORD_PATTERN =
  /(inc|llc|corp|corporation|company|co\.|group|franchising|restaurant|restaurants|studio|media|marketing|solutions)/i;
const WEBSITE_NOISE_URL_PATTERNS = [
  /aka\.ms\/learnaboutsenderidentification/i,
  /learnaboutsenderidentification/i,
  /safelinks\.protection\.outlook\.com/i,
  /protection\.outlook\.com/i,
  /track\.senderbulk\.com/i,
  /doubleclick\.net/i,
  /\/unsubscribe/i,
  /learnwhythisisimportant/i,
];
const DISCLAIMER_LINE_PATTERN =
  /(confidential|privileged|intended recipient|intended solely|disclosure|dissemination|prohibited|received this message in error|inadvertent receipt|delete this message|applicable law|any disclosure)/i;
const SIGNATURE_SEGMENT_SPLIT_PATTERN = /\s*(?:[|¦｜│]|•|·)\s*|\s+\bI\b\s+|\s*\*{2,}\s*/;

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const htmlToText = (html: string): string => {
  if (!html) return '';
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|tr|h1|h2|h3|h4|h5|h6)>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const getEmailTextForLeadExtraction = (email: Email): string => {
  const plainText = String(email.text || '').trim();
  const htmlText = htmlToText(String(email.html || '')).trim();
  const previewText = String(email.preview || '').trim();
  const candidates = [plainText, htmlText, previewText].filter(Boolean);
  if (candidates.length === 0) return '';

  const deduped: string[] = [];
  candidates.forEach((candidate) => {
    const normalized = candidate.replace(/\s+/g, ' ').trim().toLowerCase();
    if (!normalized) return;
    const alreadyCovered = deduped.some((existing) => {
      const existingNormalized = existing.replace(/\s+/g, ' ').trim().toLowerCase();
      return existingNormalized.includes(normalized) || normalized.includes(existingNormalized);
    });
    if (!alreadyCovered) deduped.push(candidate);
  });

  return deduped.join('\n');
};

const normalizeUrlCandidate = (value: string): string => {
  const trimmed = String(value || '').trim().replace(/[),.;:!?]+$/, '');
  if (!trimmed) return '';
  return /^www\./i.test(trimmed) ? `https://${trimmed}` : trimmed;
};

const extractUrlsFromText = (value: string): string[] => {
  const normalized = String(value || '');
  if (!normalized) return [];
  const unique = new Set<string>();
  Array.from(normalized.matchAll(URL_PATTERN_GLOBAL)).forEach((match) => {
    const next = normalizeUrlCandidate(String(match[1] || ''));
    if (next) unique.add(next);
  });
  return Array.from(unique);
};

const isNoiseWebsiteUrl = (value: string): boolean => {
  const normalized = String(value || '').toLowerCase().replace(/\s+/g, '');
  if (!normalized) return true;
  return WEBSITE_NOISE_URL_PATTERNS.some((pattern) => pattern.test(normalized));
};

const normalizePhoneCandidate = (value: string): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const strictMatch = raw.match(/(?:\+?1[\s().-]*)?\d{3}[\s().-]*\d{3}[\s().-]*\d{4}/);
  const chosen = strictMatch ? strictMatch[0] : raw;
  return chosen.replace(/\s+/g, ' ').trim();
};

const toPhoneCandidates = (value: string): string[] =>
  Array.from(String(value || '').matchAll(US_PHONE_PATTERN_GLOBAL))
    .map((match) => normalizePhoneCandidate(String(match[0] || '')))
    .filter((candidate) => {
      const digits = candidate.replace(/\D/g, '');
      return digits.length >= 10 && digits.length <= 11;
    });

const extractLabeledPhones = (
  sourceText: string
): { officePhone: string; cellPhone: string; allPhones: string[] } => {
  const officeMatches = Array.from(
    sourceText.matchAll(/(?:^|[\s|,;])(?:o|office|work|direct|d)\s*[:\-]\s*((?:\+?1[\s().-]*)?\d{3}[\s().-]*\d{3}[\s().-]*\d{4})/gi)
  ).map((match) => normalizePhoneCandidate(String(match[1] || '')));
  const cellMatches = Array.from(
    sourceText.matchAll(/(?:^|[\s|,;])(?:c|cell|mobile|m)\s*[:\-]\s*((?:\+?1[\s().-]*)?\d{3}[\s().-]*\d{3}[\s().-]*\d{4})/gi)
  ).map((match) => normalizePhoneCandidate(String(match[1] || '')));

  const allPhones = Array.from(new Set(toPhoneCandidates(sourceText)));
  const officePhone = officeMatches.find(Boolean) || '';
  const cellPhone = cellMatches.find(Boolean) || '';

  return {
    officePhone,
    cellPhone,
    allPhones,
  };
};

const looksLikePhoneLine = (line: string): boolean => {
  const normalized = String(line || '').trim();
  if (!normalized || !/\d/.test(normalized)) return false;
  if (/(phone|cell|mobile|fax|office|direct|o:|p:|m:|c:|t:)/i.test(normalized)) return true;
  return Array.from(normalized.matchAll(PHONE_PATTERN_GLOBAL)).some((match) => {
    const digits = String(match[0] || '').replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15;
  });
};

const isLikelyAddressStreetSegment = (value: string): boolean =>
  ADDRESS_STREET_SEGMENT_PATTERN.test(String(value || '').trim());

const isLikelyCityStateZipSegment = (value: string): boolean =>
  CITY_STATE_ZIP_PATTERN.test(String(value || '').trim());

const cleanAddressSegment = (value: string): string =>
  String(value || '')
    .replace(/\*+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[,|¦｜│*•·\s]+/, '')
    .replace(/[,|¦｜│*•·\s]+$/, '')
    .trim();

const cleanOrganizationValue = (value: string): string =>
  String(value || '')
    .replace(/\*+/g, ' ')
    .replace(/^[|¦｜│*•·\s]+/, '')
    .replace(/\s*[|¦｜│]\s*/g, ' ')
    .replace(/\s*[•·]\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeSignatureLine = (value: string): string =>
  cleanOrganizationValue(
    String(value || '')
      .replace(/^[>]+/, '')
      .replace(/^\s*(from|to|sent|date|subject)\s*:\s*/i, '')
  );

const isLikelyForwardedHeaderLine = (value: string): boolean =>
  /^(from|to|sent|date|subject)\s*:/i.test(String(value || '').trim()) ||
  /^on .+ wrote:$/i.test(String(value || '').trim());

const isLikelyDisclaimerLine = (value: string): boolean =>
  DISCLAIMER_LINE_PATTERN.test(String(value || '').trim());

const splitSignatureSegments = (value: string): string[] =>
  String(value || '')
    .split(SIGNATURE_SEGMENT_SPLIT_PATTERN)
    .map(cleanAddressSegment)
    .filter(Boolean);

const extractCityStateZipSegment = (value: string): string => {
  const raw = cleanAddressSegment(value);
  const cityStateZipMatch = raw.match(CITY_STATE_ZIP_PATTERN);
  if (cityStateZipMatch) return cleanAddressSegment(cityStateZipMatch[0]);
  const stateZipMatch = raw.match(STATE_ZIP_PATTERN);
  return stateZipMatch ? cleanAddressSegment(stateZipMatch[0]) : '';
};

const extractStreetAddressSegment = (value: string): string => {
  const raw = cleanAddressSegment(value);
  if (!raw) return '';

  let streetCandidate = raw;
  const cityStateZipMatch = raw.match(CITY_STATE_ZIP_PATTERN);
  if (cityStateZipMatch && cityStateZipMatch.index !== undefined) {
    streetCandidate = raw.slice(0, cityStateZipMatch.index);
  } else {
    const stateZipMatch = raw.match(STATE_ZIP_PATTERN);
    if (stateZipMatch && stateZipMatch.index !== undefined) {
      streetCandidate = raw.slice(0, stateZipMatch.index);
    }
  }

  streetCandidate = cleanAddressSegment(
    streetCandidate
      .replace(/^(address|addr|a)\s*:\s*/i, '')
      .replace(/[,|¦｜│*•·\s]+$/, '')
  );

  if (!isLikelyAddressStreetSegment(streetCandidate)) {
    const fallbackStreetMatch = raw.match(ADDRESS_STREET_SEGMENT_PATTERN);
    if (!fallbackStreetMatch) return '';
    streetCandidate = cleanAddressSegment(String(fallbackStreetMatch[0] || ''));
  }

  return streetCandidate;
};

const isLikelyCompanyPrefixSegment = (value: string): boolean => {
  const line = cleanOrganizationValue(value);
  if (!line) return false;
  if (line.includes('@')) return false;
  if (isLikelyForwardedHeaderLine(line) || isLikelyDisclaimerLine(line)) return false;
  if (looksLikePhoneLine(line)) return false;
  if (isLikelyAddressStreetSegment(line) || isLikelyCityStateZipSegment(line) || STATE_ZIP_PATTERN.test(line)) return false;
  if (/^https?:\/\//i.test(line) || /^www\./i.test(line)) return false;
  const wordCount = line.split(/\s+/).filter(Boolean).length;
  return wordCount > 0 && wordCount <= 4;
};

const pickOrganizationFromSegments = (segments: string[]): string => {
  const cleaned = segments.map(cleanOrganizationValue).filter(Boolean);
  if (cleaned.length === 0) return '';

  const matchIndex = cleaned.findIndex((segment) => ORGANIZATION_KEYWORD_PATTERN.test(segment));
  if (matchIndex < 0) return '';

  const parts: string[] = [cleaned[matchIndex]];
  const prev = cleaned[matchIndex - 1];
  if (prev && isLikelyCompanyPrefixSegment(prev)) {
    parts.unshift(prev);
  }

  return cleanOrganizationValue(parts.join(' '));
};

const getCompanyPrefixFromLine = (line: string): string => {
  const segments = splitSignatureSegments(line).map(cleanOrganizationValue).filter(Boolean);
  if (segments.length === 0) return '';
  for (let index = segments.length - 1; index >= 0; index -= 1) {
    const segment = segments[index];
    if (isLikelyCompanyPrefixSegment(segment)) return segment;
  }
  return '';
};

const pickNearestLineToAnchor = (
  lineCandidates: Array<{ line: string; index: number }>,
  anchorIndex: number,
  maxDistance = 24
): string => {
  if (lineCandidates.length === 0) return '';
  if (anchorIndex < 0) return lineCandidates[0]?.line || '';

  const nearby = lineCandidates.filter((candidate) => Math.abs(candidate.index - anchorIndex) <= maxDistance);
  const ranked = (nearby.length > 0 ? nearby : lineCandidates).sort((a, b) => {
    const aDistance = Math.abs(a.index - anchorIndex);
    const bDistance = Math.abs(b.index - anchorIndex);
    if (aDistance !== bDistance) return aDistance - bDistance;

    const aBeforeAnchor = a.index <= anchorIndex ? 0 : 1;
    const bBeforeAnchor = b.index <= anchorIndex ? 0 : 1;
    if (aBeforeAnchor !== bBeforeAnchor) return aBeforeAnchor - bBeforeAnchor;

    return b.index - a.index;
  });

  return ranked[0]?.line || '';
};

const extractAddressFromContextLines = (contextLines: string[]): string => {
  const sanitized = contextLines
    .map((line) => String(line || '').trim())
    .filter(Boolean)
    .filter((line) => !line.includes('@'))
    .filter((line) => !looksLikePhoneLine(line))
    .filter((line) => !isLikelyDisclaimerLine(line));

  let streetOnlyFallback = '';

  for (let index = 0; index < sanitized.length; index += 1) {
    const segments = splitSignatureSegments(sanitized[index]);
    const streetSegment = segments.map(extractStreetAddressSegment).find(Boolean) || '';
    const cityStateZipSegment = segments.map(extractCityStateZipSegment).find(Boolean) || '';

    if (streetSegment && cityStateZipSegment) {
      return `${streetSegment}, ${cityStateZipSegment}`.replace(/\s+/g, ' ').trim();
    }

    if (streetSegment) {
      if (!streetOnlyFallback) streetOnlyFallback = streetSegment;
      const nextCityStateZipSegment = extractCityStateZipSegment(sanitized[index + 1] || '');
      if (nextCityStateZipSegment) {
        return `${streetSegment}, ${nextCityStateZipSegment}`.replace(/\s+/g, ' ').trim();
      }
    }
  }

  if (streetOnlyFallback) return streetOnlyFallback;

  const cityStateZipOnly = sanitized.find((line) => isLikelyCityStateZipSegment(line));
  return cityStateZipOnly ? cleanAddressSegment(cityStateZipOnly) : '';
};

const extractFirstEmailAddress = (value: string): string => {
  const match = String(value || '').match(EMAIL_PATTERN);
  return match ? match[0].toLowerCase() : '';
};

const parseAddressNameAndEmail = (value: string): { name: string; email: string } => {
  const raw = String(value || '').replace(/^from:\s*/i, '').trim();
  const email = extractFirstEmailAddress(raw);
  let name = raw;
  if (email) {
    name = name
      .replace(new RegExp(`<\\s*${escapeRegExp(email)}\\s*>`, 'i'), '')
      .replace(new RegExp(escapeRegExp(email), 'i'), '');
  }
  name = name.replace(/[<>"]/g, '').replace(/\s+/g, ' ').trim();
  if (email && name.toLowerCase() === email.toLowerCase()) name = '';
  return { name, email };
};

const toTitleCaseWords = (value: string): string => {
  const cleaned = String(value || '').trim();
  if (!cleaned) return '';
  return cleaned
    .split(/\s+/)
    .map((word) => {
      if (!word) return '';
      const normalized = /^[A-Z]+$/.test(word) ? word.toLowerCase() : word;
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    })
    .join(' ')
    .trim();
};

const splitName = (value: string): { firstName: string; lastName: string } => {
  const cleaned = String(value || '')
    .replace(/[|,;]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return { firstName: '', lastName: '' };
  const parts = cleaned.split(' ').filter(Boolean);
  return {
    firstName: toTitleCaseWords(parts[0] || ''),
    lastName: toTitleCaseWords(parts.slice(1).join(' '))
  };
};

const normalizeNameToken = (value: string): string =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .trim();

const getNameMatchKey = (firstName: string, lastName: string): string => {
  const first = normalizeNameToken(firstName);
  const last = normalizeNameToken(lastName);
  return first && last ? `${first}|${last}` : '';
};

const buildDuplicateCandidates = (
  draft: LeadDraftFormData,
  leads: any[],
  contacts: any[],
  clients: any[]
): DuplicateCandidate[] => {
  const draftNameKey = getNameMatchKey(draft.firstName, draft.lastName);
  const draftEmail = String(draft.email || '').toLowerCase().trim();
  const candidates: DuplicateCandidate[] = [];

  const pushCandidate = (
    type: DuplicateEntityType,
    item: any,
    id: string,
    displayName: string,
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    organization: string
  ) => {
    const candidateEmail = String(email || '').toLowerCase().trim();
    const candidateNameKey = getNameMatchKey(firstName, lastName);
    const nameMatch = Boolean(draftNameKey) && candidateNameKey === draftNameKey;
    const emailMatch = Boolean(draftEmail) && Boolean(candidateEmail) && candidateEmail === draftEmail;

    if (!nameMatch && !emailMatch) return;

    const reason = nameMatch && emailMatch ? 'Name + email match' : nameMatch ? 'Name match' : 'Email match';
    candidates.push({
      key: `${type}:${id}`,
      id,
      type,
      displayName: displayName || `${firstName} ${lastName}`.trim() || '(No name)',
      firstName,
      lastName,
      email: candidateEmail,
      phone: String(phone || ''),
      organization: String(organization || ''),
      reason,
      raw: item
    });
  };

  for (const lead of Array.isArray(leads) ? leads : []) {
    const { firstName, lastName } = splitName(String(lead?.name || ''));
    pushCandidate(
      'Lead',
      lead,
      String(lead?.id || ''),
      String(lead?.name || ''),
      firstName,
      lastName,
      String(lead?.email || ''),
      String(lead?.phone || ''),
      String(lead?.organization || '')
    );
  }

  for (const contact of Array.isArray(contacts) ? contacts : []) {
    const firstName = String(contact?.firstName || '');
    const lastName = String(contact?.lastName || '');
    pushCandidate(
      'Contact',
      contact,
      String(contact?.id || ''),
      `${firstName} ${lastName}`.trim(),
      firstName,
      lastName,
      String(contact?.email || ''),
      String(contact?.phone || ''),
      String(contact?.organization || '')
    );
  }

  for (const client of Array.isArray(clients) ? clients : []) {
    const { firstName, lastName } = splitName(String(client?.name || ''));
    pushCandidate(
      'Client',
      client,
      String(client?.id || ''),
      String(client?.name || ''),
      firstName,
      lastName,
      String(client?.email || ''),
      String(client?.phone || ''),
      String(client?.organization || '')
    );
  }

  const deduped: DuplicateCandidate[] = [];
  const seen = new Set<string>();
  for (const candidate of candidates) {
    if (!candidate.id || !candidate.key || seen.has(candidate.key)) continue;
    seen.add(candidate.key);
    deduped.push(candidate);
  }
  return deduped;
};

const deriveOrganizationFromEmail = (email: string): string => {
  const domain = String(email || '').toLowerCase().split('@')[1] || '';
  if (!domain) return '';
  const parts = domain.split('.').filter(Boolean);
  if (parts.length === 0) return '';
  const root = parts.length >= 2 ? parts[parts.length - 2] : parts[0];
  return toTitleCaseWords(root.replace(/[-_]+/g, ' '));
};

const extractLeadDraftFromEmail = (email: Email, internalMailboxEmails: string[]): LeadDraftFormData => {
  const bodyText = getEmailTextForLeadExtraction(email);
  const lines = bodyText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const internalSet = new Set(internalMailboxEmails.map((x) => String(x || '').toLowerCase().trim()).filter(Boolean));

  const bodyFromLines = lines
    .filter((line) => /^from\s*:/i.test(line))
    .map((line) => {
      const parsed = parseAddressNameAndEmail(line);
      return { raw: line, ...parsed };
    })
    .filter((line) => !!line.email);

  const preferredFromLine =
    bodyFromLines.find((line) => !internalSet.has(line.email)) ||
    bodyFromLines[0] ||
    null;

  const allBodyEmails = Array.from(
    new Set(
      Array.from(bodyText.matchAll(EMAIL_PATTERN_GLOBAL)).map((match) =>
        String(match[0] || '').toLowerCase()
      )
    )
  );

  const headerSender = parseAddressNameAndEmail(String(email.from || email.sender || ''));
  const preferredEmail =
    preferredFromLine?.email ||
    allBodyEmails.find((candidate) => !internalSet.has(candidate)) ||
    ((!internalSet.has(headerSender.email) && headerSender.email) || '') ||
    headerSender.email ||
    allBodyEmails[0] ||
    '';

  let resolvedName =
    preferredFromLine?.name ||
    headerSender.name ||
    '';

  if (!resolvedName && preferredEmail) {
    resolvedName = preferredEmail.split('@')[0].replace(/[._-]+/g, ' ');
  }

  const anchorIndex = preferredEmail
    ? (() => {
        const target = preferredEmail.toLowerCase();
        const candidates = lines
          .map((line, index) => ({ line, index }))
          .filter(({ line }) => line.toLowerCase().includes(target))
          .filter(({ line }) => !isLikelyForwardedHeaderLine(line));

        if (candidates.length === 0) {
          for (let idx = lines.length - 1; idx >= 0; idx -= 1) {
            if (lines[idx].toLowerCase().includes(target)) return idx;
          }
          return -1;
        }

        const scoreCandidate = (index: number, line: string): number => {
          const lineNormalized = String(line || '').toLowerCase();
          const nearbyLines = lines.slice(Math.max(0, index - 3), Math.min(lines.length, index + 4));
          const nearbyText = nearbyLines.join('\n').toLowerCase();
          let score = 0;
          if (/(?:^|[\s|,;])e\s*:/i.test(line)) score += 4;
          if (lineNormalized.includes(`<${target}>`) || lineNormalized.includes(target)) score += 2;
          if (/(?:o\s*:|office|work|c\s*:|cell|mobile)/i.test(nearbyText)) score += 3;
          if (isLikelyAddressStreetSegment(nearbyText) || isLikelyCityStateZipSegment(nearbyText) || STATE_ZIP_PATTERN.test(nearbyText)) score += 2;
          if (isLikelyForwardedHeaderLine(line)) score -= 5;
          if (isLikelyDisclaimerLine(line)) score -= 4;
          return score;
        };

        const ranked = [...candidates]
          .map((candidate) => ({ ...candidate, score: scoreCandidate(candidate.index, candidate.line) }))
          .sort((a, b) => {
            if (a.score !== b.score) return b.score - a.score;
            return b.index - a.index;
          });

        return ranked[0]?.index ?? -1;
      })()
    : -1;
  const contextStartIndex = anchorIndex >= 0 ? Math.max(0, anchorIndex - 12) : 0;
  const contextEndIndex = anchorIndex >= 0 ? Math.min(lines.length, anchorIndex + 16) : Math.min(lines.length, 40);
  const contextLines = lines.slice(contextStartIndex, contextEndIndex);
  const extractionLines = Array.from(new Set(contextLines));
  const extractionText = extractionLines.join('\n');

  const signatureEntries = extractionLines
    .map((line, offset) => ({
      line: normalizeSignatureLine(line),
      index: contextStartIndex + offset,
    }))
    .filter(({ line }) => Boolean(line))
    .filter(({ line }) => !isLikelyDisclaimerLine(line));

  const titleCandidates = signatureEntries
    .filter(({ line }) => !line.includes('@'))
    .filter(({ line }) => !isLikelyForwardedHeaderLine(line))
    .filter(({ line }) => !looksLikePhoneLine(line))
    .filter(({ line }) =>
      /(director|manager|owner|president|vp|chief|ceo|coo|cto|operations|marketing|sales|founder|assistant|coordinator)/i.test(line)
    );
  const titleLine = pickNearestLineToAnchor(titleCandidates, anchorIndex, 14);

  const addressLineEntry = signatureEntries.find(({ line }) => {
    const segments = splitSignatureSegments(line);
    const hasStreet = segments.some((segment) => Boolean(extractStreetAddressSegment(segment)));
    const hasCityStateZip = segments.some((segment) => Boolean(extractCityStateZipSegment(segment)));
    return hasStreet && hasCityStateZip;
  });

  let organizationFromAddressLine = '';
  if (addressLineEntry) {
    const segments = splitSignatureSegments(addressLineEntry.line);
    organizationFromAddressLine = pickOrganizationFromSegments(segments);
  }

  const signatureEntryByIndex = new Map(signatureEntries.map(({ line, index }) => [index, line]));
  const organizationCandidates = signatureEntries
    .map(({ line, index }) => {
      const previousLine = signatureEntryByIndex.get(index - 1) || '';
      const previousPrefix = getCompanyPrefixFromLine(previousLine);
      const shouldPrependPrefix =
        ORGANIZATION_KEYWORD_PATTERN.test(line) &&
        !ORGANIZATION_KEYWORD_PATTERN.test(previousPrefix) &&
        isLikelyCompanyPrefixSegment(previousPrefix);
      const combined = shouldPrependPrefix ? `${previousPrefix} ${line}` : line;
      return { line: cleanOrganizationValue(combined), index };
    })
    .filter(({ line }) => !line.includes('@'))
    .filter(({ line }) => !isLikelyForwardedHeaderLine(line))
    .filter(({ line }) => !looksLikePhoneLine(line))
    .filter(({ line }) => !isLikelyAddressStreetSegment(line))
    .filter(({ line }) => ORGANIZATION_KEYWORD_PATTERN.test(line));
  const organizationLine =
    organizationFromAddressLine || pickNearestLineToAnchor(organizationCandidates, anchorIndex, 14);

  let address = extractAddressFromContextLines(extractionLines);
  if (addressLineEntry) {
    const segments = splitSignatureSegments(addressLineEntry.line);
    const streetSegment = segments.map(extractStreetAddressSegment).find(Boolean) || '';
    const cityStateZipSegment = segments.map(extractCityStateZipSegment).find(Boolean) || '';
    if (streetSegment && cityStateZipSegment) {
      address = `${streetSegment}, ${cityStateZipSegment}`.replace(/\s+/g, ' ').trim();
    }
  }
  const preferredDomain = preferredEmail.split('@')[1] || '';

  const phoneSource = `${extractionText}\n${bodyText}`;
  const { officePhone: labeledOfficePhone, cellPhone: labeledCellPhone, allPhones } = extractLabeledPhones(phoneSource);
  const fallbackPhone = allPhones[0] || '';
  const officePhone = labeledOfficePhone || fallbackPhone;
  const cellPhone =
    labeledCellPhone ||
    allPhones.find((candidate) => candidate && candidate !== officePhone) ||
    '';

  const candidateUrls = extractUrlsFromText(extractionText).filter((url) => {
    const normalized = String(url || '').trim().toLowerCase();
    if (!normalized) return false;
    if (normalized.startsWith('mailto:')) return false;
    if (isNoiseWebsiteUrl(normalized)) return false;
    return true;
  });

  const linkedInProfile =
    candidateUrls.find((url) => LINKEDIN_URL_PATTERN.test(url)) ||
    '';

  const website =
    candidateUrls.find(
      (url) =>
        !LINKEDIN_URL_PATTERN.test(url) &&
        Boolean(preferredDomain) &&
        String(url || '').toLowerCase().includes(preferredDomain.toLowerCase())
    ) ||
    '';

  const { firstName, lastName } = splitName(resolvedName);

  return {
    firstName,
    lastName,
    email: preferredEmail,
    officePhone,
    cellPhone,
    title: toTitleCaseWords(titleLine),
    organization: cleanOrganizationValue(organizationLine) || deriveOrganizationFromEmail(preferredEmail),
    address,
    website,
    linkedInProfile,
    visibility: 'global',
    sourceFromLine: preferredFromLine?.raw || ''
  };
};

const buildSyntheticEmailCursor = (email: any): string | null => {
  if (!email || typeof email !== 'object') return null;
  const cursorDate = String(email?.date || email?.createdAt || '').trim();
  const cursorId = String(email?.id || email?.messageId || email?.SK || email?.sk || '').trim();
  if (!cursorDate || !cursorId) return null;
  try {
    return encodeURIComponent(JSON.stringify({ date: cursorDate, id: cursorId }));
  } catch {
    return null;
  }
};

const Unibox: React.FC = () => {
  const accountContext = useAccount();
  const { selectedAccount, isAllAccountsSelected, accounts } = accountContext;
  
  const [selectedInbox, setSelectedInbox] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedDomainFilter, setSelectedDomainFilter] = useState<string>('all'); // 'all' or domain
  const [selectedPurposeFilter, setSelectedPurposeFilter] = useState<'all' | 'business' | 'newsletter' | 'cold_outreach'>('all');
  const [showInboxFilters, setShowInboxFilters] = useState<boolean>(false); // Toggle Domain/Purpose/Campaign controls (hidden by default)
  const [emailSearchTerm, setEmailSearchTerm] = useState<string>('');
  // Debounced search query.
  // Heavy Sent corpora (warm-up traffic at 200k+) make every keystroke a long
  // server-side scan; without debouncing the in-flight request gets
  // cancelled/replaced on every key press and the user sees "nothing happens".
  // We accept a short typing delay in exchange for a request that actually
  // completes. Inbox is small enough that the delay is invisible.
  // See agent/sent-search-parity (May 2026).
  const [debouncedEmailSearchTerm, setDebouncedEmailSearchTerm] = useState<string>('');
  useEffect(() => {
    const trimmed = String(emailSearchTerm || '').trim();
    if (trimmed.length === 0) {
      setDebouncedEmailSearchTerm('');
      return;
    }
    const handle = window.setTimeout(() => {
      setDebouncedEmailSearchTerm(trimmed);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [emailSearchTerm]);
  const normalizedEmailSearchTerm = useMemo(
    () => String(debouncedEmailSearchTerm || '').trim(),
    [debouncedEmailSearchTerm]
  );
  const hasActiveEmailSearch = normalizedEmailSearchTerm.length > 0;
  // Whether the user has typed something but the debounce hasn't fired yet.
  // Used to show a subtle "Searching…" hint so it doesn't look like the input
  // is broken on heavy folders like Sent.
  const hasPendingEmailSearchInput = useMemo(() => {
    const typed = String(emailSearchTerm || '').trim();
    return typed.length > 0 && typed !== normalizedEmailSearchTerm;
  }, [emailSearchTerm, normalizedEmailSearchTerm]);
  const emailLimit = 50;
  const emailSearchQueryLimit = 1000;
  const emailQueryLimit = hasActiveEmailSearch ? emailSearchQueryLimit : emailLimit;
  const [activeTab, setActiveTab] = useState<string>('primary'); // 'inbox', 'spam', 'sent'
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [selectedEmailToView, setSelectedEmailToView] = useState<Email | null>(null); // Email to view in detail
  const [filterPanelOpen, setFilterPanelOpen] = useState(false); // Mobile/tablet: filter drawer open
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 992);
  const isMobileOrTablet = windowWidth < 992;
  const isMobile = windowWidth < 768;
  const [showDetailActionsMenu, setShowDetailActionsMenu] = useState<boolean>(false); // 3-dots overflow in email detail actions
  const [blockSenderMsg, setBlockSenderMsg] = useState<string>(''); // Inline feedback after block action
  const moreActionsButtonRef = useRef<HTMLButtonElement>(null);
  const [emailIframeHeight, setEmailIframeHeight] = useState<number>(700);
  const emailListScrollRef = useRef<HTMLDivElement | null>(null);
  const emailHtmlSrcDoc = useMemo(() => {
    if (!selectedEmailToView?.html) return '';
    return buildIsolatedEmailHtml(selectedEmailToView.html);
  }, [selectedEmailToView?.html]);

  const handleEmailIframeLoad = (event: React.SyntheticEvent<HTMLIFrameElement>) => {
    const iframe = event.currentTarget;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;

      // Keep the iframe from introducing its own scrollbar; let the pane scroll instead.
      doc.documentElement.style.overflow = 'hidden';
      if (doc.body) doc.body.style.overflow = 'hidden';

      const recalcHeight = () => {
        const bodyHeight = doc.body?.scrollHeight || 0;
        const htmlHeight = doc.documentElement?.scrollHeight || 0;
        const docHeight = doc.body?.offsetHeight || 0;
        const nextHeight = Math.max(bodyHeight, htmlHeight, docHeight, 500);
        setEmailIframeHeight(Math.min(nextHeight + 16, 6000));
      };

      recalcHeight();
      if (typeof window !== 'undefined') {
        window.requestAnimationFrame(recalcHeight);
        window.setTimeout(recalcHeight, 120);
      }
    } catch (err) {
      console.warn('Unable to resize email iframe; using default height.', err);
    }
  };

  useEffect(() => {
    setEmailIframeHeight(700);
    setShowDetailActionsMenu(false);
  }, [selectedEmailToView?.id]);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [optimisticReadStatus, setOptimisticReadStatus] = useState<Map<string, boolean>>(new Map()); // Track optimistic read status updates
  const [optimisticRemovedIds, setOptimisticRemovedIds] = useState<Set<string>>(new Set());
  const [filterMode, setFilterMode] = useState<'status' | 'inboxes' | 'labels' | 'folders'>('inboxes'); // Status, All Inboxes, Labels, Folders
  const [failedAvatars, setFailedAvatars] = useState<Set<string>>(new Set()); // Track failed avatar loads
  const tenantIdForRulesEngine = useTenantIdForPreferences();
  // Auto-refresh toggle - load from backend user preferences
  // Use active tenant scope so folders/rules don't bleed across tenants.
  const tenantIdForPrefs = tenantIdForRulesEngine;

  const { data: rulesEngineConfigData, refetch: refetchRulesEngineConfig } = useGetRulesEngineConfigQuery(
    tenantIdForRulesEngine ? { tenantId: tenantIdForRulesEngine } : undefined,
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const {
    data: userPreferencesData,
    isLoading: isLoadingPreferences,
    isFetching: isFetchingPreferences,
  } = useGetUserPreferencesQuery(
    tenantIdForPrefs ? { tenantId: tenantIdForPrefs } : undefined,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [updateRulesEngineConfig] = useUpdateRulesEngineConfigMutation();
  const [updateUserPreferences, { isLoading: isSavingUserPreferences }] = useUpdateUserPreferencesMutation();
  /** Prefs load/save/refetch (RTK `isLoading` = first fetch; `isFetching` = refetch after save). Disables refresh toolbar + aria-busy; icon spin follows email fetch only. */
  const autoRefreshPrefsBusy =
    isLoadingPreferences || isFetchingPreferences || isSavingUserPreferences;
  const [purgeTrash, { isLoading: isPurgingTrash }] = usePurgeTrashMutation();
  const [permanentDeleteEmails, { isLoading: isPermanentDeleting }] = usePermanentDeleteEmailsMutation();
  const blocklistEntries = useMemo<BlocklistEntry[]>(
    () =>
      Array.isArray((userPreferencesData as any)?.preferences?.blocklist)
        ? ((userPreferencesData as any).preferences.blocklist as BlocklistEntry[])
        : [],
    [userPreferencesData]
  );
  const blockedEmailSet = useMemo(
    () =>
      new Set(
        blocklistEntries
          .filter((entry) => String(entry?.type || '').toLowerCase() === 'email')
          .map((entry) => String(entry?.value || '').toLowerCase().trim())
          .filter(Boolean)
      ),
    [blocklistEntries]
  );
  const blockedDomainSet = useMemo(
    () =>
      new Set(
        blocklistEntries
          .filter((entry) => String(entry?.type || '').toLowerCase() === 'domain')
          .map((entry) => String(entry?.value || '').toLowerCase().trim())
          .filter(Boolean)
      ),
    [blocklistEntries]
  );
  const autoRefreshFromPreferences = userPreferencesData?.preferences?.uniboxAutoRefresh || false;
  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );
  const [autoRefresh, setAutoRefresh] = useState<boolean>(autoRefreshFromPreferences);

  // User-created folders (stored in user preferences)
  const [folders, setFolders] = useState<string[]>([]);
  const [folderAliases, setFolderAliases] = useState<FolderAliasMap>({});
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [emailPageCursor, setEmailPageCursor] = useState<string | null>(null);
  const [emailPageCursorHistory, setEmailPageCursorHistory] = useState<Array<string | null>>([]);
  const [emailCurrentPage, setEmailCurrentPage] = useState<number>(1);
  const [emailNextPageCursor, setEmailNextPageCursor] = useState<string | null>(null);
  const [emailFallbackNextPageCursor, setEmailFallbackNextPageCursor] = useState<string | null>(null);
  const [emailFolderTotal, setEmailFolderTotal] = useState<number>(0);
  // searchPartial: backend ran out of runtime/page budget before fully scanning
  // the corpus. Heavy folders (Sent at 200k+) commonly trigger this. We surface
  // a small "Showing N best matches; older may be missed" caption so the result
  // count isn't misread as authoritative.
  const [emailSearchPartial, setEmailSearchPartial] = useState<boolean>(false);
  const [folderSearchTerm, setFolderSearchTerm] = useState<string>('');
  // Back-compat: the existing "Labels" UI is being repurposed as folders.
  const labels = folders;
  const setLabels = setFolders;

  // Auto-filters: rules that file inbound emails into folders automatically
  const [autoFilters, setAutoFilters] = useState<AutoFilterRule[]>([]);
  const [systemAlertFilters, setSystemAlertFilters] = useState<SystemAlertFilterRule[]>([]);
  const [showAutoFilterModal, setShowAutoFilterModal] = useState<boolean>(false);
  const [editingAutoFilterId, setEditingAutoFilterId] = useState<string | null>(null);
  const [autoFilterDraft, setAutoFilterDraft] = useState<Partial<AutoFilterRule>>({
    enabled: true,
    field: 'subject_or_body',
    matchType: 'contains',
    pattern: '',
    folder: '',
  });
  const [autoFilterProcessedIds] = useState<Set<string>>(() => new Set());

  // Warmup auto-filing (per-user preference)
  const warmupRule = (userPreferencesData as any)?.preferences?.warmupRule || { enabled: false, folderName: 'Warmup', pattern: '[A-Z0-9]{6,10}' };
  const warmupEnabled = Boolean(warmupRule?.enabled);
  const warmupFolderName = String(warmupRule?.folderName || 'Warmup').trim() || 'Warmup';
  const [warmupProcessedIds] = useState<Set<string>>(() => new Set());

  const formatEmailDateTime = (value?: string, includeTimezoneName = false): string => {
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...(includeTimezoneName && tenantTimezone ? { timeZoneName: 'short' } : {}),
    };
    return toLocaleStringInTimezone(value, 'en-US', options, tenantTimezone);
  };
  
  // Update local state when preferences load from backend
  useEffect(() => {
    if (!isLoadingPreferences && userPreferencesData?.preferences?.uniboxAutoRefresh !== undefined) {
      setAutoRefresh(userPreferencesData.preferences.uniboxAutoRefresh);
    }
  }, [userPreferencesData, isLoadingPreferences]);

  // Load folders from preferences (persisted per-user)
  useEffect(() => {
    const prefsFolders = (userPreferencesData as any)?.preferences?.uniboxFolders;
    const prefsFolderAliasesRaw = (userPreferencesData as any)?.preferences?.uniboxFolderAliases;
    const prefsFolderAliases = normalizeFolderAliasMap(prefsFolderAliasesRaw);
    if (Array.isArray(prefsFolders)) {
      const cleaned = mergeUniqueFolders([
        ...DEFAULT_UNIBOX_FOLDERS,
        ...prefsFolders.map((x: any) => String(x || '').trim()).filter(Boolean),
      ]);
      setFolders(cleaned);
      setFolderAliases(pruneFolderAliases(prefsFolderAliases, cleaned));
      const selectedStillExists = cleaned.some((folder) => normalizeFolderLabel(folder) === normalizeFolderLabel(selectedFolder));
      if (selectedFolder && !isSystemFolderSelection(selectedFolder) && !selectedStillExists) {
        setSelectedFolder(null);
      }
    } else {
      const cleaned = [...DEFAULT_UNIBOX_FOLDERS];
      setFolders(cleaned);
      setFolderAliases(pruneFolderAliases(prefsFolderAliases, cleaned));
      if (selectedFolder && !isSystemFolderSelection(selectedFolder)) {
        setSelectedFolder(null);
      }
    }
  }, [userPreferencesData, selectedFolder]);

  // Reset pagination cursor when mailbox context changes (folder/tab/account)
  useEffect(() => {
    setEmailPageCursor(null);
    setEmailPageCursorHistory([]);
    setEmailCurrentPage(1);
    setEmailNextPageCursor(null);
  }, [selectedFolder, activeTab, selectedInbox]);

  // Load auto-filters from preferences (persisted per-user).
  // If no explicit auto-filters exist, surface legacy warmupRule as a fallback rule.
  useEffect(() => {
    const prefsRulesRaw = (userPreferencesData as any)?.preferences?.uniboxAutoFilters;
    const incoming: AutoFilterRule[] = Array.isArray(prefsRulesRaw)
      ? prefsRulesRaw
          .map((r: any) => ({
            id: String(r?.id || '').trim() || `rule_${Math.random().toString(36).slice(2)}`,
            enabled: Boolean(r?.enabled !== false),
            field: (['subject', 'body', 'subject_or_body'].includes(String(r?.field)) ? (String(r.field) as AutoFilterField) : 'subject_or_body'),
            matchType: (['contains', 'regex'].includes(String(r?.matchType)) ? (String(r.matchType) as AutoFilterMatchType) : 'contains'),
            pattern: String(r?.pattern || '').trim(),
            folder: String(r?.folder || '').trim(),
          }))
          .filter((r: AutoFilterRule) => r.pattern && r.folder)
          .slice(0, 50)
      : [];

    // Legacy warmup fallback only when there are no explicit auto-filters.
    if (incoming.length === 0 && warmupEnabled && warmupFolderName) {
      const hasWarmup = incoming.some((r) => r.id === 'warmup' || (r.folder === warmupFolderName && r.matchType === 'regex'));
      if (!hasWarmup) {
        incoming.unshift({
          id: 'warmup',
          enabled: true,
          field: 'subject_or_body',
          matchType: 'regex',
          pattern: String(warmupRule?.pattern || '[A-Z0-9]{6,10}'),
          folder: warmupFolderName,
        });
      }
    }
    setAutoFilters(incoming);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreferencesData]);

  useEffect(() => {
    const raw = (userPreferencesData as any)?.preferences?.uniboxSystemAlertFilters;
    setSystemAlertFilters(normalizeSystemAlertFilters(raw));
  }, [userPreferencesData]);

  useEffect(() => {
    return () => {
      if (systemAlertRuleToastTimerRef.current) {
        clearTimeout(systemAlertRuleToastTimerRef.current);
      }
    };
  }, []);

  const [isFilterSectionCollapsed, setIsFilterSectionCollapsed] = useState<boolean>(false); // Collapse/expand filter section
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null); // Selected status option
  const [statusSearchTerm, setStatusSearchTerm] = useState<string>(''); // Search term for status
  const [customStatusLabels, setCustomStatusLabels] = useState<string[]>([]);
  const [hiddenStatusLabels, setHiddenStatusLabels] = useState<string[]>([]);
  const [isUpdatingHiddenStatuses, setIsUpdatingHiddenStatuses] = useState<boolean>(false);
  const [showCreateStatusLabelModal, setShowCreateStatusLabelModal] = useState<boolean>(false);
  const [newStatusLabelName, setNewStatusLabelName] = useState<string>('');
  const [isSavingStatusLabel, setIsSavingStatusLabel] = useState<boolean>(false);
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null); // Selected campaign
  const [emailStatusDropdown, setEmailStatusDropdown] = useState<{ [emailId: string]: boolean }>({}); // Track which email's status dropdown is open
  const [showAutoRefreshTooltip, setShowAutoRefreshTooltip] = useState<boolean>(() => {
    // Check localStorage to see if user dismissed the tooltip
    const dismissed = localStorage.getItem('unibox-auto-refresh-tooltip-dismissed');
    return dismissed !== 'true';
  });
  const [showHelpSidebar, setShowHelpSidebar] = useState<boolean>(false); // Help sidebar toggle
  const [showUnreadOnly, setShowUnreadOnly] = useState<boolean>(false); // Toggle: show all ↔ unread only
  const [showReadOnly, setShowReadOnly] = useState<boolean>(false);    // Toggle: show all ↔ read only
  const [showMoveToDropdown, setShowMoveToDropdown] = useState<boolean>(false);
  const [showCreateLabelModal, setShowCreateLabelModal] = useState<boolean>(false);
  const [newLabelName, setNewLabelName] = useState<string>('');
  const [renamingFolderName, setRenamingFolderName] = useState<string | null>(null);
  const [renamingFolderDraft, setRenamingFolderDraft] = useState<string>('');
  const [isRenamingFolderSaving, setIsRenamingFolderSaving] = useState<boolean>(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  
  // Trash-tab action modals
  const [showTrashActionsModal, setShowTrashActionsModal] = useState<boolean>(false);
  const [showEmptyTrashConfirm, setShowEmptyTrashConfirm] = useState<boolean>(false);
  const [showTrashSettingsModal, setShowTrashSettingsModal] = useState<boolean>(false);

  // Undo-delete: delayed hard-delete ref (permanent delete / empty trash)
  const pendingDeleteRef = useRef<{
    timerId: ReturnType<typeof setTimeout>;
    toastId: ToastId;
    idsToRestore: string[];
    type: 'permanent' | 'purge';
    capturedKeys?: { PK: string; SK: string }[];
  } | null>(null);

  // Compose modal state
  const [showComposeModal, setShowComposeModal] = useState<boolean>(false);
  const [showOriginalModal, setShowOriginalModal] = useState<boolean>(false);
  const [showCreateLeadModal, setShowCreateLeadModal] = useState<boolean>(false);
  const [showCreateEntityMenu, setShowCreateEntityMenu] = useState<boolean>(false);
  const [createEntityType, setCreateEntityType] = useState<CreateEntityType>('Lead');
  const [showDuplicateLeadModal, setShowDuplicateLeadModal] = useState<boolean>(false);
  const [originalEmailData, setOriginalEmailData] = useState<any | null>(null);
  const [originalEmailError, setOriginalEmailError] = useState<string>('');
  const [downloadingAttachmentKey, setDownloadingAttachmentKey] = useState<string>('');
  const [systemAlertRuleToast, setSystemAlertRuleToast] = useState<{ senderEmail: string; ruleId: string } | null>(null);
  const systemAlertRuleToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const composeBodyRef = useRef<HTMLTextAreaElement | null>(null);
  const composeAttachmentInputRef = useRef<HTMLInputElement | null>(null);
  const [leadDraftForm, setLeadDraftForm] = useState<LeadDraftFormData>(EMPTY_LEAD_DRAFT);
  const [leadSourceEmail, setLeadSourceEmail] = useState<Email | null>(null);
  const [duplicateCandidates, setDuplicateCandidates] = useState<DuplicateCandidate[]>([]);
  const [selectedDuplicateCandidateKey, setSelectedDuplicateCandidateKey] = useState<string>('');
  const [composeAttachments, setComposeAttachments] = useState<ComposeAttachment[]>([]);
  const [composeConfidentialMode, setComposeConfidentialMode] = useState<boolean>(false);
  // Compose origin used by send() to set isManualReply on the outbound payload.
  // Only 'reply' / 'reply-all' bypass the Rules Engine on the backend.
  const [composeMode, setComposeMode] = useState<ComposeMode>('new');
  // When the backend returns 400 RULES_ENGINE_BLOCKED, we surface the reason in
  // a confirmation modal instead of a bare alert(). The modal's Send-anyway
  // button is gated by the backend's `overridable` flag on the response:
  //   * overridable === true  → SOFT-BLOCK tier (Active Opportunity /
  //     Current Client). Render Cancel + Send-anyway. Send-anyway retries with
  //     manualSendAnywayOverride: true.
  //   * overridable === false → HARD-STOP-NEW-OUTREACH tier (Dead Opportunity /
  //     Do Not Prospect). Render Cancel only. The user must close the modal
  //     and pick a different recipient or have the recipient reply first.
  // Reply / Reply All should never reach this state because they auto-bypass
  // server-side via isManualReply, but if they ever do the modal still displays
  // safely (Send-anyway is hidden when overridable === false).
  // See hotfix/rules-engine-harden.
  const [rulesEngineBlock, setRulesEngineBlock] = useState<{
    message: string;
    reasons: Array<{
      recipient: string;
      entityType: string;
      entityName: string;
      target: string;
      stage: string;
      triggerNames: string[];
      tier?: 'soft' | 'hard_stop_new_outreach';
    }>;
    composeModeAtBlock: ComposeMode;
    overridable: boolean;
    isOverriding: boolean;
  } | null>(null);
  const [composeData, setComposeData] = useState<ComposeData>({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    fromAccount: ''
  });
  const composeBlockedRecipients = useMemo(() => {
    const sourceRows: Array<{ field: 'to' | 'cc' | 'bcc'; address: string }> = [
      ...parseRecipientList(composeData.to).map((address) => ({ field: 'to' as const, address })),
      ...parseRecipientList(composeData.cc).map((address) => ({ field: 'cc' as const, address })),
      ...parseRecipientList(composeData.bcc).map((address) => ({ field: 'bcc' as const, address })),
    ];
    const seen = new Set<string>();
    const matches: Array<{ field: 'to' | 'cc' | 'bcc'; address: string; type: 'email' | 'domain'; value: string }> = [];
    for (const row of sourceRows) {
      const address = String(row.address || '').toLowerCase().trim();
      if (!address) continue;
      const domain = address.split('@')[1]?.toLowerCase().trim() || '';
      let type: 'email' | 'domain' | null = null;
      let value = '';
      if (blockedEmailSet.has(address)) {
        type = 'email';
        value = address;
      } else if (domain && blockedDomainSet.has(domain)) {
        type = 'domain';
        value = domain;
      }
      if (!type) continue;
      const dedupeKey = `${row.field}:${address}:${type}:${value}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      matches.push({ field: row.field, address, type, value });
    }
    return matches;
  }, [composeData.to, composeData.cc, composeData.bcc, blockedEmailSet, blockedDomainSet]);

  useEffect(() => {
    const rawHiddenStatuses = (userPreferencesData as any)?.preferences?.uniboxHiddenStatuses;
    if (!Array.isArray(rawHiddenStatuses)) {
      setHiddenStatusLabels([]);
      return;
    }
    const dedup = new Map<string, string>();
    rawHiddenStatuses.forEach((value: any) => {
      const key = normalizeStatusLabel(value);
      if (!key || dedup.has(key)) return;
      dedup.set(key, key);
    });
    setHiddenStatusLabels(Array.from(dedup.values()));
  }, [userPreferencesData]);

  useEffect(() => {
    const rawCustomStatuses = (userPreferencesData as any)?.preferences?.uniboxCustomStatuses;
    if (!Array.isArray(rawCustomStatuses)) {
      setCustomStatusLabels([]);
      return;
    }
    const dedup = new Map<string, string>();
    rawCustomStatuses.forEach((value: any) => {
      const name = String(value || '').trim();
      const key = normalizeStatusLabel(name);
      if (!name || !key || DEFAULT_STATUS_KEYS.has(key) || dedup.has(key)) return;
      dedup.set(key, name);
    });
    setCustomStatusLabels(Array.from(dedup.values()));
  }, [userPreferencesData]);

  const { 
    data: emailAccountsData,
    refetch: loadEmailAccounts 
  } = useGetEmailAccountsQuery(undefined, {
    // Enable refetching when account changes
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
    refetchOnReconnect: false
  });

  // Update email mutation
  const [updateEmail, { isLoading: isUpdatingEmail }] = useUpdateEmailMutation();
  const [createLead, { isLoading: isCreatingLeadFromEmail }] = useCreateLeadMutation();
  const [createContact, { isLoading: isCreatingContactFromEmail }] = useCreateContactMutation();
  const [createClient, { isLoading: isCreatingClientFromEmail }] = useCreateClientMutation();
  const [updateLead] = useUpdateLeadMutation();
  const [updateContact] = useUpdateContactMutation();
  const [updateClient] = useUpdateClientMutation();
  const [getEmailOriginal, { isFetching: isLoadingOriginalEmail }] = useLazyGetEmailOriginalQuery();
  const [getEmailAttachment, { isFetching: isLoadingEmailAttachment }] = useLazyGetEmailAttachmentQuery();
  const [fetchLeadsLazy] = useLazyGetLeadsQuery();
  const [fetchContactsLazy] = useLazyGetContactsQuery();
  const [fetchClientsLazy] = useLazyGetClientsQuery();

  const { data: leadsForDedup = [], isFetching: isFetchingLeadsForDedup } = useGetLeadsQuery(
    {},
    { skip: !showCreateLeadModal, refetchOnMountOrArgChange: true }
  );
  const { data: contactsForDedup = [], isFetching: isFetchingContactsForDedup } = useGetContactsQuery(
    {},
    { skip: !showCreateLeadModal, refetchOnMountOrArgChange: true }
  );
  const { data: clientsForDedup = [], isFetching: isFetchingClientsForDedup } = useGetClientsQuery(
    {},
    { skip: !showCreateLeadModal, refetchOnMountOrArgChange: true }
  );
  const isCheckingDuplicateCandidates =
    showCreateLeadModal &&
    (isFetchingLeadsForDedup || isFetchingContactsForDedup || isFetchingClientsForDedup);
  const selectedDuplicateCandidate = useMemo(
    () => duplicateCandidates.find((candidate) => candidate.key === selectedDuplicateCandidateKey) || null,
    [duplicateCandidates, selectedDuplicateCandidateKey]
  );
  const isCreatingEntityFromEmail =
    isCreatingLeadFromEmail ||
    isCreatingContactFromEmail ||
    isCreatingClientFromEmail;

  const isReservedFolderName = (value: string): boolean =>
    RESERVED_CUSTOM_FOLDER_NAMES.has(normalizeFolderLabel(value));
  const isLockedFolderName = (value: string): boolean =>
    normalizeFolderLabel(value) === 'bounce' ||
    normalizeFolderLabel(value) === normalizeFolderLabel(SYSTEM_ALERTS_FOLDER_LABEL) ||
    // Phase A: `Warm Up` system folder is locked alongside Bounce and
    // System Alerts. Locked folders render with the gray
    // `<Badge bg="secondary">System</Badge>` instead of Edit/Delete
    // buttons (see the `isLockedFolder ? <Badge ...>` branch in the
    // sidebar JSX below).
    normalizeFolderLabel(value) === normalizeFolderLabel(WARMUP_SYSTEM_FOLDER_LABEL);

  const folderNameExists = (candidate: string, ignore?: string): boolean => {
    const candidateKey = normalizeFolderLabel(candidate);
    if (!candidateKey) return false;
    const ignoreKey = normalizeFolderLabel(ignore || '');
    return (folders || []).some((folder) => {
      const folderKey = normalizeFolderLabel(folder);
      if (!folderKey) return false;
      if (ignoreKey && folderKey === ignoreKey) return false;
      return folderKey === candidateKey;
    });
  };

  const persistFolderPreferences = async (
    nextFolders: string[],
    nextAutoFilters?: AutoFilterRule[],
    nextWarmupRule?: any,
    nextFolderAliases?: FolderAliasMap
  ): Promise<void> => {
    await updateUserPreferences({
      ...preferencesTenantWriteSpread(tenantIdForPrefs),
      preferences: {
        uniboxAutoRefresh: autoRefresh,
        uniboxFolders: nextFolders,
        ...(nextAutoFilters ? { uniboxAutoFilters: nextAutoFilters } : {}),
        ...(nextWarmupRule ? { warmupRule: nextWarmupRule } : {}),
        ...(nextFolderAliases ? { uniboxFolderAliases: nextFolderAliases } : {}),
      },
    }).unwrap();
  };

  const persistSystemAlertFilters = async (nextRules: SystemAlertFilterRule[]): Promise<void> => {
    await updateUserPreferences({
      ...preferencesTenantWriteSpread(tenantIdForPrefs),
      preferences: {
        uniboxSystemAlertFilters: nextRules,
      },
    }).unwrap();
  };

  const maybeSyncRulesEngineTargetFolderRename = async (oldName: string, nextName: string): Promise<void> => {
    let config = (rulesEngineConfigData && typeof rulesEngineConfigData === 'object')
      ? (rulesEngineConfigData as Record<string, any>)
      : null;
    try {
      const refetched = await refetchRulesEngineConfig();
      const fetchedData = (refetched as any)?.data;
      if (fetchedData && typeof fetchedData === 'object') {
        config = fetchedData as Record<string, any>;
      }
    } catch (refetchError) {
      console.warn('Unable to refetch Rules Engine config before folder-rename sync:', refetchError);
    }
    if (!config) return;
    const warmupDeletion = (config.warmupDeletion && typeof config.warmupDeletion === 'object')
      ? (config.warmupDeletion as Record<string, any>)
      : null;
    const currentTargetFolder = String(warmupDeletion?.targetFolder || '').trim();
    if (!currentTargetFolder) return;
    if (normalizeFolderLabel(currentTargetFolder) !== normalizeFolderLabel(oldName)) return;
    const nextTargetKey = normalizeFolderLabel(nextName);
    const targetFolderAliases = Array.isArray((warmupDeletion as any)?.targetFolderAliases)
      ? (warmupDeletion as any).targetFolderAliases
      : [];
    const aliasDedup = new Map<string, string>();
    [...targetFolderAliases, oldName].forEach((aliasRaw) => {
      const alias = String(aliasRaw || '').trim();
      const aliasKey = normalizeFolderLabel(alias);
      if (!alias || !aliasKey || aliasKey === nextTargetKey || aliasDedup.has(aliasKey)) return;
      aliasDedup.set(aliasKey, alias);
    });

    await updateRulesEngineConfig({
      ...(tenantIdForRulesEngine ? { tenantId: tenantIdForRulesEngine } : {}),
      config: {
        ...config,
        warmupDeletion: {
          ...(warmupDeletion || {}),
          targetFolder: nextName,
          targetFolderAliases: Array.from(aliasDedup.values()),
        },
      },
    }).unwrap();
  };

  const startFolderRename = (folderName: string): void => {
    setRenamingFolderName(folderName);
    setRenamingFolderDraft(folderName);
  };

  const cancelFolderRename = (): void => {
    setRenamingFolderName(null);
    setRenamingFolderDraft('');
    setIsRenamingFolderSaving(false);
  };

  const saveFolderRename = async (): Promise<void> => {
    const oldName = String(renamingFolderName || '').trim();
    const nextName = String(renamingFolderDraft || '').trim();
    if (!oldName || !nextName) return;
    if (isLockedFolderName(oldName)) {
      alert('System folders cannot be renamed.');
      return;
    }
    if (isReservedFolderName(nextName)) {
      alert('That folder name is reserved. Please choose a different name.');
      return;
    }
    if (folderNameExists(nextName, oldName)) {
      alert('A folder with that name already exists.');
      return;
    }

    const oldKey = normalizeFolderLabel(oldName);
    const nextFolders = mergeUniqueFolders(
      (folders || []).map((folder) => (normalizeFolderLabel(folder) === oldKey ? nextName : folder))
    );
    const nextAutoFilters = (autoFilters || []).map((rule) => (
      normalizeFolderLabel(rule.folder) === oldKey
        ? { ...rule, folder: nextName }
        : rule
    ));
    const nextWarmupRule = normalizeFolderLabel(String(warmupRule?.folderName || '')) === oldKey
      ? { ...warmupRule, folderName: nextName }
      : undefined;
    const nextFolderAliases = pruneFolderAliases(
      buildFolderAliasMapForRename(folderAliases, oldName, nextName),
      nextFolders
    );

    const shouldUpdateSelectedFolder = normalizeFolderLabel(String(selectedFolder || '')) === oldKey;
    const shouldUpdateAutoFilterDraft = normalizeFolderLabel(String(autoFilterDraft.folder || '')) === oldKey;
    setIsRenamingFolderSaving(true);
    try {
      setFolders(nextFolders);
      setFolderAliases(nextFolderAliases);
      setAutoFilters(nextAutoFilters);
      if (shouldUpdateSelectedFolder) setSelectedFolder(nextName);
      if (shouldUpdateAutoFilterDraft) {
        setAutoFilterDraft((prev) => ({ ...prev, folder: nextName }));
      }
      await persistFolderPreferences(nextFolders, nextAutoFilters, nextWarmupRule, nextFolderAliases);
      let rulesEngineSyncWarning = '';
      try {
        await maybeSyncRulesEngineTargetFolderRename(oldName, nextName);
      } catch (rulesEngineSyncError: any) {
        console.warn('Folder renamed but failed to sync Rules Engine target folder:', rulesEngineSyncError);
        rulesEngineSyncWarning = 'Folder renamed, but Rules Engine target folder was not updated automatically. Please re-select it in Rules Engine.';
      }
      cancelFolderRename();
      if (rulesEngineSyncWarning) alert(rulesEngineSyncWarning);
    } catch (error: any) {
      console.warn('Failed to rename folder:', error);
      alert(`Failed to rename folder: ${error?.message || 'Unknown error'}`);
      setFolders(folders);
      setFolderAliases(folderAliases);
      setAutoFilters(autoFilters);
      if (shouldUpdateSelectedFolder) setSelectedFolder(oldName);
      if (shouldUpdateAutoFilterDraft) {
        setAutoFilterDraft((prev) => ({ ...prev, folder: oldName }));
      }
      setIsRenamingFolderSaving(false);
    }
  };

  const detailActionButtonStyle: React.CSSProperties = {
    fontSize: '0.5rem',
    padding: '0.03rem 0.1rem',
    lineHeight: '1.05',
    whiteSpace: 'nowrap',
    height: '19px',
    minWidth: '64px',
    maxWidth: '64px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  };
  const createEntityButtonStyle: React.CSSProperties = {
    ...detailActionButtonStyle,
    minWidth: '96px',
    maxWidth: '96px',
  };

  // Get domains data to look up accountId and region for email sending
  const { data: domainsData } = useGetDomainsQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false
  });

  const normalizeDomainType = (t: any): 'business' | 'newsletter' | 'cold_outreach' => {
    const v = String(t || '').toLowerCase().trim();
    if (v === 'cold_outreach') return 'cold_outreach';
    if (v === 'newsletter') return 'newsletter';
    // Back-compat: treat transactional as business
    return 'business';
  };

  const domainTypeByDomain = useMemo(() => {
    const map = new Map<string, 'business' | 'newsletter' | 'cold_outreach'>();
    const domains = domainsData?.data?.domains || [];
    for (const d of domains) {
      const name = String(d?.domain || '').toLowerCase().trim();
      if (!name) continue;
      map.set(name, normalizeDomainType(d?.domainType || d?.domain_type));
    }
    return map;
  }, [domainsData]);

  // Transform email accounts data - only use real API data
  const emailAccounts = useMemo((): EmailAccount[] => {
    const apiAccounts = emailAccountsData?.data?.emailAccounts || [];
    
    // Debug logging
    console.log('🔍 Unibox - selectedAccount:', selectedAccount);
    console.log('🔍 Unibox - isAllAccountsSelected:', isAllAccountsSelected);
    console.log('🔍 Unibox - API accounts:', apiAccounts);
    
    // Filter accounts by selected account domains if a specific account is selected
    let filteredAccounts = apiAccounts;
    if (!isAllAccountsSelected && selectedAccount.id !== 'all' && selectedAccount.domains && selectedAccount.domains.length > 0) {
      // Filter by domains that belong to the selected account
      // selectedAccount.domains is an array of strings, not objects
      const accountDomains = Array.isArray(selectedAccount.domains) 
        ? selectedAccount.domains 
        : [];
      
      console.log('🔍 Unibox - Filtering by account domains:', accountDomains);
      
      filteredAccounts = apiAccounts.filter((account: any) => {
        const accountDomain = account.domain;
        const matches = accountDomains.includes(accountDomain);
        console.log(`🔍 Unibox - ${accountDomain} matches: ${matches}`);
        return matches;
      });
      
      console.log('🔍 Unibox - Filtered accounts:', filteredAccounts);
    } else {
      console.log('🔍 Unibox - Showing all accounts (no filtering)');
    }
    
    const result = filteredAccounts.map((account: any) => ({
      id: account.fullEmail || `${account.email}@${account.domain}`,
      email: account.fullEmail || `${account.email}@${account.domain}`,
      username: account.email,
      displayName: String(
        account.displayName ||
        account.name ||
        account.fullName ||
        `${String(account.firstName || '').trim()} ${String(account.lastName || '').trim()}`.trim() ||
        ''
      ).trim(),
      domain: account.domain,
      status: account.status || 'active',
      unreadCount: 0,
      lastActivity: account.lastUsed || account.updatedAt || account.createdAt || null,
      avatar: account.avatar || null,
      hideFromInbox: !!account.hideFromInbox
    }))
    .sort((a: EmailAccount, b: EmailAccount) =>
      String(a.email || '').toLowerCase().localeCompare(String(b.email || '').toLowerCase())
    );
    
    console.log('🔍 Unibox - Transformed email accounts:', result);
    return result;
  }, [emailAccountsData, selectedAccount?.id, isAllAccountsSelected]);

  // Fetch emails from API
  // CRITICAL: Use selectedInbox (email account selection) NOT selectedAccount (AWS account context)
  // When an email account is clicked, selectedInbox is set to that account's id or email
  // We need to find the email address from the selectedInbox
  const selectedEmailAccount = useMemo(() => {
    if (selectedInbox === 'all' || !selectedInbox) {
      // "Show all inboxes" should aggregate across all mailboxes in the current scope.
      return 'all';
    }
    
    // Find the email account by id or email
    const account = emailAccounts.find(acc => acc.id === selectedInbox || acc.email === selectedInbox);
    
    // CRITICAL DEBUG: Log account lookup
    if (!account) {
      console.warn(`⚠️ Unibox - Email account not found for selectedInbox: ${selectedInbox}`, {
        selectedInbox,
        availableAccounts: emailAccounts.map(acc => ({ id: acc.id, email: acc.email }))
      });
    } else {
      console.log(`✅ Unibox - Found email account:`, {
        selectedInbox,
        foundAccount: { id: account.id, email: account.email }
      });
    }
    
    return account?.email || null;
  }, [selectedInbox, emailAccounts]);
  
  // CRITICAL: Normalize emailAccount to lowercase for case-insensitive matching
  const normalizedEmailAccount = selectedEmailAccount 
    ? selectedEmailAccount.toLowerCase().trim() 
    : (isAllAccountsSelected ? 'all' : null);
  const sentStatsRange = useMemo(() => {
    const now = new Date();
    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
    const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      endAt: now.toISOString(),
      startToday: startToday.toISOString(),
      startWeek: startWeek.toISOString(),
      startMonth: startMonth.toISOString(),
    };
  }, []);
  const {
    data: sentStatsData,
    isLoading: isLoadingSentStats,
    isFetching: isFetchingSentStats,
    error: sentStatsError,
  } = useGetEmailSentStatsQuery(
    {
      ...sentStatsRange,
    },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }
  );

  const isAllEmailsVirtualFolderSelected = useMemo(
    () => isAllEmailsFolder(selectedFolder),
    [selectedFolder]
  );
  const isQuarantinedFolderSelected = useMemo(
    () => normalizeFolderLabel(selectedFolder) === 'quarantined',
    [selectedFolder]
  );
  const isQuarantinedView = activeTab === 'quarantined' || isQuarantinedFolderSelected;
  // Phase A (2026-05-14): protected `Warm Up` system folder selection.
  // Sibling to `isQuarantinedFolderSelected` and the All Emails virtual
  // folder check above. Used by the primary-tab filter below to take a
  // distinct rendering path that:
  //   (a) does NOT exclude `isSent === true` rows (the Warm Up folder is
  //       intentionally bidirectional — both inbound replies and outbound
  //       warm-up sends should surface here);
  //   (b) does NOT apply the legacy folder-string match (`email.folder`
  //       on legacy KrossKast rows is `Warm up: KrossKastChannels` which
  //       does not normalize to `warm up` — those rows would be hidden
  //       client-side even though the backend correctly returns them);
  //   (c) keys solely on `emailKind === 'warmup'` so the system folder is
  //       a true classification view, independent of any per-row physical
  //       folder label written by user-prefs auto-filers.
  // Recognises `Warm Up`, `warmup`, and `warm-up` defensively so any
  // future call site that uses an alias still resolves to this view.
  const isWarmupSystemFolderSelected = useMemo(() => {
    const normalized = normalizeFolderLabel(selectedFolder);
    return (
      normalized === normalizeFolderLabel(WARMUP_SYSTEM_FOLDER_LABEL) ||
      normalized === 'warmup' ||
      normalized === 'warm-up'
    );
  }, [selectedFolder]);
  // Phase A.1 (2026-05-14): the protected `Warm Up` system folder
  // scopes the population to `emailKind === 'warmup'`, but the active
  // sub-tab refines by direction inside that population:
  //   - Inbox / primary → inbound only (isSent !== true)
  //   - Sent            → outbound only (isSent === true)
  //   - Archive / Spam / Trash → no direction split; the standard flag
  //     filter (isArchived / isSpam / isDeleted) is applied on top of
  //     the warmup classification.
  // The derived `warmupDirectionParam` is forwarded to the backend so
  // the count badge (`emailFolderTotal`) on each sub-tab matches the
  // FE-rendered list length. Returns null when the folder isn't Warm
  // Up so the existing API/filter behavior for every other folder is
  // untouched. See WARMUP_SYSTEM_FOLDER_ARCHITECTURE_PROPOSAL.md §1.
  const warmupDirectionParam = useMemo<'inbound' | 'sent' | null>(() => {
    if (!isWarmupSystemFolderSelected) return null;
    if (activeTab === 'sent') return 'sent';
    if (activeTab === 'primary' || activeTab === 'inbox') return 'inbound';
    return null;
  }, [isWarmupSystemFolderSelected, activeTab]);
  const selectedFolderApiParam = useMemo(() => {
    if (!selectedFolder) return null;
    return isAllEmailsVirtualFolderSelected ? ALL_EMAILS_FOLDER_API_VALUE : selectedFolder;
  }, [selectedFolder, isAllEmailsVirtualFolderSelected]);
  const apiFolderParam = useMemo(() => {
    // If a custom folder is selected, ask backend to return only that folder.
    if (selectedFolderApiParam) return selectedFolderApiParam;
    return (activeTab === 'primary' ? 'inbox' : activeTab);
  }, [selectedFolderApiParam, activeTab]);
  const selectedFolderAliases = useMemo(
    () => (selectedFolder && !isAllEmailsVirtualFolderSelected ? getAliasesForFolder(folderAliases, selectedFolder) : []),
    [folderAliases, selectedFolder, isAllEmailsVirtualFolderSelected]
  );
  
  // CRITICAL DEBUG: Log what we're querying
  console.log('🔍 Unibox - Querying emails with:', {
    selectedEmailAccount,
    normalizedEmailAccount,
    selectedAccountEmail: selectedAccount?.email,
    selectedAccountId: selectedAccount?.id,
    isAllAccountsSelected,
    queryParams: {
      emailAccount: normalizedEmailAccount || 'all',
      limit: emailQueryLimit,
      search: normalizedEmailSearchTerm || undefined,
    }
  });
  
  // When viewing a folder or quarantined view, we can load with emailAccount 'all' even if no single inbox is selected.
  const effectiveEmailAccount = normalizedEmailAccount || (selectedFolder ? 'all' : null) || (activeTab === 'quarantined' ? 'all' : null);
  const { 
    data: emailsData, 
    isLoading: isLoadingEmails,
    isFetching: isRefreshingEmails,
    error: emailsError,
    refetch: refetchEmails 
  } = useGetEmailsQuery(
    { 
      emailAccount: effectiveEmailAccount || 'all',
      limit: emailQueryLimit,
      lastKey: hasActiveEmailSearch ? undefined : (emailPageCursor || undefined),
      search: normalizedEmailSearchTerm || undefined,
      // Ask backend for the correct folder so it can return deterministic results even without a sort-key index.
      folder: apiFolderParam,
      folderAliases: selectedFolder ? selectedFolderAliases : undefined,
      tenantId: tenantIdForPrefs || undefined,
      // Phase A.1: forward warmup direction (Inbox vs Sent vs none)
      // so the backend filter + count match the active sub-tab on the
      // protected Warm Up system folder. Backend ignores when folder
      // !== 'warmup'; safe to always forward.
      direction: warmupDirectionParam || undefined
    },
    {
      // Skip only when we have no account context AND no folder selected (folder view can use 'all')
      skip: !effectiveEmailAccount && !selectedFolder,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false
    }
  );

  /** Mirrors RTK inbox query busy flags for async spin teardown (refs update every render). */
  const emailQueryBusyRef = useRef(false);
  emailQueryBusyRef.current = isLoadingEmails || isRefreshingEmails;

  /** Tracks every explicit `refetch()`; spin clears only after min one CSS rotation + query idle. */
  const mailRefreshDepthRef = useRef(0);
  const minSpinDeadlineRef = useRef(0);
  const [mailRefreshTrackedPending, setMailRefreshTrackedPending] = useState(false);

  const tryEndMailRefreshTrackedSpin = useCallback(() => {
    if (mailRefreshDepthRef.current > 0) return;
    const now = Date.now();
    const deadline = minSpinDeadlineRef.current;
    if (deadline > 0 && now < deadline) {
      window.setTimeout(tryEndMailRefreshTrackedSpin, deadline - now);
      return;
    }
    if (emailQueryBusyRef.current) {
      window.requestAnimationFrame(tryEndMailRefreshTrackedSpin);
      return;
    }
    setMailRefreshTrackedPending(false);
    minSpinDeadlineRef.current = 0;
  }, []);

  const loadEmails = useCallback(async () => {
    mailRefreshDepthRef.current += 1;
    if (mailRefreshDepthRef.current === 1) {
      setMailRefreshTrackedPending(true);
      minSpinDeadlineRef.current = Math.max(
        minSpinDeadlineRef.current,
        Date.now() + UNIBOX_REFRESH_MIN_SPIN_MS
      );
    }
    try {
      return await refetchEmails();
    } finally {
      mailRefreshDepthRef.current -= 1;
      if (mailRefreshDepthRef.current === 0) {
        tryEndMailRefreshTrackedSpin();
      }
    }
  }, [refetchEmails, tryEndMailRefreshTrackedSpin]);

  /** Always latest `loadEmails` for effects that must not re-run when the callback identity changes (e.g. Auto interval). */
  const loadEmailsRef = useRef(loadEmails);
  loadEmailsRef.current = loadEmails;

  const emailRefreshToolbarBusy =
    isLoadingEmails || isRefreshingEmails || autoRefreshPrefsBusy;

  const emailRefreshIconSpinning =
    isLoadingEmails || isRefreshingEmails || mailRefreshTrackedPending;

  // Recovery refetch on inbox/folder/search context changes.
  // Keep this scoped to context shifts only (exclude page cursor) to avoid
  // duplicate fetches on next/previous page clicks.
  useEffect(() => {
    if (!effectiveEmailAccount && !selectedFolder) return;
    try {
      loadEmails();
    } catch (error) {
      console.warn('⚠️ Could not refetch emails on view change:', error);
    }
  }, [
    effectiveEmailAccount,
    apiFolderParam,
    selectedInbox,
    selectedFolder,
    selectedFolderAliases,
    activeTab,
    isAllAccountsSelected,
    loadEmails,
    selectedEmailAccount,
    normalizedEmailSearchTerm
  ]);

  // GUARDRAIL: Reset emailFolderTotal to 0 on every folder/tab switch so each folder
  // gets its own fresh count from the backend. Do NOT add anti-flicker guards that
  // prevent the count from lowering — trust the backend's total from getFolderTotalCountCached.
  // See kross-global-marketing-api/.cursor/rules/email-counts-architecture.mdc
  useEffect(() => {
    setEmailPageCursor(null);
    setEmailPageCursorHistory([]);
    setEmailCurrentPage(1);
    setEmailNextPageCursor(null);
    setEmailFallbackNextPageCursor(null);
    setEmailFolderTotal(0);
  }, [effectiveEmailAccount, apiFolderParam, selectedInbox, selectedFolder, selectedEmailAccount, activeTab, tenantIdForPrefs, normalizedEmailSearchTerm]);

  // Capture paging metadata from API response.
  useEffect(() => {
    if (!emailsData) return;
    const root = (emailsData as any) || {};
    const payload = root?.data && typeof root.data === 'object' ? root.data : root;
    const nextCursor = String(payload?.lastKey || root?.lastKey || '').trim() || null;
    const responseEmails = Array.isArray(payload?.emails)
      ? payload.emails
      : Array.isArray(root?.emails)
        ? root.emails
        : Array.isArray(root?.data)
          ? root.data
          : [];
    const totalCandidates = [
      payload?.total,
      payload?.totalCount,
      payload?.count,
      payload?.pagination?.total,
      root?.total,
      root?.totalCount,
      root?.count,
      root?.pagination?.total,
    ];
    const firstFiniteTotal = totalCandidates
      .map((candidate) => Number(candidate))
      .find((value) => Number.isFinite(value) && value >= 0);
    const fallbackTotal = responseEmails.length;
    const resolvedTotal = firstFiniteTotal !== undefined
      ? Math.max(Number(firstFiniteTotal), fallbackTotal)
      : fallbackTotal;
    const isEstimate = Boolean(payload?.totalIsEstimate || root?.totalIsEstimate);
    const searchPartial = Boolean(payload?.searchPartial || root?.searchPartial);
    console.log('📊 Unibox - Paging metadata:', {
      resolvedTotal,
      firstFiniteTotal,
      fallbackTotal,
      isEstimate,
      searchPartial,
      nextCursor: !!nextCursor,
      responseEmailsCount: responseEmails.length,
      payloadTotal: payload?.total,
      rootTotal: root?.total,
      backendDebug: payload?._debug || root?._debug || null,
    });
    setEmailNextPageCursor(nextCursor);
    setEmailFallbackNextPageCursor(nextCursor || buildSyntheticEmailCursor(responseEmails[responseEmails.length - 1]));
    setEmailFolderTotal(resolvedTotal);
    setEmailSearchPartial(searchPartial);
  }, [emailsData]);

  const handleNextEmailPage = () => {
    if (hasActiveEmailSearch) return;
    const totalPages = emailFolderTotal > 0
      ? Math.max(1, Math.ceil(emailFolderTotal / Math.max(1, emailLimit)))
      : 0;
    const hasMoreByTotal = totalPages > 0 && emailCurrentPage < totalPages;
    const resolvedNextCursor = emailNextPageCursor || (hasMoreByTotal ? emailFallbackNextPageCursor : null);
    if (!resolvedNextCursor || resolvedNextCursor === emailPageCursor) return;
    setEmailPageCursorHistory((prev) => [...prev, emailPageCursor]);
    setEmailPageCursor(resolvedNextCursor);
    setEmailCurrentPage((prev) => prev + 1);
  };

  const handlePreviousEmailPage = () => {
    if (hasActiveEmailSearch) return;
    if (emailPageCursorHistory.length === 0) return;
    const nextHistory = [...emailPageCursorHistory];
    const previousCursor = nextHistory.pop() ?? null;
    setEmailPageCursorHistory(nextHistory);
    setEmailPageCursor(previousCursor);
    setEmailCurrentPage((prev) => Math.max(1, prev - 1));
  };
  
  // Safe refetch function that checks if query is active before refetching
  const safeRefetchEmails = () => {
    if (effectiveEmailAccount || selectedFolder) {
      try {
        loadEmails();
      } catch (error) {
        console.warn('⚠️ Could not refetch emails (query may not be started):', error);
      }
    }
  };

  function buildUpdatePayload(args: {
    emailId?: string;
    id?: string;
    emailAccount?: string;
    updates?: Record<string, any>;
    email?: Email | null;
    folder?: string;
    isRead?: boolean;
    isDeleted?: boolean;
    isArchived?: boolean;
    isSpam?: boolean;
    status?: string | null;
    source?: string;
  }) {
    const emailId = args.emailId || args.id;
    if (!emailId) {
      throw new Error('Missing emailId for updateEmail');
    }

    const resolvedEmail =
      args.email ||
      filteredEmails.find((e) => e.id === emailId) ||
      (selectedEmailToView && selectedEmailToView.id === emailId ? selectedEmailToView : null);

    const emailAccount =
      args.emailAccount ||
      resolvedEmail?.emailAccount ||
      (Array.isArray(resolvedEmail?.to) ? resolvedEmail?.to[0] : resolvedEmail?.to) ||
      resolvedEmail?.from ||
      normalizedEmailAccount ||
      '';

    const updates: Record<string, any> = { ...(args.updates || {}) };
    if (args.folder !== undefined) updates.folder = args.folder;
    if (args.isRead !== undefined) updates.isRead = args.isRead;
    if (args.isDeleted !== undefined) updates.isDeleted = args.isDeleted;
    if (args.isArchived !== undefined) updates.isArchived = args.isArchived;
    if (args.isSpam !== undefined) updates.isSpam = args.isSpam;
    if (args.status !== undefined) updates.status = args.status;

    return {
      emailId,
      emailAccount,
      updates,
      ...(args.source ? { source: args.source } : {}),
      unifiedPk: resolvedEmail?.unifiedPk,
      unifiedSk: resolvedEmail?.unifiedSk
    };
  }

  const getActionEmailIds = (): string[] => {
    if (selectedEmails.size > 0) return Array.from(selectedEmails);
    if (selectedEmailToView?.id) return [selectedEmailToView.id];
    return [];
  };

  const flushPendingDelete = useCallback(() => {
    const pending = pendingDeleteRef.current;
    if (!pending) return;
    clearTimeout(pending.timerId);
    toast.dismiss(pending.toastId);
    pendingDeleteRef.current = null;
    if (pending.type === 'permanent' && pending.capturedKeys && pending.capturedKeys.length > 0) {
      permanentDeleteEmails({ emailIds: pending.capturedKeys }).unwrap().then(() => loadEmails()).catch(console.error);
    } else if (pending.type === 'purge') {
      purgeTrash({}).unwrap().then(() => loadEmails()).catch(console.error);
    }
  }, [permanentDeleteEmails, purgeTrash, loadEmails]);

  const cancelPendingDelete = useCallback(() => {
    const pending = pendingDeleteRef.current;
    if (!pending) return;
    clearTimeout(pending.timerId);
    toast.dismiss(pending.toastId);
    setOptimisticRemovedIds((prev) => {
      const next = new Set(prev);
      pending.idsToRestore.forEach((id) => next.delete(id));
      return next;
    });
    pendingDeleteRef.current = null;
    loadEmails();
  }, [loadEmails]);

  // CRITICAL: If selected inbox no longer resolves (e.g. account list changed),
  // fail safe back to all inboxes so query is not skipped.
  useEffect(() => {
    if (selectedInbox && selectedInbox !== 'all' && !selectedEmailAccount) {
      console.warn('⚠️ Selected inbox missing from account list, falling back to all:', selectedInbox);
      setSelectedInbox('all');
    }
  }, [selectedInbox, selectedEmailAccount]);

  useEffect(() => {
    if (emailListScrollRef.current) {
      emailListScrollRef.current.scrollTop = 0;
    }
  }, [selectedInbox, selectedDomainFilter, activeTab]);

  // Fire any pending hard-delete if component unmounts during the 5s grace window
  useEffect(() => {
    return () => {
      if (pendingDeleteRef.current) {
        clearTimeout(pendingDeleteRef.current.timerId);
        const pending = pendingDeleteRef.current;
        pendingDeleteRef.current = null;
        if (pending.type === 'permanent' && pending.capturedKeys && pending.capturedKeys.length > 0) {
          permanentDeleteEmails({ emailIds: pending.capturedKeys }).catch(() => {});
        } else if (pending.type === 'purge') {
          purgeTrash({}).catch(() => {});
        }
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle manual refresh
  const handleRefresh = async (): Promise<void> => {
    console.log('🔄 Unibox - Refresh button clicked');
    try {
      await loadEmails();
      console.log('✅ Unibox - Emails refreshed successfully');
    } catch (error: any) {
      console.error('❌ Unibox - Error refreshing emails:', error);
    }
  };

  const handleShowAllEmailsFeed = (): void => {
    // Restore the unified feed across all inboxes in the current scope.
    setSelectedInbox('all');
  };

  const hasAnyLeftFiltersActive = Boolean(
    filterMode !== 'inboxes' ||
    String(searchTerm || '').trim() ||
    selectedDomainFilter !== 'all' ||
    selectedPurposeFilter !== 'all' ||
    Boolean(selectedCampaign) ||
    Boolean(selectedStatus) ||
    Boolean(selectedFolder) ||
    Boolean(selectedLabel) ||
    String(folderSearchTerm || '').trim() ||
    String(statusSearchTerm || '').trim()
  );

  const handleClearAllFilters = (): void => {
    // Left/first section reset: return inbox list + filters to defaults.
    setFilterMode('inboxes');
    setSearchTerm('');
    setSelectedDomainFilter('all');
    setSelectedPurposeFilter('all');
    setSelectedCampaign(null);
    setShowInboxFilters(false);

    // Clear other left-panel modes/filters so the UI fully resets.
    setSelectedStatus(null);
    setStatusSearchTerm('');
    setSelectedFolder(null);
    setFolderSearchTerm('');
    setSelectedLabel(null);
  };

  // Save auto-refresh state to backend whenever it changes
  useEffect(() => {
    // Only save if preferences have loaded (to avoid overwriting with default)
    if (!isLoadingPreferences && userPreferencesData) {
      updateUserPreferences({
        ...preferencesTenantWriteSpread(tenantIdForPrefs),
        preferences: { uniboxAutoRefresh: autoRefresh }
      }).catch((error) => {
        console.error('❌ Error saving auto refresh preference:', error);
      });
    }
  }, [autoRefresh, isLoadingPreferences, tenantIdForPrefs, userPreferencesData, updateUserPreferences]);

  // Auto-refresh: same tracked `loadEmails` as manual refresh (not a stale `handleRefresh` closure).
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (autoRefresh) {
      void loadEmailsRef.current().catch((error: unknown) => {
        console.error('❌ Unibox - Auto-refresh initial load failed:', error);
      });

      intervalId = setInterval(() => {
        void loadEmailsRef.current().catch((error: unknown) => {
          console.error('❌ Unibox - Auto-refresh interval load failed:', error);
        });
      }, 30000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefresh]);
  
  // CRITICAL DEBUG: Log API response
  let fullResponseStr = 'null/undefined';
  try {
    if (emailsData) {
      const stringified = JSON.stringify(emailsData, null, 2);
      if (stringified) {
        fullResponseStr = stringified.substring(0, 1000);
      }
    }
  } catch (e: any) {
    fullResponseStr = `Error stringifying: ${e?.message || String(e)}`;
  }
  
  console.log('🔍 Unibox - API Response:', {
    emailsData,
    isLoadingEmails,
    emailsError,
    hasData: !!emailsData,
    emailCount: emailsData?.data?.emails?.length || 0,
    fullResponse: fullResponseStr,
    emailsDataKeys: emailsData ? Object.keys(emailsData) : [],
    dataKeys: emailsData?.data ? Object.keys(emailsData.data) : [],
    emailsType: Array.isArray(emailsData?.data?.emails) ? 'array' : typeof emailsData?.data?.emails
  });
  
  if (!effectiveEmailAccount && !selectedFolder) {
    console.warn('⚠️ Unibox - Query is SKIPPED (no account or folder context)');
  }

  // Force refetch when selected account changes
  useEffect(() => {
    console.log('🔄 Unibox - useEffect triggered, selectedAccount:', selectedAccount);
    if (selectedAccount?.id) {
      console.log('🔄 Unibox - Account changed, refetching:', selectedAccount.id);
      loadEmailAccounts();
      // Use safe refetch to avoid error #38 (cannot refetch query that hasn't started)
      safeRefetchEmails();
    }
  }, [selectedAccount?.id, isAllAccountsSelected]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const [sendEmail, { isLoading: isSendingEmail }] = useSendEmailMutation();

  // Transform email data from API
  const emails = useMemo((): Email[] => {
    console.log('📧 Unibox - emailsData:', emailsData);
    console.log('📧 Unibox - emailsData?.data?.emails:', emailsData?.data?.emails);
    
    let fullStructureStr = 'null/undefined';
    try {
      if (emailsData) {
        const stringified = JSON.stringify(emailsData, null, 2);
        if (stringified) {
          fullStructureStr = stringified.substring(0, 2000);
        }
      }
    } catch (e: any) {
      fullStructureStr = `Error stringifying: ${e?.message || String(e)}`;
    }
    
    // If we have no account context and no folder selected, return empty (query is skipped)
    if (!effectiveEmailAccount && !selectedFolder) {
      console.log('📧 Unibox - No account/folder context, returning empty array');
      return [];
    }
    
    console.log('📧 Unibox - emailsData structure:', {
      hasEmailsData: !!emailsData,
      hasData: !!emailsData?.data,
      hasEmails: !!emailsData?.data?.emails,
      emailsIsArray: Array.isArray(emailsData?.data?.emails),
      emailsLength: emailsData?.data?.emails?.length,
      fullStructure: fullStructureStr
    });
    
    // Try multiple possible response structures
    let emailArray = null;
    if (Array.isArray(emailsData?.data?.emails)) {
      emailArray = emailsData.data.emails;
      console.log('✅ Found emails in emailsData.data.emails');
    } else if (Array.isArray(emailsData?.emails)) {
      emailArray = emailsData.emails;
      console.log('✅ Found emails in emailsData.emails');
    } else if (Array.isArray(emailsData?.data)) {
      emailArray = emailsData.data;
      console.log('✅ Found emails in emailsData.data (direct array)');
    }
    
    if (!emailArray || emailArray.length === 0) {
      console.log('⚠️ Unibox - No emails data from API');
      let fullEmailsDataStr = 'null/undefined';
      try {
        if (emailsData) {
          const stringified = JSON.stringify(emailsData, null, 2);
          if (stringified) {
            fullEmailsDataStr = stringified;
          }
        }
      } catch (e: any) {
        fullEmailsDataStr = `Error stringifying: ${e?.message || String(e)}`;
      }
      console.log('⚠️ Unibox - Full emailsData:', fullEmailsDataStr);
      return [];
    }
    
    console.log(`📧 Unibox - Found ${emailArray.length} emails from API`);

    const normalizeBool = (value: any, fallback = false): boolean => {
      if (value === true) return true;
      if (value === false) return false;
      if (value === 'true') return true;
      if (value === 'false') return false;
      if (value?.BOOL === true || value?.BOOL === false) return value.BOOL === true;
      if (value?.BOOL?.BOOL === true || value?.BOOL?.BOOL === false) return value.BOOL.BOOL === true;
      return fallback;
    };
    
    const transformed = emailArray
    .filter((email: any) => isMailboxEmailLikeRecord(email))
    .map((email: any) => {
      // Find matching account ID (case-insensitive: API may lowercase emailAccount)
      const ea = String(email.emailAccount || '').toLowerCase().trim();
      const toFirst = Array.isArray(email.to)
        ? String(email.to[0] || '').toLowerCase().trim()
        : String(email.to || '').toLowerCase().trim();
      const accountId =
        emailAccounts.find((acc: EmailAccount) => {
          const ae = String(acc.email || '').toLowerCase().trim();
          return ae && (ae === ea || (toFirst && ae === toFirst));
        })?.id ||
        email.emailAccount ||
        'unknown';
      const attachments = normalizeAttachmentMetaList(
        email.attachments || email.attachmentMeta || email.AttachmentMeta || email.Attachments
      );
      const attachmentCountRaw = Number(email.attachmentCount ?? email.AttachmentCount ?? attachments.length);
      const attachmentCount = Number.isFinite(attachmentCountRaw) ? attachmentCountRaw : attachments.length;
      const hasAttachment =
        normalizeBool(email.hasAttachment, attachmentCount > 0 || attachments.length > 0) ||
        attachmentCount > 0 ||
        attachments.length > 0;
      
      const unifiedPk = email.PK || email.pk || email.unifiedPk;
      const unifiedSk = email.SK || email.sk || email.unifiedSk;
      const stableFallbackId = [
        unifiedPk,
        unifiedSk,
        email.messageId,
        email.subject,
        email.date,
        email.from,
        Array.isArray(email.to) ? email.to[0] : email.to
      ].filter(Boolean).join('|');
      const emailId = email.id || email.messageId || unifiedSk || stableFallbackId;
      
      // CRITICAL: Check optimistic read status first, then fall back to API value
      const optimisticIsRead = optimisticReadStatus.get(emailId);
      const apiIsRead = normalizeBool(email.isRead, false);
      const finalIsRead = optimisticIsRead !== undefined ? optimisticIsRead : apiIsRead;
      
      return {
        id: emailId,
        accountId: accountId,
        emailAccount: email.emailAccount,
        unifiedPk: unifiedPk,
        unifiedSk: unifiedSk,
        messageId: email.messageId || null,
        inReplyTo: String(email.inReplyTo || '').trim() || undefined,
        references: String(email.references || '').trim() || undefined,
        s3Key: email.s3Key || null,
        s3Bucket: email.s3Bucket || null,
        sender: deriveSenderLabel(email),
        from:
          normalizeSenderToken(email.from) ||
          normalizeSenderToken(email.senderAddress) ||
          normalizeSenderToken(email.returnPath) ||
          '',
        senderAddress: normalizeSenderToken(email.senderAddress) || '',
        senderName: sanitizeHeaderToken(email.senderName) || '',
        fromHeader: sanitizeHeaderToken(email.fromHeader) || '',
        senderHeader: sanitizeHeaderToken(email.senderHeader) || '',
        returnPath: normalizeSenderToken(email.returnPath) || '',
        replyTo: normalizeSenderToken(email.replyTo) || '',
        envelopeFrom: normalizeSenderToken(email.envelopeFrom) || '',
        to: email.to || [],
        cc: email.cc || [],
        bcc: email.bcc || [],
        subject: email.subject || '(No Subject)',
        preview: getPreviewSnippet({ text: email.text, html: email.html }, 100),
        text: email.text,
        html: email.html,
        date: email.date || email.createdAt || email.updatedAt || FALLBACK_EMAIL_DATE_ISO,
        // CRITICAL: Use optimistic read status if available, otherwise use API value
        isRead: finalIsRead,
        isImportant: normalizeBool(email.isImportant, false),
        hasAttachment,
        attachmentCount,
        attachments,
        isSystemEmail: normalizeBool(email.isSystemEmail, false),
        isSent: normalizeBool(email.isSent, false),
        // CRITICAL: Normalize legacy/nested Dynamo bools consistently
        isDeleted: normalizeBool(email.isDeleted, false),
        isArchived: normalizeBool(email.isArchived, false),
        isSpam: normalizeBool(email.isSpam, false),
        folder: email.folder ?? (email as any).folder ?? null,
        // CRITICAL: Handle DynamoDB format {S: "status"} -> convert to string
        status: email.status?.S !== undefined ? email.status.S : (email.status || null),
        // Phase A (2026-05-14): preserve `emailKind` so the protected
        // `Warm Up` system folder filter (in `filteredEmails`) can key
        // off `emailKind === 'warmup'`. Without this carry-through the
        // transform silently strips the field (only listed keys survive),
        // and the Warm Up early-return reads `undefined`, dropping every
        // warm-up row even though the backend correctly returned it.
        emailKind: typeof (email as any).emailKind === 'string' ? (email as any).emailKind : null
      };
    });
    
    // Debug: Log ALL emails and specifically hello-related ones
    console.log(`📧 Unibox - Total transformed emails: ${transformed.length}`);
    console.log('📧 Unibox - All emails:', transformed.map((e: Email) => ({
      id: e.id,
      emailAccount: e.emailAccount,
      from: e.from,
      to: e.to,
      subject: e.subject,
      isSent: e.isSent,
      isSystemEmail: e.isSystemEmail,
      isRead: e.isRead // CRITICAL: Log isRead status
    })));
    
    // CRITICAL: Log read/unread counts
    const readCount = transformed.filter((e: Email) => e.isRead).length;
    const unreadCount = transformed.filter((e: Email) => !e.isRead).length;
    console.log(`📧 Unibox - Read/Unread counts: ${readCount} read, ${unreadCount} unread`);
    console.log(`📧 Unibox - Optimistic read status map size: ${optimisticReadStatus.size}`);
    
    // Debug: Log all emails related to hello@krosskastmusic.com
    const helloEmails = transformed.filter((e: Email) => 
      e.emailAccount?.toLowerCase().includes('hello@krosskastmusic') ||
      (Array.isArray(e.to) && e.to.some((t: string) => t.toLowerCase().includes('hello@krosskastmusic')))
    );
    console.log(`📧 Unibox - Found ${helloEmails.length} emails related to hello@krosskastmusic.com:`, helloEmails);
    
    // Filter out emails from hidden accounts when viewing "All Inboxes"
    if (selectedInbox === 'all' || !selectedInbox) {
      const hiddenEmails = new Set(
        emailAccounts.filter((acc: any) => acc.hideFromInbox).map((acc: any) => acc.email?.toLowerCase())
      );
      if (hiddenEmails.size > 0) {
        return transformed.filter((e: any) => !hiddenEmails.has(String(e.emailAccount || '').toLowerCase()));
      }
    }

    return transformed;
  }, [emailsData, emailAccounts, effectiveEmailAccount, selectedFolder, optimisticReadStatus, selectedInbox]);

  // Legacy warmup fallback auto-file: used only when no explicit auto-filters exist.
  useEffect(() => {
    if (!warmupEnabled) return;
    const hasEnabledAutoFilters = autoFilters.some(
      (rule) => rule.enabled && String(rule.pattern || '').trim() && String(rule.folder || '').trim()
    );
    if (hasEnabledAutoFilters) return;
    // Ensure folder exists in prefs list (best effort)
    if (warmupFolderName && !folders.includes(warmupFolderName)) {
      const next = Array.from(new Set([...(folders || []), warmupFolderName]));
      setFolders(next);
      updateUserPreferences({ ...preferencesTenantWriteSpread(tenantIdForPrefs), preferences: { uniboxAutoRefresh: autoRefresh, uniboxFolders: next, warmupRule } })
        .unwrap()
        .catch(() => {});
    }
    // Only auto-file on inbox view (avoid touching sent/spam/trash)
    if (!(activeTab === 'primary' || activeTab === 'inbox')) return;
    // Avoid auto-filing while user is explicitly viewing a folder
    if (selectedFolder) return;

    // Build regex. We enforce "has letters + digits" to avoid too many false positives.
    let tokenRe: RegExp | null = null;
    try {
      const raw = String(warmupRule?.pattern || '[A-Z0-9]{6,10}');
      tokenRe = new RegExp(`\\b${raw}\\b`, 'i');
    } catch {
      tokenRe = /\b[A-Z0-9]{6,10}\b/i;
    }
    const hasLetterAndDigit = (s: string) => /[a-z]/i.test(s) && /\d/.test(s);

    const candidates = (emails || [])
      .filter((e: any) => e && e.id && !warmupProcessedIds.has(e.id))
      .filter((e: any) => e.isSent !== true && e.isDeleted !== true && e.isArchived !== true && e.isSpam !== true)
      .filter((e: any) => !String(e.folder || '').trim())
      .filter((e: any) => {
        if (isReplyOrAutomaticInboundEmail(e)) {
          return false;
        }
        const hay = `${e.subject || ''}\n${e.preview || ''}\n${e.text || ''}`.slice(0, 5000);
        const m = hay.match(tokenRe!);
        if (!m) return false;
        return hasLetterAndDigit(String(m[0] || ''));
      })
      .slice(0, 10); // rate limit per refresh

    if (candidates.length === 0) return;

    (async () => {
      for (const e of candidates) {
        warmupProcessedIds.add(e.id);
        const emailAccount = e.emailAccount || (Array.isArray(e.to) ? e.to[0] : e.to) || e.from;
        if (!emailAccount) continue;
        try {
          await updateEmail(buildUpdatePayload({
            emailId: e.id,
            emailAccount,
            updates: { folder: warmupFolderName, isDeleted: false, isArchived: false, isSpam: false },
            email: e,
            source: 'auto_filter_legacy_warmup',
          })).unwrap();
        } catch (err) {
          // Best-effort: don't block UX
          console.warn('Warmup auto-file failed for email', e?.id, err);
        }
      }
      // Refresh view after filing
      loadEmails();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emails, autoFilters, warmupEnabled, warmupFolderName, activeTab, selectedFolder]);

  // CRITICAL: Clear optimistic updates when server confirms the email is read
  // Check the RAW API data, not the transformed emails (which use optimistic values)
  useEffect(() => {
    if (optimisticReadStatus.size === 0) return;
    if (!emailsData) return;
    
    // Get raw email array from API response
    let rawEmailArray: any[] = [];
    if (Array.isArray(emailsData?.data?.emails)) {
      rawEmailArray = emailsData.data.emails;
    } else if (Array.isArray(emailsData?.emails)) {
      rawEmailArray = emailsData.emails;
    } else if (Array.isArray(emailsData?.data)) {
      rawEmailArray = emailsData.data;
    }
    
    // Check each optimistic update - if server now has isRead: true, clear it
    setOptimisticReadStatus(prev => {
      const newMap = new Map(prev);
      let changed = false;
      
      prev.forEach((optimisticValue, emailId) => {
        // Find the RAW email in API response (not transformed)
        const rawEmail = rawEmailArray.find((e: any) => {
          const eId = e.id || e.messageId || '';
          return eId === emailId;
        });
        
        if (rawEmail) {
          // CRITICAL: Check RAW API value, not transformed value
          // Parse the raw DynamoDB format
          const rawServerIsRead = rawEmail.isRead?.BOOL !== undefined 
            ? rawEmail.isRead.BOOL 
            : (rawEmail.isRead === true || rawEmail.isRead === 'true' || false);
          
          // Only clear if server EXPLICITLY has isRead: true
          if (rawServerIsRead === true && optimisticValue === true) {
            newMap.delete(emailId);
            changed = true;
            console.log(`✅ Clearing optimistic update for ${emailId} - server confirmed isRead: true`);
          } else {
            // Server doesn't have isRead: true yet, keep optimistic update
            console.log(`⏳ Keeping optimistic update for ${emailId} - raw server isRead: ${rawServerIsRead}, optimistic: ${optimisticValue}`);
          }
        } else {
          // Email not found in raw API response - keep optimistic update
          console.log(`⏳ Keeping optimistic update for ${emailId} - email not found in raw API response`);
        }
      });
      
      return changed ? newMap : prev;
    });
  }, [emailsData, optimisticReadStatus]);

  useEffect(() => {
    if (optimisticRemovedIds.size === 0) return;
    // Don't clear optimistic removals while a delayed delete is pending (5s undo window)
    if (pendingDeleteRef.current) return;
    setOptimisticRemovedIds(new Set());
  }, [emailsData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update unread counts for email accounts
  const emailAccountsWithUnread = useMemo((): EmailAccount[] => {
    if (emails.length === 0) return emailAccounts;
    
    return emailAccounts.map((account: EmailAccount) => {
      const accountEmails = emails.filter(
        (email: Email) => email.accountId === account.id || email.emailAccount === account.email
      );
      const unreadCount = accountEmails.filter((email: Email) => !email.isRead).length;
      return {
        ...account,
        unreadCount
      };
    });
  }, [emailAccounts, emails]);

  // Sorted list for Compose "From" dropdown (alphabetical by email). Only include accounts with full email (user@domain) so API accepts them.
  const composeFromOptions = useMemo((): EmailAccount[] => {
    return [...emailAccountsWithUnread]
      .filter((a: EmailAccount) => (a.email || '').includes('@'))
      .sort((a, b) =>
        (a.email || '').toLowerCase().localeCompare((b.email || '').toLowerCase())
      );
  }, [emailAccountsWithUnread]);

  const availableDomains = useMemo(() => {
    const set = new Set<string>();
    for (const a of emailAccountsWithUnread) {
      const d = String(a?.domain || '').toLowerCase().trim();
      if (d) set.add(d);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [emailAccountsWithUnread]);

  const availableCampaigns = useMemo(() => {
    // Best-effort: derive campaign identifiers from whatever the backend returns.
    // If none exist, the dropdown will just show "All campaigns".
    const set = new Set<string>();
    for (const e of emails as any[]) {
      const raw =
        e?.campaignName ??
        e?.campaign ??
        e?.campaignId ??
        e?.campaign_id ??
        e?.metadata?.campaign ??
        e?.metadata?.campaignName ??
        e?.headers?.['X-Campaign'] ??
        e?.headers?.['x-campaign'];
      const v = String(raw || '').trim();
      if (v) set.add(v);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [emails]);

  const hiddenStatusKeySet = useMemo(() => {
    const set = new Set<string>();
    (hiddenStatusLabels || []).forEach((value) => {
      const key = normalizeStatusLabel(value);
      if (key) set.add(key);
    });
    return set;
  }, [hiddenStatusLabels]);

  const statusFallbackLabel = useMemo(
    () => (hiddenStatusKeySet.has(normalizeStatusLabel('Lead')) ? UNKNOWN_STATUS_LABEL : 'Lead'),
    [hiddenStatusKeySet]
  );

  const statusOptions = useMemo((): StatusOption[] => {
    const customStatuses: StatusOption[] = [];
    const customKeys = new Set<string>();
    (customStatusLabels || []).forEach((value) => {
      const name = String(value || '').trim();
      const key = normalizeStatusLabel(name);
      if (!name || !key || DEFAULT_STATUS_KEYS.has(key) || customKeys.has(key)) return;
      customKeys.add(key);
      customStatuses.push({ name, color: DEFAULT_STATUS_COLOR });
    });

    const dynamicStatuses = new Map<string, StatusOption>();
    const addDynamicStatus = (rawValue: unknown) => {
      const name = String(rawValue || '').trim();
      const key = normalizeStatusLabel(name);
      if (!name || !key || DEFAULT_STATUS_KEYS.has(key) || customKeys.has(key) || dynamicStatuses.has(key)) return;
      dynamicStatuses.set(key, { name, color: DEFAULT_STATUS_COLOR });
    };

    (emails || []).forEach((email) => addDynamicStatus(email?.status));
    addDynamicStatus(selectedEmailToView?.status);

    const dynamicSorted = Array.from(dynamicStatuses.values()).sort((a, b) => (
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    ));
    return [...DEFAULT_STATUS_OPTIONS, ...customStatuses, ...dynamicSorted];
  }, [emails, selectedEmailToView?.status, customStatusLabels]);

  const visibleStatusOptions = useMemo(
    () => statusOptions.filter((status) => !hiddenStatusKeySet.has(normalizeStatusLabel(status.name))),
    [statusOptions, hiddenStatusKeySet]
  );

  const alphabeticalStatusOptions = useMemo(
    () => [...visibleStatusOptions].sort((a, b) => (
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    )),
    [visibleStatusOptions]
  );

  const isStatusLabelDeletable = (statusName: string): boolean => (
    !PROTECTED_STATUS_KEYS.has(normalizeStatusLabel(statusName))
  );

  const openCreateStatusLabelModal = (): void => {
    setNewStatusLabelName('');
    setShowCreateStatusLabelModal(true);
  };

  const handleCreateStatusLabel = async (): Promise<void> => {
    const raw = String(newStatusLabelName || '').trim();
    const key = normalizeStatusLabel(raw);
    if (!raw || !key) return;

    const existingStatus = statusOptions.find((status) => normalizeStatusLabel(status.name) === key) || null;
    const isCurrentlyHidden = hiddenStatusKeySet.has(key);
    if (existingStatus && !isCurrentlyHidden) {
      alert('A status label with that name already exists.');
      return;
    }

    const hasCustomStatus = customStatusLabels.some((value) => normalizeStatusLabel(value) === key);
    const nextCustomStatuses =
      !DEFAULT_STATUS_KEYS.has(key) && !hasCustomStatus
        ? [...customStatusLabels, raw]
        : customStatusLabels;
    const nextHiddenStatuses = hiddenStatusLabels.filter((value) => normalizeStatusLabel(value) !== key);

    setIsSavingStatusLabel(true);
    try {
      await updateUserPreferences({
        ...preferencesTenantWriteSpread(tenantIdForPrefs),
        preferences: {
          uniboxCustomStatuses: nextCustomStatuses,
          uniboxHiddenStatuses: nextHiddenStatuses,
        },
      }).unwrap();
      setCustomStatusLabels(nextCustomStatuses);
      setHiddenStatusLabels(nextHiddenStatuses);
      setShowCreateStatusLabelModal(false);
      setNewStatusLabelName('');
      setSelectedStatus(existingStatus?.name || raw);
      setFilterMode('status');
    } catch (error: any) {
      console.error('Failed to create status label:', error);
      alert(`Failed to create status label: ${error?.data?.message || error?.message || 'Unknown error'}`);
    } finally {
      setIsSavingStatusLabel(false);
    }
  };

  const handleDeleteStatusLabel = async (statusName: string): Promise<void> => {
    const key = normalizeStatusLabel(statusName);
    if (!key || hiddenStatusKeySet.has(key) || !isStatusLabelDeletable(statusName)) return;
    const shouldDelete = window.confirm(
      `Delete status label "${statusName}"? Emails using it will be reassigned to "${UNKNOWN_STATUS_LABEL}".`
    );
    if (!shouldDelete) return;

    const matchingEmailsById = new Map<string, Email>();
    (emails || []).forEach((email) => {
      if (!email?.id) return;
      if (normalizeStatusLabel(email.status) !== key) return;
      matchingEmailsById.set(email.id, email);
    });
    if (
      selectedEmailToView?.id &&
      normalizeStatusLabel(selectedEmailToView.status) === key
    ) {
      matchingEmailsById.set(selectedEmailToView.id, selectedEmailToView);
    }

    const matchingEmails = Array.from(matchingEmailsById.values());
    const nextHiddenStatuses = Array.from(new Set([
      ...hiddenStatusLabels,
      key,
    ].map((value) => normalizeStatusLabel(value)).filter(Boolean)));

    setIsUpdatingHiddenStatuses(true);
    try {
      if (matchingEmails.length > 0) {
        const reassignResults = await Promise.allSettled(
          matchingEmails.map((email) => (
            updateEmail(
              buildUpdatePayload({
                emailId: email.id,
                email,
                status: UNKNOWN_STATUS_LABEL,
              })
            ).unwrap()
          ))
        );
        const failedCount = reassignResults.filter((result) => result.status === 'rejected').length;
        if (failedCount > 0) {
          throw new Error(`Failed to reassign ${failedCount} email${failedCount === 1 ? '' : 's'} to ${UNKNOWN_STATUS_LABEL}.`);
        }
      }

      await updateUserPreferences({
        ...preferencesTenantWriteSpread(tenantIdForPrefs),
        preferences: {
          uniboxHiddenStatuses: nextHiddenStatuses,
        },
      }).unwrap();
      setHiddenStatusLabels(nextHiddenStatuses);
      if (selectedStatus && normalizeStatusLabel(selectedStatus) === key) {
        setSelectedStatus(UNKNOWN_STATUS_LABEL);
      }
      if (selectedEmailToView && normalizeStatusLabel(selectedEmailToView.status) === key) {
        setSelectedEmailToView({ ...selectedEmailToView, status: UNKNOWN_STATUS_LABEL });
      }
      safeRefetchEmails();
    } catch (error: any) {
      console.error('Failed to delete status label:', error);
      alert(`Failed to delete status label: ${error?.data?.message || error?.message || 'Unknown error'}`);
    } finally {
      setIsUpdatingHiddenStatuses(false);
    }
  };

  useEffect(() => {
    if (!selectedStatus) return;
    if (!hiddenStatusKeySet.has(normalizeStatusLabel(selectedStatus))) return;
    setSelectedStatus(null);
  }, [hiddenStatusKeySet, selectedStatus]);
  
  // Calculate actual unread count per account from real emails
  const unreadCountsByAccount = useMemo(() => {
    const counts: Record<string, number> = {};
    const accountEmails: Record<string, Email[]> = {};
    
    // Initialize counts and group emails by account
    emailAccountsWithUnread.forEach((account: EmailAccount) => {
      counts[account.id] = 0;
      counts[account.email] = 0;
      accountEmails[account.id] = [];
      accountEmails[account.email] = [];
    });
    
    // Group emails by account and count unread
    emails.forEach((email: Email) => {
      const accountKey = email.accountId || email.emailAccount || 'unknown';
      if (accountEmails[accountKey]) {
        accountEmails[accountKey].push(email);
        if (!email.isRead) {
          counts[accountKey] = (counts[accountKey] || 0) + 1;
        }
      }
    });
    
    return { counts, accountEmails };
  }, [emailAccountsWithUnread, emails]);

  // Filter emails based on selected inbox, search, tab, and filter mode
  const filteredEmails = useMemo((): Email[] => {
    let filtered = emails;

    const getEmailDomains = (email: Email): string[] => {
      const domains: string[] = [];
      const pushDomain = (addr: any) => {
        const s = String(addr || '').toLowerCase().trim();
        const d = s.includes('@') ? s.split('@')[1]?.toLowerCase().trim() : '';
        if (d) domains.push(d);
      };
      if (email.emailAccount) pushDomain(email.emailAccount);
      if (email.from) pushDomain(email.from);
      if (Array.isArray(email.to)) email.to.forEach(pushDomain);
      else if (email.to) pushDomain(email.to);
      return Array.from(new Set(domains)).filter(Boolean);
    };

    // CRITICAL: First filter by AWS account domains (if a specific AWS account is selected)
    // Only show emails that belong to domains in the selected AWS account
    // Access domains directly here to avoid dependency issues - only check when account ID changes
    if (!isAllAccountsSelected && selectedAccount?.id && selectedAccount.id !== 'all' && selectedAccount.domains && Array.isArray(selectedAccount.domains) && selectedAccount.domains.length > 0) {
      // Create normalized domains array here (not memoized to avoid dependency issues)
      const accountDomains = selectedAccount.domains.map((d: string) => d.toLowerCase().trim());
      
      console.log('🔍 Unibox - Filtering emails by AWS account domains:', accountDomains);
      
      filtered = filtered.filter((email: Email) => {
        // Extract domain from emailAccount (e.g., "hello@krosskastmusic.com" -> "krosskastmusic.com")
        const emailAccountDomain = email.emailAccount?.split('@')[1]?.toLowerCase().trim();
        
        // Extract domain from 'to' field
        const toDomains = Array.isArray(email.to)
          ? email.to.map((to: string) => to.split('@')[1]?.toLowerCase().trim()).filter(Boolean)
          : (email.to?.split('@')[1]?.toLowerCase().trim() ? [email.to.split('@')[1].toLowerCase().trim()] : []);
        
        // Extract domain from 'from' field
        const fromDomain = email.from?.split('@')[1]?.toLowerCase().trim();
        
        // Check if any domain matches
        const emailAccountMatches = emailAccountDomain && accountDomains.includes(emailAccountDomain);
        const toMatches = toDomains.some((domain: string) => accountDomains.includes(domain));
        const fromMatches = fromDomain && accountDomains.includes(fromDomain);
        
        const matches = emailAccountMatches || toMatches || fromMatches;
        
        if (!matches) {
          console.log(`❌ Filtering out email - doesn't match AWS account domains:`, {
            emailId: email.id,
            emailAccount: email.emailAccount,
            emailAccountDomain,
            to: email.to,
            toDomains,
            from: email.from,
            fromDomain,
            accountDomains
          });
        }
        
        return matches;
      });
      console.log(`🔍 After AWS account domain filter: ${filtered.length} emails`);
    }

    // CRITICAL: Then filter by selected email account (if a specific email is selected)
    // Only show emails that belong to the selected account
    if (selectedEmailAccount && selectedEmailAccount !== 'all') {
      const normalizedSelected = selectedEmailAccount.toLowerCase().trim();
      filtered = filtered.filter((email: Email) => {
        // Match by emailAccount field (normalized)
        const emailAccountMatch = email.emailAccount?.toLowerCase().trim() === normalizedSelected;
        
        // Match by 'to' field (for received emails)
        const toMatch = Array.isArray(email.to) 
          ? email.to.some((to: string) => to.toLowerCase().trim() === normalizedSelected)
          : (email.to?.toLowerCase().trim() === normalizedSelected);
        
        // Match by 'from' field (for sent emails), including "Name <addr>" shapes
        const fromProbe = collectSentFromAddressProbe(email);
        const fromMatch =
          email.from?.toLowerCase().trim() === normalizedSelected ||
          extractEmailAddresses(fromProbe).some((addr) => addr === normalizedSelected);
        
        // Match by accountId if available
        const account = emailAccounts.find(
          (acc) => acc.email.toLowerCase().trim() === normalizedSelected
        );
        const accountIdMatch = account && email.accountId === account.id;
        
        const matches = emailAccountMatch || toMatch || fromMatch || accountIdMatch;
        
        if (!matches) {
          console.log(`❌ Filtering out email - doesn't match selected account:`, {
            emailId: email.id,
            emailAccount: email.emailAccount,
            to: email.to,
            from: email.from,
            selectedEmailAccount: normalizedSelected
          });
        }
        
        return matches;
      });
      console.log(`🔍 After email account filter (${selectedEmailAccount}): ${filtered.length} emails`);
    } else if (!selectedEmailAccount && selectedInbox !== 'all') {
      // CRITICAL: If an account is selected but not found, show NO emails
      console.log(`⚠️ Account selected but not found (${selectedInbox}), showing 0 emails`);
      filtered = [];
    }

    // Domain + Purpose filters (applies to message list, especially useful in "All Inboxes")
    if (selectedDomainFilter !== 'all') {
      const wanted = String(selectedDomainFilter || '').toLowerCase().trim();
      filtered = filtered.filter((email: Email) => getEmailDomains(email).includes(wanted));
    }

    if (selectedPurposeFilter !== 'all') {
      const wantedType = selectedPurposeFilter;
      filtered = filtered.filter((email: Email) => {
        const domains = getEmailDomains(email);
        const mailboxDomain = email.emailAccount ? String(email.emailAccount).split('@')[1] : '';
        const d = String((mailboxDomain || domains[0] || '')).toLowerCase().trim();
        const t = domainTypeByDomain.get(d) || 'business';
        return t === wantedType;
      });
    }

    // Campaign filter (shown in "All Inboxes" filters UI)
    if (selectedCampaign) {
      const wanted = String(selectedCampaign || '').trim().toLowerCase();
      filtered = filtered.filter((email: Email) => {
        const e: any = email as any;
        const raw =
          e?.campaignName ??
          e?.campaign ??
          e?.campaignId ??
          e?.campaign_id ??
          e?.metadata?.campaign ??
          e?.metadata?.campaignName ??
          e?.headers?.['X-Campaign'] ??
          e?.headers?.['x-campaign'];
        const v = String(raw || '').trim().toLowerCase();
        // Fallback: match subject if no explicit campaign field exists
        const subj = String(email.subject || '').toLowerCase();
        return (v && v === wanted) || (!v && subj.includes(wanted));
      });
    }

    // CRITICAL DEBUG: Log emails before tab filtering
    console.log(`🔍 Filtering - Starting with ${filtered.length} emails, activeTab: ${activeTab}, selectedInbox: ${selectedInbox}, filterMode: ${filterMode}, selectedEmailAccount: ${selectedEmailAccount}`);

    // Phase A.2 (2026-05-15): strict warmup isolation. Once a row is
    // classified `emailKind === 'warmup'` it is visible ONLY through
    // the protected Warm Up system folder — never in Inbox, Sent,
    // Archive, Spam, Trash, All Inboxes, All Emails, custom user
    // folders, or Auto-Filter destinations. Decision matrix from
    // 2026-05-15 (user-confirmed): warmup classification wins over
    // every other surface (custom-folder placement and legacy Auto
    // Filter routing included). This single guard sits BEFORE the
    // activeTab branches so the rule applies uniformly without
    // touching each branch's filter callback. The Warm Up early-return
    // paths inside each activeTab branch (added Phase A.1) remain
    // unchanged because they are only reached when the Warm Up folder
    // is the selected folder. See SERVERLESS_ESBUILD docs and
    // WARMUP_SYSTEM_FOLDER_ARCHITECTURE_PROPOSAL §1.
    if (!isWarmupSystemFolderSelected) {
      const beforeWarmupExclusion = filtered.length;
      filtered = filtered.filter((email: Email) => {
        const kind = String((email as any).emailKind || '').trim().toLowerCase();
        return kind !== 'warmup';
      });
      const removed = beforeWarmupExclusion - filtered.length;
      if (removed > 0) {
        console.log(`🔥 Warmup isolation - Hidden ${removed} warmup row(s) from non-Warm-Up view (activeTab=${activeTab}, selectedFolder=${selectedFolder || 'null'})`);
      }
    }

    // Filter by tab FIRST (Inbox = received emails, Spam = system/spam emails, Sent = sent emails)
    // This ensures we filter by isSent before filtering by inbox, preventing received emails from showing in Sent folder
    // CRITICAL: Filter by tab BEFORE filtering by inbox to ensure correct folder separation
    if (activeTab === 'primary' || activeTab === 'inbox') {
      // Inbox: received emails that are NOT deleted and NOT archived
      // CRITICAL: Include SNS subscription confirmations and other important system emails in Inbox
      // Exclude deleted emails (they go to Trash) and archived emails (they go to Archive)
      filtered = filtered.filter((email: Email) => {
        // Phase A (2026-05-14) + A.1: protected `Warm Up` system folder
        // takes an EARLY-RETURN path BEFORE the standard inbox filter
        // kicks in. The standard branch below excludes legacy filed
        // rows whose `email.folder` does not normalize to a member of
        // `selectedFolderKeys`, which would hide warm-up rows whose
        // `folder` is absent or set to `Warm up: KrossKastChannels`
        // (legacy). Phase A.1 added a direction split: when on the
        // Inbox sub-tab the Warm Up folder shows ONLY inbound warmup
        // rows (`isSent !== true`); the Sent sub-tab shows outbound
        // warmup rows (handled in the `activeTab === 'sent'` branch
        // below). Spam + archived + deleted are always hidden so the
        // folder never surfaces mail the user explicitly moved out of
        // the inbox view. See WARMUP_SYSTEM_FOLDER_ARCHITECTURE_PROPOSAL
        // §1 for the full interaction model.
        if (isWarmupSystemFolderSelected) {
          if (email.isDeleted === true) return false;
          if (email.isArchived === true) return false;
          if (email.isSpam === true) return false;
          const kind = String((email as any).emailKind || '').trim().toLowerCase();
          if (kind !== 'warmup') return false;
          return email.isSent !== true;
        }
        const emailFolderKey = normalizeFolderLabel(String((email as any).folder || '').trim());
        // Must be a received email (not sent)
        if (email.isSent === true) return false;
        // "All Emails" virtual folder = all inbound regardless filing/spam/archive/delete flags.
        if (isAllEmailsVirtualFolderSelected) return true;
        // Quarantined folder behaves like a system folder and is keyed by flag.
        if (isQuarantinedFolderSelected) {
          return (email as any).isQuarantined === true || emailFolderKey === 'quarantined';
        }
        // Exclude deleted emails (they belong in Trash)
        if (email.isDeleted === true) return false;
        // Exclude archived emails (they belong in Archive)
        if (email.isArchived === true) return false;
        // Exclude spam (belongs in Spam)
        if (email.isSpam === true) return false;
        // Hide user-filed emails from the default Inbox (align with
        // hasExplicitNonInboxFolder in get-emails.ts): only rows whose folder is
        // explicitly something other than the canonical inbox label. Many stores
        // persist folder="Inbox" on normal mail — treating any truthy folder as
        // "filed" incorrectly hid every message when a single mailbox was selected.
        if (!selectedFolder && emailFolderKey && emailFolderKey !== 'inbox') return false;
        // When viewing a folder, only show emails that are in that folder
        if (selectedFolder) {
          const selectedFolderKeys = new Set(
            [selectedFolder, ...selectedFolderAliases].map((value) => normalizeFolderLabel(value)).filter(Boolean)
          );
          if (!selectedFolderKeys.has(emailFolderKey)) return false;
        }
        
        // CRITICAL: Include SNS subscription confirmations in Inbox (they're important!)
        // These need to be confirmed to complete bounce/complaint handling setup
        const subjectLower = (email.subject || '').toLowerCase();
        const fromLower = (email.from || '').toLowerCase();
        const isSNSSubscription = 
          subjectLower.includes('subscription confirmation') ||
          (subjectLower.includes('aws notification') && subjectLower.includes('subscription')) ||
          fromLower.includes('sns.amazonaws.com');
        
        // If it's an SNS subscription confirmation, always show in Inbox (even if marked as system email)
        if (isSNSSubscription) {
          return true;
        }
        
        return true;
      });
      console.log(`🔍 Inbox filter - Showing ${filtered.length} non-deleted, non-archived received emails`);
    } else if (activeTab === 'spam') {
      // Spam: ONLY show emails that are explicitly marked as spam (isSpam === true)
      // CRITICAL: Do NOT automatically categorize emails as spam!
      // Users must explicitly mark emails as spam
      filtered = filtered.filter((email: Email) => {
        // Exclude deleted emails (they belong in Trash)
        if (email.isDeleted === true) return false;
        // Exclude archived emails (they belong in Archive)
        if (email.isArchived === true) return false;
        // Phase A.1 (2026-05-14): on Warm Up system folder + Spam tab,
        // scope additionally to warmup classification (both directions).
        if (isWarmupSystemFolderSelected) {
          if (email.isSpam !== true) return false;
          const kind = String((email as any).emailKind || '').trim().toLowerCase();
          return kind === 'warmup';
        }
        // Only show emails explicitly marked as spam
        return email.isSpam === true;
      });
    } else if (activeTab === 'sent') {
      // Sent: only sent emails (isSent === true) FROM the selected account (sender)
      // CRITICAL: Only show emails where isSent is explicitly true AND from the selected account
      // Exclude deleted and archived emails from Sent folder
      filtered = filtered.filter((email: Email) => {
        // Phase A.1 (2026-05-14): protected `Warm Up` system folder +
        // Sent sub-tab. The folder scopes to `emailKind === 'warmup'`,
        // and the Sent tab refines to outbound rows. Bypasses the
        // mailbox/sender match further down because the Warm Up view
        // is a tenant-wide classification surface, not a single-mailbox
        // sent ledger. Mirrors the Inbox-side early-return above.
        if (isWarmupSystemFolderSelected) {
          if (email.isDeleted === true) return false;
          if (email.isArchived === true) return false;
          if (email.isSpam === true) return false;
          const kind = String((email as any).emailKind || '').trim().toLowerCase();
          if (kind !== 'warmup') return false;
          return email.isSent === true;
        }
        // Must be a sent email (explicitly true, not undefined/null)
        if (email.isSent !== true) return false;
        // Exclude deleted emails (they belong in Trash)
        if (email.isDeleted === true) return false;
        // Exclude archived emails (they belong in Archive)
        if (email.isArchived === true) return false;
        
        // If an inbox is selected, show sent emails FROM that account (sender)
        // CRITICAL: For sent emails, emailAccount is the SENDER, not the recipient
        if (filterMode === 'inboxes' && selectedInbox !== 'all') {
          // Match by account ID, email address, or emailAccount
          const normalizedInboxKey = String(selectedInbox || '').toLowerCase().trim();
          const selectedAccount = emailAccountsWithUnread.find(
            (acc) =>
              acc.id === selectedInbox ||
              String(acc.email || '').toLowerCase().trim() === normalizedInboxKey
          );
          
          if (selectedAccount) {
            // CRITICAL: Only match if email is FROM the selected account (sender)
            // emailAccount for sent emails is the sender
            // from field is also the sender
            // Do NOT match on 'to' field - that would show received emails!
            // CRITICAL: Normalize emails to lowercase for case-insensitive matching
            const normalizedEmailAccount = (email.emailAccount || '').toLowerCase().trim();
            const normalizedSelectedEmail = (selectedAccount.email || '').toLowerCase().trim();
            const fromHeaderEmails = extractEmailAddresses(collectSentFromAddressProbe(email));
            const fromHeaderMatches = fromHeaderEmails.some((addr) => addr === normalizedSelectedEmail);

            return (
              normalizedEmailAccount === normalizedSelectedEmail
              || email.accountId === selectedAccount.id
              || fromHeaderMatches
            );
          }
          
          // Fallback: direct match on emailAccount or from (both should be sender)
          // CRITICAL: Normalize emails to lowercase for case-insensitive matching
          const normalizedEmailAccount = (email.emailAccount || '').toLowerCase().trim();
          const normalizedFrom = (email.from || '').toLowerCase().trim();
          const normalizedSelectedInbox = (selectedInbox || '').toLowerCase().trim();
          const fromHeaderEmails = extractEmailAddresses(collectSentFromAddressProbe(email));

          return (
            normalizedEmailAccount === normalizedSelectedInbox
            || fromHeaderEmails.some((addr) => addr === normalizedSelectedInbox)
            || normalizedFrom === normalizedSelectedInbox
          );
        }
        return true; // Show all sent emails if no specific inbox selected
      });
    } else if (activeTab === 'archive') {
      // Archive: emails marked as archived (isArchived === true)
      // Archived emails are removed from inbox but kept for reference
      filtered = filtered.filter((email: Email) => {
        // Phase A.1 (2026-05-14): on the Warm Up system folder + Archive
        // sub-tab, scope additionally to warmup classification so the
        // tab honors the system-folder population. Direction is not
        // split here — both inbound and outbound archived warmup rows
        // surface together (the Archive intent is "things I moved out
        // of view", not directional triage).
        if (isWarmupSystemFolderSelected) {
          if (email.isArchived !== true) return false;
          const kind = String((email as any).emailKind || '').trim().toLowerCase();
          return kind === 'warmup';
        }
        return email.isArchived === true;
      });
      console.log(`📦 Archive tab filter - Showing ${filtered.length} archived emails`);
    } else if (activeTab === 'trash') {
      // Trash: emails marked as deleted (isDeleted === true)
      // Deleted emails go to trash and can be permanently deleted later
      // CRITICAL: Handle both boolean and DynamoDB format {BOOL: true}
      filtered = filtered.filter((email: Email) => {
        const emailDeleted = email.isDeleted as any;
        const isDeleted = emailDeleted?.BOOL !== undefined 
          ? emailDeleted.BOOL 
          : (emailDeleted === true);
        // Phase A.1: same shape as Archive — on Warm Up + Trash, scope
        // additionally to warmup classification (both directions).
        if (isWarmupSystemFolderSelected) {
          if (isDeleted !== true) return false;
          const kind = String((email as any).emailKind || '').trim().toLowerCase();
          return kind === 'warmup';
        }
        console.log(`🗑️ Trash filter - Email ${email.id}: isDeleted=${emailDeleted}, normalized=${isDeleted}`);
        return isDeleted === true;
      });
      console.log(`🗑️ Trash tab filter - Showing ${filtered.length} deleted emails`);
    } else if (activeTab === 'quarantined') {
      // Quarantined: inbound emails rejected (recipient not in KG). API returns only quarantined when folder=quarantined.
      filtered = filtered.filter((email: Email) => (email as any).isQuarantined === true);
      console.log(`🧯 Quarantined tab filter - Showing ${filtered.length} quarantined emails`);
    }

    // TEMPORARY: Disable selectedInbox filter - show ALL emails regardless of selected inbox
    // This ensures verification emails and all other emails are visible
    if (filterMode === 'inboxes' && selectedInbox !== 'all') {
      console.log(`🔍 selectedInbox filter - DISABLED (showing all ${filtered.length} emails regardless of selected inbox)`);
      // Don't filter by selected inbox - show all emails
      // filtered = filtered; // Already filtered, just pass through
      
      console.log(`🔍 After selectedInbox filter: ${filtered.length} emails`);
    }

    // Filter by filter mode
    if (filterMode === 'status') {
      // Filter by selected status if one is selected
      if (selectedStatus) {
        console.log(`🔍 Status filter - Filtering for status: "${selectedStatus}"`);
        console.log(`🔍 Status filter - Total emails before filter: ${filtered.length}`);
        filtered = filtered.filter((email: Email) => {
          const emailStatus = email.status || statusFallbackLabel;
          const matches = emailStatus === selectedStatus;
          if (matches) {
            console.log(`✅ Status filter - Email ${email.id} matches (status: "${emailStatus}")`);
          }
          return matches;
        });
        console.log(`🔍 Status filter - Total emails after filter: ${filtered.length}`);
      } else {
        console.log('🔍 Status filter - No status selected, showing all emails');
      }
      // Sort by read/unread status (unread first, then read)
      filtered = [...filtered].sort((a, b) => {
        if (!a.isRead && b.isRead) return -1;
        if (a.isRead && !b.isRead) return 1;
        return 0;
      });
    }

    // Search is performed server-side (`search` query param) so it can scan the full
    // inbox corpus; avoid re-filtering client-side, which can hide valid server matches.

    // Filter by read/unread (quick action toggles; only one active at a time)
    if (showUnreadOnly) {
      filtered = filtered.filter((email: Email) => !email.isRead);
    } else if (showReadOnly) {
      filtered = filtered.filter((email: Email) => email.isRead);
    }

    // CRITICAL: Sort by date/time (newest first)
    // This ensures emails are always displayed in chronological order
    filtered = [...filtered].sort((a, b) => {
      const dateA = new Date(a.date || 0).getTime();
      const dateB = new Date(b.date || 0).getTime();
      return dateB - dateA; // Newest first (descending order)
    });

    return filtered;
  }, [emails, selectedInbox, normalizedEmailSearchTerm, activeTab, filterMode, selectedStatus, showUnreadOnly, showReadOnly, selectedAccount?.id, isAllAccountsSelected, selectedEmailAccount, emailAccounts, selectedDomainFilter, selectedPurposeFilter, selectedCampaign, domainTypeByDomain, selectedFolder, selectedFolderAliases, isAllEmailsVirtualFolderSelected, isQuarantinedFolderSelected, isWarmupSystemFolderSelected, statusFallbackLabel]);

  const visibleEmails = useMemo((): Email[] => {
    const seen = new Set<string>();
    return (filteredEmails || []).filter((email: Email) => {
      const id = email?.id;
      if (!id) return false;
      if (seen.has(id)) return false;
      if (optimisticRemovedIds.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [filteredEmails, optimisticRemovedIds]);
  const emailTotalPages = useMemo(() => {
    const total = Math.max(0, Number(emailFolderTotal || 0));
    return total > 0 ? Math.max(1, Math.ceil(total / Math.max(1, emailLimit))) : 0;
  }, [emailFolderTotal, emailLimit]);
  const visibleEmailCountLabel = useMemo(() => {
    if (hasPendingEmailSearchInput) {
      // Typing is faster than the debounce window. Tell the user we're about
      // to refetch so heavy folders (Sent) don't look frozen between keystrokes.
      return 'Searching\u2026';
    }
    if (hasActiveEmailSearch) {
      const count = visibleEmails.length;
      const base = `${wholeNumberFormatter.format(count)} result${count === 1 ? '' : 's'}`;
      // Backend ran out of runtime/page budget before fully scanning the
      // folder (common on Sent at 200k+). Tell the user the result set is
      // depth-bounded so they don't read it as authoritative.
      return emailSearchPartial ? `${base} (partial)` : base;
    }
    const currentPage = emailTotalPages === 0 ? 0 : Math.min(Math.max(1, emailCurrentPage), emailTotalPages);
    return `Page ${wholeNumberFormatter.format(currentPage)} of ${wholeNumberFormatter.format(emailTotalPages)}`;
  }, [hasActiveEmailSearch, hasPendingEmailSearchInput, emailSearchPartial, visibleEmails.length, emailTotalPages, emailCurrentPage]);
  const canNavigatePreviousEmailPage = !hasActiveEmailSearch && emailPageCursorHistory.length > 0;
  const canNavigateNextEmailPage = useMemo(() => {
    if (hasActiveEmailSearch) return false;
    if (emailNextPageCursor && emailNextPageCursor !== emailPageCursor) return true;
    if (!emailFallbackNextPageCursor || emailFallbackNextPageCursor === emailPageCursor) return false;
    return emailCurrentPage < emailTotalPages;
  }, [hasActiveEmailSearch, emailNextPageCursor, emailFallbackNextPageCursor, emailPageCursor, emailCurrentPage, emailTotalPages]);
  const totalEmailCountLabel = useMemo(() => {
    if (isLoadingEmails && emailFolderTotal === 0) return '...';
    const total = Math.max(0, Number(emailFolderTotal || 0));
    return `${wholeNumberFormatter.format(total)} email${total === 1 ? '' : 's'}`;
  }, [emailFolderTotal, isLoadingEmails]);
  const sentStatsParsed = useMemo(() => {
    const sent = (sentStatsData as any)?.data?.sent || (sentStatsData as any)?.sent || {};
    const todayBreakdownRaw = (sentStatsData as any)?.data?.todayBreakdown || (sentStatsData as any)?.todayBreakdown || {};
    return {
      today: sent.today,
      week: sent.week,
      month: sent.month,
      todayAccounts: Array.isArray(todayBreakdownRaw.accounts) ? todayBreakdownRaw.accounts as { email: string; count: number }[] : [],
    };
  }, [sentStatsData]);
  const sentStatsLabel = useMemo(() => {
    if (isLoadingSentStats || isFetchingSentStats) {
      return 'Today: ...  This Week: ...  This Month: ...';
    }
    if (sentStatsError) {
      return 'Sent stats unavailable';
    }
    return `Today: ${formatSentStatsCount(sentStatsParsed.today)}  This Week: ${formatSentStatsCount(sentStatsParsed.week)}  This Month: ${formatSentStatsCount(sentStatsParsed.month)}`;
  }, [isLoadingSentStats, isFetchingSentStats, sentStatsError, sentStatsParsed]);

  // Apply auto-filters to inbound emails (best-effort, rate-limited)
  useEffect(() => {
    if (!Array.isArray(autoFilters) || autoFilters.length === 0) return;
    if (!Array.isArray(filteredEmails) || filteredEmails.length === 0) return;
    if (!selectedEmailAccount) return; // avoid firing while in "no account selected" state

    const rules = autoFilters.filter((r) => r.enabled && r.pattern && r.folder);
    if (rules.length === 0) return;

    const safeContains = (haystack: string, needle: string) =>
      haystack.toLowerCase().includes(needle.toLowerCase());

    const compileRegex = (pattern: string) => {
      try {
        return new RegExp(pattern, 'i');
      } catch (e) {
        return null;
      }
    };

    let processedThisRun = 0;
    const MAX_PER_RUN = 10;

    const run = async () => {
      for (const email of filteredEmails) {
        if (processedThisRun >= MAX_PER_RUN) break;
        if (!email?.id) continue;
        if (autoFilterProcessedIds.has(email.id)) continue;

        // Only auto-file inbound inbox items that are currently unfiled
        if (email.isSent) continue;
        if (email.isDeleted || email.isArchived || email.isSpam) continue;
        if (email.folder && String(email.folder).trim()) continue;

        const subject = String(email.subject || '');
        const body =
          String(email.text || '') +
          '\n' +
          String((email as any)?.preview || '') +
          '\n' +
          String((email as any)?.html || '');

        for (const rule of rules) {
          const haystack =
            rule.field === 'subject'
              ? subject
              : rule.field === 'body'
              ? body
              : `${subject}\n${body}`;

          let matched = false;
          if (rule.matchType === 'contains') {
            matched = safeContains(haystack, rule.pattern);
          } else {
            const re = compileRegex(rule.pattern);
            matched = Boolean(re && re.test(haystack));
          }

          if (matched) {
            autoFilterProcessedIds.add(email.id);
            processedThisRun += 1;
            try {
              // Let buildUpdatePayload derive emailAccount from the email; avoids passing "all" in All-Inboxes view.
              await updateEmail(
                buildUpdatePayload({ id: email.id, folder: rule.folder, email, source: 'auto_filter_rule' })
              ).unwrap();
            } catch (e) {
              console.warn('Auto-filter move failed:', e);
            }
            break; // first match wins
          }
        }
      }
    };

    run();
  }, [autoFilters, filteredEmails, selectedEmailAccount, normalizedEmailAccount, updateEmail, autoFilterProcessedIds]);

  // Filter inboxes by search term
  const filteredInboxes = useMemo((): EmailAccount[] => {
    let out = emailAccountsWithUnread;
    if (searchTerm) {
      out = out.filter((account: EmailAccount) =>
        account.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.domain.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (selectedDomainFilter !== 'all') {
      const wanted = String(selectedDomainFilter || '').toLowerCase().trim();
      out = out.filter((a) => String(a.domain || '').toLowerCase().trim() === wanted);
    }
    if (selectedPurposeFilter !== 'all') {
      out = out.filter((a) => {
        const d = String(a.domain || '').toLowerCase().trim();
        const t = domainTypeByDomain.get(d) || 'business';
        return t === selectedPurposeFilter;
      });
    }
    return [...out].sort((a: EmailAccount, b: EmailAccount) =>
      String(a.email || '').toLowerCase().localeCompare(String(b.email || '').toLowerCase())
    );
  }, [emailAccountsWithUnread, searchTerm, selectedDomainFilter, selectedPurposeFilter, domainTypeByDomain]);

  // CRITICAL: Clear selected emails when switching tabs, inboxes, or when filtered emails change
  // This prevents stale "1 selected" state when switching views
  useEffect(() => {
    // Clear selections when switching tabs
    setSelectedEmails(new Set());
    console.log('🧹 Cleared selectedEmails due to tab/inbox/filter change');
  }, [activeTab, selectedInbox, filteredEmails.length]);

  const handleEmailSelect = async (emailId: string, event?: React.MouseEvent | React.ChangeEvent): Promise<void> => {
    // If clicking on checkbox, just toggle selection
    if (event?.target instanceof HTMLElement && (event.target.tagName === 'INPUT' || event.target.closest('input'))) {
      const newSelected = new Set(selectedEmails);
      if (newSelected.has(emailId)) {
        newSelected.delete(emailId);
      } else {
        newSelected.add(emailId);
      }
      setSelectedEmails(newSelected);
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      return;
    }
    
    // Otherwise, open email for viewing
    if (!selectedEmails.has(emailId)) {
      setSelectedEmails(new Set([emailId]));
    }
    const email = filteredEmails.find(e => e.id === emailId);
    if (email) {
      // CRITICAL: Optimistically update read status immediately for instant UI feedback
      if (!email.isRead) {
        setOptimisticReadStatus(prev => new Map(prev).set(emailId, true));
        setSelectedEmailToView({ ...email, isRead: true });
      } else {
        setSelectedEmailToView(email);
      }
      
      // Mark as read when opened (API call in background)
      if (!email.isRead) {
        try {
          // CRITICAL: Use emailAccount (recipient) not from (sender)
          const emailAccount = email.emailAccount || (Array.isArray(email.to) ? email.to[0] : email.to) || email.from;
          
          console.log(`🔄 Auto-marking email as read:`, {
            emailId: email.id,
            emailIdType: typeof email.id,
            emailIdLength: email.id?.length,
            emailAccount,
            from: email.from,
            to: email.to,
            emailAccountField: email.emailAccount,
            fullEmailObject: email
          });
          
          await updateEmail(buildUpdatePayload({
            emailId: email.id,
            emailAccount: emailAccount,
            updates: {
              isRead: true
            },
            email
          })).unwrap();
          
          // API call succeeded - refetch to sync with server
          await loadEmails();
          
          // CRITICAL: Don't clear optimistic update - let it persist
          // The optimistic update will naturally be overridden when server value becomes true
          // If server value is still false, optimistic update keeps the UI correct
          // Only clear when we verify server has isRead: true
          // For now, keep it indefinitely to prevent flickering
        } catch (error: any) {
          console.error('❌ Error marking email as read:', error);
          console.error('❌ Error details:', {
            message: error.message,
            data: error.data,
            status: error.status,
            emailId: email.id,
            emailIdType: typeof email.id,
            emailIdLength: email.id?.length,
            emailAccount: email.emailAccount || (Array.isArray(email.to) ? email.to[0] : email.to) || email.from,
            from: email.from,
            to: email.to,
            fullEmailObject: JSON.stringify(email, null, 2)
          });
          console.error('❌ API Request that failed:', {
            emailId: email.id,
            emailAccount: email.emailAccount || (Array.isArray(email.to) ? email.to[0] : email.to) || email.from,
            updates: { isRead: true }
          });
          
          // Show user-friendly error message
          alert(`Failed to mark email as read. The change will not persist after refresh.\n\nError: ${error.data?.message || error.message || 'Unknown error'}\n\nPlease check the console for details.`);
          
          // API call failed - but keep the optimistic update since user clicked on the email
          // The user's intent is clear: they want to mark it as read
          // We'll keep the optimistic state and retry in the background
          console.warn('⚠️ Keeping optimistic read status despite API error - user clicked on email');
          
          // Retry the API call after a delay
          setTimeout(async () => {
            try {
              const retryEmailAccount = email.emailAccount || (Array.isArray(email.to) ? email.to[0] : email.to) || email.from;
              console.log('Retrying mark as read for', email.id);
              await updateEmail(buildUpdatePayload({
                emailId: email.id,
                emailAccount: retryEmailAccount,
                updates: { isRead: true },
                email
              })).unwrap();
              console.log(`✅ Retry succeeded for ${email.id}`);
              await loadEmails();
              // Clear optimistic update after successful retry
              setOptimisticReadStatus(prev => {
                const newMap = new Map(prev);
                newMap.delete(emailId);
                return newMap;
              });
            } catch (retryError: any) {
              console.error('❌ Retry also failed, keeping optimistic state:', retryError);
              console.error('❌ Retry error details:', {
                message: retryError.message,
                data: retryError.data,
                status: retryError.status
              });
            }
          }, 2000); // Retry after 2 seconds
        }
      }
    }
  };

  // Separate handler for checkbox changes
  const handleCheckboxChange = (emailId: string, event: React.ChangeEvent<HTMLInputElement>): void => {
    event.stopPropagation();
    // Don't prevent default - let the checkbox update naturally
    const newSelected = new Set(selectedEmails);
    const isChecked = event.target.checked;
    
    if (isChecked) {
      newSelected.add(emailId);
      console.log('✅ Checkbox checked, email added to selection:', emailId, 'Total selected:', newSelected.size);
    } else {
      newSelected.delete(emailId);
      console.log('❌ Checkbox unchecked, email removed from selection:', emailId, 'Total selected:', newSelected.size);
    }
    setSelectedEmails(newSelected);
  };

  const handleOpenEmail = async (email: Email): Promise<void> => {
    // CRITICAL: Optimistically update read status immediately for instant UI feedback
    if (!email.isRead) {
      setOptimisticReadStatus(prev => new Map(prev).set(email.id, true));
      setSelectedEmailToView({ ...email, isRead: true });
    } else {
      setSelectedEmailToView(email);
    }
    
    // Mark as read when opened (API call in background)
    if (!email.isRead) {
      try {
        // CRITICAL: Use emailAccount (recipient) not from (sender)
        const emailAccount = email.emailAccount || (Array.isArray(email.to) ? email.to[0] : email.to) || email.from;
        
        console.log(`🔄 Auto-marking email as read (handleOpenEmail):`, {
          emailId: email.id,
          emailAccount,
          from: email.from,
          to: email.to,
          emailAccountField: email.emailAccount
        });
        
        await updateEmail(buildUpdatePayload({
          emailId: email.id,
          emailAccount: emailAccount,
          updates: {
            isRead: true
          },
          email
        })).unwrap();
        
        // API call succeeded - refetch to sync with server
        await loadEmails();
        
        // CRITICAL: Don't clear optimistic update - let it persist
        // The optimistic update will naturally be overridden when server value becomes true
        // If server value is still false, optimistic update keeps the UI correct
        // Only clear when we verify server has isRead: true
        // For now, keep it indefinitely to prevent flickering
      } catch (error: any) {
        console.error('❌ Error marking email as read:', error);
        console.error('❌ Error details:', {
          message: error.message,
          data: error.data,
          status: error.status,
          emailId: email.id,
          emailIdType: typeof email.id,
          emailIdLength: email.id?.length,
          emailAccount: email.emailAccount || (Array.isArray(email.to) ? email.to[0] : email.to) || email.from,
          from: email.from,
          to: email.to,
          fullEmailObject: JSON.stringify(email, null, 2)
        });
        console.error('❌ API Request that failed:', {
          emailId: email.id,
          emailAccount: email.emailAccount || (Array.isArray(email.to) ? email.to[0] : email.to) || email.from,
          updates: { isRead: true }
        });
        
        // Show user-friendly error message
        alert(`Failed to mark email as read. The change will not persist after refresh.\n\nError: ${error.data?.message || error.message || 'Unknown error'}\n\nPlease check the console for details.`);
        
        // API call failed - but keep the optimistic update since user clicked on the email
        // The user's intent is clear: they want to mark it as read
        // We'll keep the optimistic state and retry in the background
        console.warn('⚠️ Keeping optimistic read status despite API error - user clicked on email');
        
        // Retry the API call after a delay
        setTimeout(async () => {
          try {
            const emailAccount = email.emailAccount || (Array.isArray(email.to) ? email.to[0] : email.to) || email.from;
            console.log(`🔄 Retrying mark as read for ${email.id}...`);
            await updateEmail(buildUpdatePayload({
              emailId: email.id,
              emailAccount: emailAccount,
              updates: { isRead: true },
              email
            })).unwrap();
            console.log(`✅ Retry succeeded for ${email.id}`);
            await loadEmails();
            // Clear optimistic update after successful retry
            setOptimisticReadStatus(prev => {
              const newMap = new Map(prev);
              newMap.delete(email.id);
              return newMap;
            });
          } catch (retryError: any) {
            console.error('❌ Retry also failed, keeping optimistic state:', retryError);
            console.error('❌ Retry error details:', {
              message: retryError.message,
              data: retryError.data,
              status: retryError.status
            });
          }
        }, 2000); // Retry after 2 seconds
      }
    }
  };

  const handleSelectAll = (): void => {
    if (selectedEmails.size === visibleEmails.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(visibleEmails.map(email => email.id)));
    }
  };

  // Compose modal handlers
  const handleComposeClick = (): void => {
    setShowComposeModal(true);
    setComposeAttachments([]);
    setComposeConfidentialMode(false);
    setComposeMode('new');
    // Prefill From with a value that actually exists in the dropdown options.
    // composeFromOptions is the source of truth for the From <select> and is
    // already filtered to accounts whose email contains '@'. Picking anything
    // else (or leaving fromAccount empty) leaves the controlled <select> with
    // a state value that doesn't match any option — the browser visually shows
    // the first option but composeData.fromAccount stays stale, and the send
    // validator then rejects what looks to the user like a valid From.
    const inboxEmail =
      selectedEmailAccount && selectedEmailAccount !== 'all'
        ? selectedEmailAccount
        : null;
    const matchedSelected = inboxEmail
      ? composeFromOptions.find((a: EmailAccount) => a.email === inboxEmail)?.email
      : undefined;
    const defaultFrom = matchedSelected || composeFromOptions[0]?.email || '';
    setComposeData(prev => ({
      ...prev,
      fromAccount: defaultFrom,
    }));
  };

  const handleComposeClose = (): void => {
    setShowComposeModal(false);
    setComposeAttachments([]);
    setComposeConfidentialMode(false);
    setComposeMode('new');
    if (composeAttachmentInputRef.current) composeAttachmentInputRef.current.value = '';
    setComposeData({
      to: '',
      cc: '',
      bcc: '',
      subject: '',
      body: '',
      fromAccount: ''
    });
  };

  // Self-heal compose From while the modal is open: if composeFromOptions resolves
  // after the modal opened, or fromAccount falls out of sync with what's actually
  // selectable in the <select>, snap fromAccount back to a valid option. Without
  // this, a controlled <select> whose value doesn't match any <option> visually
  // shows the first option but keeps composeData.fromAccount stale, so the send
  // validator rejects the (apparently selected) From.
  useEffect(() => {
    if (!showComposeModal) return;
    if (composeFromOptions.length === 0) return;
    setComposeData(prev => {
      const current = prev.fromAccount;
      const matches = !!current && composeFromOptions.some((a: EmailAccount) => a.email === current);
      if (matches) return prev;
      return { ...prev, fromAccount: composeFromOptions[0].email };
    });
  }, [showComposeModal, composeFromOptions]);

  // Test internal send: open compose with From = current account, To = another internal account
  const handleTestInternalSend = (): void => {
    const current = selectedEmailAccount || emailAccounts[0]?.email || emailAccounts[0]?.id;
    const other = emailAccounts.find(
      (a: EmailAccount) => (a.email || a.id || '').toLowerCase() !== (current || '').toLowerCase()
    );
    setComposeAttachments([]);
    setComposeConfidentialMode(false);
    setComposeMode('new');
    setComposeData({
      fromAccount: current || '',
      to: other?.email || other?.id || '',
      cc: '',
      bcc: '',
      subject: 'Internal test',
      body: 'Sent via Unibox internal mail delivery test.'
    });
    setShowComposeModal(true);
  };

  // Normalize address to email only (strip "Display Name <email>" -> email, lowercase)
  const toEmailOnly = (addr: string): string => {
    const s = String(addr || '').trim();
    const angle = /<([^>]+)>/.exec(s);
    const email = angle ? angle[1].trim() : s;
    return email.toLowerCase();
  };

  // Resolve which account is "replying" (the one that received this email). Prefer original To field so Reply From is correct.
  const getReplyFromAccount = (email: Email): EmailAccount | undefined => {
    const toList = Array.isArray(email.to) ? email.to : (email.to ? [String(email.to)] : []);
    const toSet = new Set(toList.map((t: string) => toEmailOnly(t)).filter(Boolean));
    // 1) Account that received the email = one of our accounts in the original To field
    const fromTo = emailAccounts.find((acc: EmailAccount) => {
      const ae = (acc.email || '').toLowerCase().trim();
      return ae && toSet.has(ae);
    });
    if (fromTo) return fromTo;
    // 2) Match by emailAccount/accountId (case-insensitive)
    const fromEmailAccount = emailAccountsWithUnread.find(
      (acc: EmailAccount) =>
        (email.emailAccount && (acc.email || '').toLowerCase() === (email.emailAccount || '').toLowerCase()) ||
        acc.id === email.accountId
    );
    if (fromEmailAccount) return fromEmailAccount;
    // 3) Current selected inbox
    if (selectedEmailAccount && selectedInbox !== 'all') {
      const sel = emailAccounts.find((acc: EmailAccount) => acc.email === selectedEmailAccount || acc.id === selectedInbox);
      if (sel) return sel;
    }
    return emailAccountsWithUnread[0];
  };

  // Reply, Reply All, and Forward handlers
  //
  // Direction-aware recipient resolution:
  //   - Inbox reply: To = original sender (existing behavior).
  //   - Sent reply:  To = original recipients (the people we sent to), excluding
  //                  our own sending account if it appears in the To list.
  // For Reply All from Sent we additionally include the original CC list (still
  // excluding our own sending account and the duplicate of the original sender,
  // which in a sent message is us). The Reply / Reply All buttons themselves
  // are no longer disabled when viewing Sent items (see the Action Buttons
  // block below); the disabled-when-isSent guard was the original block.
  const handleReply = (email: Email): void => {
    if (!email) return;
    const replyFromAccount = getReplyFromAccount(email);
    const replyFromEmail = replyFromAccount?.email || replyFromAccount?.id || '';
    const isReplyingFromSent = !!email.isSent;
    const sender = email.from || email.sender || '';
    const originalToList = Array.isArray(email.to)
      ? email.to.map((x: string) => String(x || '').trim()).filter(Boolean)
      : (email.to ? [String(email.to).trim()].filter(Boolean) : []);
    const myAccountLower = (replyFromEmail || '').toLowerCase();
    const replyToValue = isReplyingFromSent
      ? originalToList.filter((addr) => addr.toLowerCase() !== myAccountLower).join(', ')
      : sender;
    const originalSubject = email.subject || '';
    const subject = originalSubject.startsWith('Re: ') ? originalSubject : `Re: ${originalSubject}`;
    const originalDate = formatEmailDateTime(email.date, true) || toLocaleStringInTimezone(email.date, 'en-US', {}, tenantTimezone);
    const originalBody = email.text || email.html?.replace(/<[^>]*>/g, '') || email.preview || '';
    const originalToHeader = originalToList.join(', ');
    const quotedBody = isReplyingFromSent
      ? `\n\n--- Original Message ---\nFrom: ${sender}\nTo: ${originalToHeader}\nDate: ${originalDate}\nSubject: ${originalSubject}\n\n${originalBody}`
      : `\n\n--- Original Message ---\nFrom: ${sender}\nDate: ${originalDate}\nSubject: ${originalSubject}\n\n${originalBody}`;
    setComposeAttachments([]);
    setComposeConfidentialMode(false);
    setComposeMode('reply');
    setComposeData({
      to: replyToValue,
      cc: '',
      bcc: '',
      subject: subject,
      body: quotedBody,
      fromAccount: replyFromEmail,
    });
    setShowComposeModal(true);
  };

  const handleReplyAll = (email: Email): void => {
    if (!email) return;
    const replyFromAccount = getReplyFromAccount(email);
    const replyFromEmail = replyFromAccount?.email || replyFromAccount?.id || '';
    const isReplyingFromSent = !!email.isSent;
    const sender = email.from || email.sender || '';
    const originalSubject = email.subject || '';
    const subject = originalSubject.startsWith('Re: ') ? originalSubject : `Re: ${originalSubject}`;
    const originalToList = Array.isArray(email.to)
      ? email.to.map((x: string) => String(x || '').trim()).filter(Boolean)
      : (email.to ? [String(email.to).trim()].filter(Boolean) : []);
    const myAccountLower = (replyFromEmail || '').toLowerCase();
    const senderLower = (sender || '').toLowerCase();
    const toRecipients: string[] = isReplyingFromSent
      ? originalToList.filter((addr) => addr.toLowerCase() !== myAccountLower)
      : (sender ? [sender] : []);
    const ccRecipients: string[] = [];
    if (email.cc) {
      const ccArray = Array.isArray(email.cc) ? email.cc : [email.cc];
      ccRecipients.push(...ccArray.filter((addr: string) => {
        const a = (addr || '').trim();
        if (!a) return false;
        const aLower = a.toLowerCase();
        return aLower !== senderLower && aLower !== myAccountLower;
      }));
    }
    const originalDate = formatEmailDateTime(email.date, true) || toLocaleStringInTimezone(email.date, 'en-US', {}, tenantTimezone);
    const originalBody = email.text || email.html?.replace(/<[^>]*>/g, '') || email.preview || '';
    const originalToHeader = originalToList.join(', ');
    const quotedBody = isReplyingFromSent
      ? `\n\n--- Original Message ---\nFrom: ${sender}\nTo: ${originalToHeader}\nDate: ${originalDate}\nSubject: ${originalSubject}\n\n${originalBody}`
      : `\n\n--- Original Message ---\nFrom: ${sender}\nDate: ${originalDate}\nSubject: ${originalSubject}\n\n${originalBody}`;
    setComposeAttachments([]);
    setComposeConfidentialMode(false);
    setComposeMode('reply-all');
    setComposeData({
      to: toRecipients.join(', '),
      cc: ccRecipients.join(', '),
      bcc: '',
      subject: subject,
      body: quotedBody,
      fromAccount: replyFromEmail,
    });
    setShowComposeModal(true);
  };

  const handleForward = (email: Email): void => {
    if (!email) return;
    const replyFromAccount = getReplyFromAccount(email);
    const originalSubject = email.subject || '';
    const subject = originalSubject.startsWith('Fwd: ') ? originalSubject : `Fwd: ${originalSubject}`;
    // Forward original email in body
    const originalDate = formatEmailDateTime(email.date, true) || toLocaleStringInTimezone(email.date, 'en-US', {}, tenantTimezone);
    const originalFrom = email.from || email.sender || 'Unknown';
    const uniqTo = Array.isArray(email.to)
      ? Array.from(new Set(email.to.map((x: string) => String(x || '').toLowerCase().trim()).filter(Boolean)))
      : [];
    const originalTo = Array.isArray(email.to) ? uniqTo.join(', ') : email.to || '';
    const originalBody = email.text || email.html?.replace(/<[^>]*>/g, '') || email.preview || '';
    const forwardedBody = `\n\n--- Forwarded Message ---\nFrom: ${originalFrom}\nTo: ${originalTo}\nDate: ${originalDate}\nSubject: ${email.subject || '(No Subject)'}\n\n${originalBody}`;
    
    setComposeAttachments([]);
    setComposeConfidentialMode(false);
    setComposeMode('forward');
    setComposeData({
      to: '',
      cc: '',
      bcc: '',
      subject: subject,
      body: forwardedBody,
      fromAccount: replyFromAccount?.email || replyFromAccount?.id || ''
    });
    
    // Open compose modal
    setShowComposeModal(true);
  };

  const updateComposeBodySelection = (
    nextBody: string,
    selectionStart: number,
    selectionEnd: number = selectionStart
  ): void => {
    handleComposeChange('body', nextBody);
    window.requestAnimationFrame(() => {
      const bodyInput = composeBodyRef.current as HTMLTextAreaElement | null;
      if (!bodyInput) return;
      bodyInput.focus();
      bodyInput.setSelectionRange(selectionStart, selectionEnd);
    });
  };

  const wrapComposeSelection = (prefix: string, suffix: string, placeholder: string): void => {
    const bodyInput = composeBodyRef.current as HTMLTextAreaElement | null;
    const currentBody = String(composeData.body || '');
    if (!bodyInput) {
      handleComposeChange('body', `${currentBody}${prefix}${placeholder}${suffix}`);
      return;
    }

    const selectionStart = bodyInput.selectionStart ?? currentBody.length;
    const selectionEnd = bodyInput.selectionEnd ?? selectionStart;
    const selectedText = currentBody.slice(selectionStart, selectionEnd);
    const textToWrap = selectedText || placeholder;
    const replacement = `${prefix}${textToWrap}${suffix}`;
    const nextBody = `${currentBody.slice(0, selectionStart)}${replacement}${currentBody.slice(selectionEnd)}`;
    const nextSelectionStart = selectedText ? selectionStart + replacement.length : selectionStart + prefix.length;
    const nextSelectionEnd = selectedText ? nextSelectionStart : nextSelectionStart + textToWrap.length;
    updateComposeBodySelection(nextBody, nextSelectionStart, nextSelectionEnd);
  };

  const insertComposeTextAtCursor = (insertText: string): void => {
    const bodyInput = composeBodyRef.current as HTMLTextAreaElement | null;
    const currentBody = String(composeData.body || '');
    if (!bodyInput) {
      handleComposeChange('body', `${currentBody}${insertText}`);
      return;
    }
    const selectionStart = bodyInput.selectionStart ?? currentBody.length;
    const selectionEnd = bodyInput.selectionEnd ?? selectionStart;
    const nextBody = `${currentBody.slice(0, selectionStart)}${insertText}${currentBody.slice(selectionEnd)}`;
    const nextCursor = selectionStart + insertText.length;
    updateComposeBodySelection(nextBody, nextCursor);
  };

  const handleComposeBold = (): void => wrapComposeSelection('**', '**', 'bold text');
  const handleComposeItalic = (): void => wrapComposeSelection('*', '*', 'italic text');
  const handleComposeUnderline = (): void => wrapComposeSelection('__', '__', 'underlined text');

  const openComposeAttachmentPicker = (accept: string): void => {
    if (!composeAttachmentInputRef.current) return;
    composeAttachmentInputRef.current.accept = accept;
    composeAttachmentInputRef.current.value = '';
    composeAttachmentInputRef.current.click();
  };

  const handleComposeAttachmentInputChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (selectedFiles.length === 0) return;

    const existingAttachmentKeys = new Set(
      composeAttachments.map((attachment) => `${attachment.name}:${attachment.size}:${attachment.lastModified}`)
    );
    const existingTotalBytes = composeAttachments.reduce(
      (sum, attachment) => sum + Number(attachment.size || 0),
      0
    );

    const skippedReasons: string[] = [];
    const acceptedFiles: File[] = [];
    let acceptedTotalBytes = 0;

    for (const file of selectedFiles) {
      const fileKey = `${file.name}:${file.size}:${file.lastModified}`;
      if (existingAttachmentKeys.has(fileKey)) {
        skippedReasons.push(`${file.name} (already attached)`);
        continue;
      }
      if (file.size > MAX_COMPOSE_ATTACHMENT_SIZE_BYTES) {
        skippedReasons.push(
          `${file.name} (too large; max ${formatAttachmentSize(MAX_COMPOSE_ATTACHMENT_SIZE_BYTES)})`
        );
        continue;
      }
      if (composeAttachments.length + acceptedFiles.length >= MAX_COMPOSE_ATTACHMENTS) {
        skippedReasons.push(`${file.name} (max ${MAX_COMPOSE_ATTACHMENTS} attachments)`);
        continue;
      }
      if (existingTotalBytes + acceptedTotalBytes + file.size > MAX_COMPOSE_TOTAL_ATTACHMENT_SIZE_BYTES) {
        skippedReasons.push(
          `${file.name} (total attachment size limit ${formatAttachmentSize(MAX_COMPOSE_TOTAL_ATTACHMENT_SIZE_BYTES)})`
        );
        continue;
      }
      existingAttachmentKeys.add(fileKey);
      acceptedFiles.push(file);
      acceptedTotalBytes += file.size;
    }

    const nextAttachments: ComposeAttachment[] = [];
    for (const file of acceptedFiles) {
      try {
        const contentBase64 = await readFileAsBase64(file);
        nextAttachments.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Date.now()}`,
          name: file.name,
          contentType: file.type || 'application/octet-stream',
          size: file.size,
          contentBase64,
          lastModified: file.lastModified,
        });
      } catch (readError: any) {
        console.error('Failed to read attachment file:', readError);
        skippedReasons.push(`${file.name} (failed to read file)`);
      }
    }

    if (nextAttachments.length > 0) {
      setComposeAttachments((prev) => [...prev, ...nextAttachments]);
    }

    if (skippedReasons.length > 0) {
      alert(`Some files were skipped:\n- ${skippedReasons.join('\n- ')}`);
    }
  };

  const handleRemoveComposeAttachment = (attachmentId: string): void => {
    setComposeAttachments((prev) => prev.filter((attachment) => attachment.id !== attachmentId));
  };

  const handleComposeInsertLink = (): void => {
    const bodyInput = composeBodyRef.current as HTMLTextAreaElement | null;
    const currentBody = String(composeData.body || '');
    const selectionStart = bodyInput?.selectionStart ?? currentBody.length;
    const selectionEnd = bodyInput?.selectionEnd ?? selectionStart;
    const selectedText = currentBody.slice(selectionStart, selectionEnd);
    const rawUrl = window.prompt('Enter URL', 'https://');
    if (!rawUrl) return;
    const normalizedUrl = /^https?:\/\//i.test(rawUrl) ? rawUrl.trim() : `https://${rawUrl.trim()}`;
    if (!normalizedUrl || normalizedUrl === 'https://') return;
    const linkText =
      selectedText || window.prompt('Enter link text', normalizedUrl)?.trim() || normalizedUrl;
    const markdownLink = `[${linkText}](${normalizedUrl})`;
    const nextBody = `${currentBody.slice(0, selectionStart)}${markdownLink}${currentBody.slice(selectionEnd)}`;
    updateComposeBodySelection(nextBody, selectionStart + markdownLink.length);
  };

  const handleComposeInsertEmoji = (): void => {
    const emoji = window.prompt('Enter emoji to insert', '🙂');
    if (!emoji) return;
    insertComposeTextAtCursor(`${emoji} `);
  };

  const handleComposeToggleConfidential = (): void => {
    setComposeConfidentialMode((prev) => {
      const next = !prev;
      setComposeData((current) => {
        const currentSubject = String(current.subject || '');
        const subjectHasPrefix = currentSubject.trimStart().toLowerCase().startsWith(COMPOSE_CONFIDENTIAL_SUBJECT_PREFIX.toLowerCase());
        if (next && !subjectHasPrefix) {
          const trimmedCurrent = currentSubject.trim();
          return {
            ...current,
            subject: trimmedCurrent
              ? `${COMPOSE_CONFIDENTIAL_SUBJECT_PREFIX} ${trimmedCurrent}`
              : COMPOSE_CONFIDENTIAL_SUBJECT_PREFIX,
          };
        }
        if (!next && subjectHasPrefix) {
          return {
            ...current,
            subject: currentSubject.replace(/^\s*\[confidential\]\s*/i, ''),
          };
        }
        return current;
      });
      return next;
    });
  };

  const handleComposeInsertScheduleNote = (): void => {
    const defaultDate = new Date(Date.now() + 60 * 60 * 1000);
    const defaultValue = `${defaultDate.getFullYear()}-${String(defaultDate.getMonth() + 1).padStart(2, '0')}-${String(defaultDate.getDate()).padStart(2, '0')} ${String(defaultDate.getHours()).padStart(2, '0')}:${String(defaultDate.getMinutes()).padStart(2, '0')}`;
    const scheduleText = window.prompt('Add a schedule note (local date/time)', defaultValue);
    if (!scheduleText) return;
    insertComposeTextAtCursor(`\n[Schedule: ${scheduleText.trim()}]\n`);
  };

  const handleComposeClearDraft = (): void => {
    const hasDraftContent =
      Boolean(composeData.to || composeData.cc || composeData.bcc || composeData.subject || composeData.body) ||
      composeAttachments.length > 0;
    if (!hasDraftContent) return;
    const confirmed = window.confirm('Clear this draft and remove all attachments?');
    if (!confirmed) return;
    setComposeAttachments([]);
    if (composeAttachmentInputRef.current) composeAttachmentInputRef.current.value = '';
    setComposeConfidentialMode(false);
    setComposeData((prev) => ({
      ...prev,
      to: '',
      cc: '',
      bcc: '',
      subject: '',
      body: '',
    }));
  };

  const handleOpenCreateLeadModal = (
    email: Email,
    entityType: CreateEntityType = 'Lead'
  ): void => {
    if (!email) return;
    const internalMailboxEmails = emailAccounts
      .map((account) => String(account.email || account.id || '').toLowerCase().trim())
      .filter(Boolean);
    const draft = extractLeadDraftFromEmail(email, internalMailboxEmails);
    setCreateEntityType(entityType);
    setLeadDraftForm(draft);
    setLeadSourceEmail(email);
    setShowCreateEntityMenu(false);
    setDuplicateCandidates([]);
    setSelectedDuplicateCandidateKey('');
    setShowDuplicateLeadModal(false);
    setShowCreateLeadModal(true);
  };

  const handleCloseCreateLeadModal = (): void => {
    setShowCreateLeadModal(false);
    setShowCreateEntityMenu(false);
    setShowDuplicateLeadModal(false);
    setLeadDraftForm(EMPTY_LEAD_DRAFT);
    setLeadSourceEmail(null);
    setDuplicateCandidates([]);
    setSelectedDuplicateCandidateKey('');
  };

  const handleLeadDraftChange = (field: keyof LeadDraftFormData, value: string): void => {
    setLeadDraftForm((prev) => ({ ...prev, [field]: value }));
  };

  const markEmailStatusAsLead = async (
    sourceEmail: Email,
    statusLabel: CreateEntityType = 'Lead'
  ): Promise<void> => {
    await updateEmail(
      buildUpdatePayload({
        emailId: sourceEmail.id,
        email: sourceEmail,
        status: statusLabel
      })
    ).unwrap();
    if (selectedEmailToView?.id === sourceEmail.id) {
      setSelectedEmailToView((prev) => (prev ? { ...prev, status: statusLabel } : prev));
    }
  };

  const createLeadFromDraft = async (): Promise<void> => {
    const sourceEmail = leadSourceEmail || selectedEmailToView;
    if (!sourceEmail) return;
    const entityType = createEntityType;

    const firstName = String(leadDraftForm.firstName || '').trim();
    const lastName = String(leadDraftForm.lastName || '').trim();
    const email = String(leadDraftForm.email || '').trim().toLowerCase();
    const officePhone = String(leadDraftForm.officePhone || '').trim();
    const cellPhone = String(leadDraftForm.cellPhone || '').trim();
    const address = String(leadDraftForm.address || '').trim();
    const website = String(leadDraftForm.website || '').trim();
    const linkedInProfile = String(leadDraftForm.linkedInProfile || '').trim();
    const visibility = leadDraftForm.visibility === 'personal' ? 'personal' : 'global';
    const fallbackName = email ? toTitleCaseWords(email.split('@')[0].replace(/[._-]+/g, ' ')) : '';
    const fullName = `${firstName} ${lastName}`.trim() || fallbackName;

    try {
      if (entityType === 'Contact') {
        const resolvedName = splitName(`${firstName} ${lastName}`.trim() || fullName);
        const contactFirstName = firstName || resolvedName.firstName;
        const contactLastName = lastName || resolvedName.lastName;
        if (!contactFirstName || !contactLastName) {
          alert('Please provide first and last name before creating the contact.');
          return;
        }

        await createContact({
          firstName: contactFirstName,
          lastName: contactLastName,
          title: String(leadDraftForm.title || '').trim(),
          organization: String(leadDraftForm.organization || '').trim(),
          email,
          phone: officePhone || cellPhone,
          address,
          linkedInProfile,
          sourceChannel: 'Email',
          sourceOrigin: 'Unibox',
          visibility
        }).unwrap();
      } else {
        if (!fullName) {
          alert(`Please provide at least a name or email before creating the ${entityType.toLowerCase()}.`);
          return;
        }

        if (entityType === 'Client') {
          await createClient({
            name: fullName,
            organization: String(leadDraftForm.organization || '').trim(),
            email,
            phone: officePhone || cellPhone,
            address,
            website,
            linkedInProfile,
            status: 'current',
            sourceChannel: 'Email',
            sourceOrigin: 'Unibox',
            visibility
          }).unwrap();
        } else {
          await createLead({
            name: fullName,
            title: String(leadDraftForm.title || '').trim(),
            organization: String(leadDraftForm.organization || '').trim(),
            email,
            phone: officePhone || cellPhone,
            workPhone: officePhone,
            cellPhone,
            address,
            website,
            linkedInProfile,
            phoneType: 'Work',
            emailType: 'Work',
            sourceChannel: 'Email',
            sourceOrigin: 'Unibox',
            status: 'Lead',
            visibility
          }).unwrap();
        }
      }

      try {
        await markEmailStatusAsLead(sourceEmail, entityType);
      } catch (statusErr: any) {
        console.warn(`${entityType} created, but email status could not be updated to ${entityType}:`, statusErr);
        alert(`${entityType} created, but failed to mark the email status as ${entityType}. Please set status manually.`);
      }

      await loadEmails();
      handleCloseCreateLeadModal();
      if (entityType === 'Contact') {
        const contactName = `${firstName} ${lastName}`.trim() || fullName;
        alert(`Contact created: ${contactName}`);
      } else {
        alert(`${entityType} created: ${fullName}`);
      }
    } catch (error: any) {
      console.error(`Failed to create ${entityType.toLowerCase()} from email:`, error);
      alert(`Failed to create ${entityType.toLowerCase()}: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleCreateLeadFromEmail = async (): Promise<void> => {
    if (isCheckingDuplicateCandidates) {
      alert('Checking Leads/Contacts/Clients for duplicates. Please try again in a second.');
      return;
    }

    const matches = buildDuplicateCandidates(
      leadDraftForm,
      Array.isArray(leadsForDedup) ? leadsForDedup : [],
      Array.isArray(contactsForDedup) ? contactsForDedup : [],
      Array.isArray(clientsForDedup) ? clientsForDedup : []
    );

    if (matches.length > 0) {
      setDuplicateCandidates(matches);
      setSelectedDuplicateCandidateKey(matches[0].key);
      setShowDuplicateLeadModal(true);
      return;
    }

    await createLeadFromDraft();
  };

  const handleKeepExistingPersonNoAdd = async (): Promise<void> => {
    const sourceEmail = leadSourceEmail || selectedEmailToView;
    if (!sourceEmail) return;

    try {
      await markEmailStatusAsLead(sourceEmail, createEntityType);
      await loadEmails();
    } catch (statusErr: any) {
      console.warn(`Failed to mark email status as ${createEntityType} after no-add decision:`, statusErr);
      alert(`No new ${createEntityType.toLowerCase()} added, but failed to mark email status as ${createEntityType}.`);
    }

    handleCloseCreateLeadModal();
    alert(`No new ${createEntityType.toLowerCase()} added. Existing person kept.`);
  };

  const handleMergeWithExistingRecord = async (): Promise<void> => {
    const sourceEmail = leadSourceEmail || selectedEmailToView;
    if (!sourceEmail) return;
    if (!selectedDuplicateCandidate) {
      alert('Please select a record to merge.');
      return;
    }

    const firstName = String(leadDraftForm.firstName || '').trim();
    const lastName = String(leadDraftForm.lastName || '').trim();
    const email = String(leadDraftForm.email || '').trim().toLowerCase();
    const officePhone = String(leadDraftForm.officePhone || '').trim();
    const cellPhone = String(leadDraftForm.cellPhone || '').trim();
    const bestPhone = officePhone || cellPhone;
    const title = String(leadDraftForm.title || '').trim();
    const organization = String(leadDraftForm.organization || '').trim();
    const address = String(leadDraftForm.address || '').trim();
    const website = String(leadDraftForm.website || '').trim();
    const linkedInProfile = String(leadDraftForm.linkedInProfile || '').trim();
    const fallbackName = email ? toTitleCaseWords(email.split('@')[0].replace(/[._-]+/g, ' ')) : '';
    const fullName = `${firstName} ${lastName}`.trim() || fallbackName;

    const pickIfMissing = (existingValue: any, incomingValue: string): string | undefined => {
      const existing = String(existingValue || '').trim();
      const incoming = String(incomingValue || '').trim();
      return existing ? undefined : incoming || undefined;
    };

    try {
      if (selectedDuplicateCandidate.type === 'Lead') {
        const existing = selectedDuplicateCandidate.raw || {};
        const payload: Record<string, any> = {
          name: pickIfMissing(existing.name, fullName),
          title: pickIfMissing(existing.title, title),
          organization: pickIfMissing(existing.organization, organization),
          email: pickIfMissing(existing.email, email),
          phone: pickIfMissing(existing.phone, bestPhone),
          workPhone: pickIfMissing(existing.workPhone, officePhone),
          cellPhone: pickIfMissing(existing.cellPhone, cellPhone),
          address: pickIfMissing(existing.address, address),
          website: pickIfMissing(existing.website, website),
          linkedInProfile: pickIfMissing(existing.linkedInProfile, linkedInProfile),
          sourceChannel: pickIfMissing(existing.sourceChannel, 'Email'),
          sourceOrigin: pickIfMissing(existing.sourceOrigin, 'Unibox')
        };
        await updateLead({
          leadId: selectedDuplicateCandidate.id,
          ...payload
        }).unwrap();
      } else if (selectedDuplicateCandidate.type === 'Contact') {
        const existing = selectedDuplicateCandidate.raw || {};
        const payload: Record<string, any> = {
          firstName: pickIfMissing(existing.firstName, firstName),
          lastName: pickIfMissing(existing.lastName, lastName),
          title: pickIfMissing(existing.title, title),
          organization: pickIfMissing(existing.organization, organization),
          email: pickIfMissing(existing.email, email),
          phone: pickIfMissing(existing.phone, bestPhone),
          address: pickIfMissing(existing.address, address),
          linkedInProfile: pickIfMissing(existing.linkedInProfile, linkedInProfile),
          sourceChannel: pickIfMissing(existing.sourceChannel, 'Email'),
          sourceOrigin: pickIfMissing(existing.sourceOrigin, 'Unibox')
        };
        await updateContact({
          contactId: selectedDuplicateCandidate.id,
          ...payload
        }).unwrap();
      } else {
        const existing = selectedDuplicateCandidate.raw || {};
        const payload: Record<string, any> = {
          name: pickIfMissing(existing.name, fullName),
          organization: pickIfMissing(existing.organization, organization),
          email: pickIfMissing(existing.email, email),
          phone: pickIfMissing(existing.phone, bestPhone),
          address: pickIfMissing(existing.address, address),
          website: pickIfMissing(existing.website, website),
          linkedInProfile: pickIfMissing(existing.linkedInProfile, linkedInProfile),
          sourceChannel: pickIfMissing(existing.sourceChannel, 'Email'),
          sourceOrigin: pickIfMissing(existing.sourceOrigin, 'Unibox')
        };
        await updateClient({
          clientId: selectedDuplicateCandidate.id,
          ...payload
        }).unwrap();
      }

      try {
        await markEmailStatusAsLead(sourceEmail, createEntityType);
      } catch (statusErr: any) {
        console.warn(`Merge succeeded, but failed to mark email status as ${createEntityType}:`, statusErr);
      }

      await loadEmails();
      handleCloseCreateLeadModal();
      alert(`Merged into existing ${selectedDuplicateCandidate.type.toLowerCase()} record.`);
    } catch (error: any) {
      console.error('Failed to merge with existing record:', error);
      alert(`Failed to merge: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleAddAsDifferentPerson = async (): Promise<void> => {
    setShowDuplicateLeadModal(false);
    await createLeadFromDraft();
  };

  const resolveEmailLookupContext = (email: Email): { emailAccount: string; hasUnifiedKeys: boolean } => {
    const directEmailAccount = String(
      email.emailAccount ||
      (Array.isArray(email.to) ? email.to[0] : email.to) ||
      ''
    ).toLowerCase().trim();
    const pkDerivedEmailAccount = String(email.unifiedPk || '').startsWith('EA#')
      ? String(email.unifiedPk || '').slice(3).toLowerCase().trim()
      : '';
    const emailAccount = directEmailAccount || pkDerivedEmailAccount;
    const hasUnifiedKeys = Boolean(String(email.unifiedPk || '').trim() && String(email.unifiedSk || '').trim());
    return { emailAccount, hasUnifiedKeys };
  };

  const handleShowOriginal = async (email: Email): Promise<void> => {
    if (!email) return;
    const { emailAccount: resolvedEmailAccount, hasUnifiedKeys } = resolveEmailLookupContext(email);

    if (!resolvedEmailAccount && !hasUnifiedKeys) {
      setOriginalEmailError('Could not determine mailbox or unified keys for this email.');
      setOriginalEmailData(null);
      setShowOriginalModal(true);
      return;
    }

    setShowOriginalModal(true);
    setOriginalEmailError('');
    setOriginalEmailData(null);

    try {
      const response: any = await getEmailOriginal({
        emailAccount: resolvedEmailAccount,
        emailId: email.id,
        unifiedPk: email.unifiedPk,
        unifiedSk: email.unifiedSk,
      }).unwrap();
      setOriginalEmailData(response?.data || response || null);
    } catch (err: any) {
      const message = String(
        err?.data?.message ||
        err?.error ||
        err?.message ||
        'Failed to load original message source.'
      );
      setOriginalEmailError(message);
    }
  };

  const handleDownloadOriginal = (): void => {
    const raw = String(originalEmailData?.rawSource || '').trim();
    if (!raw) return;
    const filename = String(originalEmailData?.downloadFileName || 'original-message.eml');
    const blob = new Blob([raw], { type: 'message/rfc822;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const handleCopyOriginal = async (): Promise<void> => {
    const raw = String(originalEmailData?.rawSource || '').trim();
    if (!raw) return;
    try {
      await navigator.clipboard.writeText(raw);
    } catch (err: any) {
      alert(`Failed to copy original message: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleDownloadAttachment = async (email: Email, attachment: AttachmentMeta): Promise<void> => {
    const attachmentS3Key = String(attachment?.s3Key || '').trim();
    if (!attachmentS3Key) {
      alert('Attachment key is missing for this message.');
      return;
    }

    const { emailAccount, hasUnifiedKeys } = resolveEmailLookupContext(email);
    if (!emailAccount && !hasUnifiedKeys) {
      alert('Could not determine mailbox or unified keys for attachment download.');
      return;
    }

    setDownloadingAttachmentKey(attachmentS3Key);
    try {
      const response: any = await getEmailAttachment({
        emailAccount,
        emailId: email.id,
        unifiedPk: email.unifiedPk,
        unifiedSk: email.unifiedSk,
        attachmentS3Key,
      }).unwrap();
      const payload: any = response?.data || response || {};
      const downloadedAttachment: any = payload?.attachment || payload || {};
      const fileName = String(downloadedAttachment?.name || attachment.name || 'attachment.bin');
      const downloadUrl = String(downloadedAttachment?.downloadUrl || downloadedAttachment?.signedUrl || '').trim();

      if (downloadUrl) {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      // Backward compatibility fallback for older API responses.
      const contentBase64 = String(downloadedAttachment?.contentBase64 || '').trim();
      if (!contentBase64) {
        alert('Attachment download URL is unavailable for this message.');
        return;
      }

      const binary = atob(contentBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const contentType = String(downloadedAttachment?.contentType || attachment.contentType || 'application/octet-stream');
      const blob = new Blob([bytes], { type: contentType });
      const blobLink = document.createElement('a');
      blobLink.href = URL.createObjectURL(blob);
      blobLink.download = fileName;
      document.body.appendChild(blobLink);
      blobLink.click();
      document.body.removeChild(blobLink);
      URL.revokeObjectURL(blobLink.href);
    } catch (error: any) {
      const message = String(
        error?.data?.message ||
        error?.error ||
        error?.message ||
        'Failed to download attachment.'
      );
      alert(message);
    } finally {
      setDownloadingAttachmentKey('');
    }
  };

  const getBlockedSenderMatch = (email: Email): { type: 'email' | 'domain'; value: string } | null => {
    const senderCandidates = getSenderEmailCandidates(email);
    for (const candidate of senderCandidates) {
      if (blockedEmailSet.has(candidate)) {
        return { type: 'email', value: candidate };
      }
      const domain = candidate.split('@')[1]?.toLowerCase().trim() || '';
      if (domain && blockedDomainSet.has(domain)) {
        return { type: 'domain', value: domain };
      }
    }
    return null;
  };

  /**
   * Visual "Spam" label match.
   *
   * Currently driven entirely by the persisted `isSpam` flag on the
   * message row (set either by a manual Mark-as-Spam action or by the
   * inbound worker auto-routing from a previously-marked sender's
   * spam-sender list). Mirrors getBlockedSenderMatch's shape for
   * rendering consistency with the existing "Blocked" badge.
   */
  const getSpamMatch = (email: Email): { value: string; source: string } | null => {
    if (!email || email.isSpam !== true) return null;
    const senderCandidates = getSenderEmailCandidates(email);
    const sender = senderCandidates[0] || email.senderAddress || email.sender || '';
    const source = String((email as any).spamSource || 'manual');
    return { value: sender, source };
  };

  const extractSenderEmail = (email: Email): string => {
    const candidates = getSenderEmailCandidates(email);
    return candidates[0] || '';
  };

  const parseMessageIdTokens = (value: unknown): string[] => {
    const raw = String(value || '').trim();
    if (!raw) return [];
    const bracketed = raw.match(/<[^>]+>/g) || [];
    const plain = raw
      .split(/[\s,]+/)
      .map((token) => token.trim())
      .filter((token) => token.includes('@'));
    return Array.from(new Set([...bracketed, ...plain].map((token) => token.toLowerCase().trim()).filter(Boolean)));
  };

  const buildThreadTokenSet = (email: Email): Set<string> => {
    const tokens = new Set<string>();
    parseMessageIdTokens(email.messageId).forEach((token) => tokens.add(token));
    parseMessageIdTokens(email.inReplyTo).forEach((token) => tokens.add(token));
    parseMessageIdTokens(email.references).forEach((token) => tokens.add(token));
    return tokens;
  };

  const collectThreadEmailIds = (email: Email): string[] => {
    const tokenSet = buildThreadTokenSet(email);
    if (tokenSet.size === 0) return email.id ? [email.id] : [];
    const allCandidates = [
      ...(Array.isArray(emails) ? emails : []),
      ...(selectedEmailToView ? [selectedEmailToView] : []),
    ];
    const dedup = new Set<string>();
    allCandidates.forEach((candidate) => {
      const id = String(candidate?.id || '').trim();
      if (!id) return;
      const candidateTokens = buildThreadTokenSet(candidate as Email);
      const matches = Array.from(candidateTokens).some((token) => tokenSet.has(token));
      if (matches) dedup.add(id);
    });
    if (dedup.size === 0 && email.id) dedup.add(email.id);
    return Array.from(dedup);
  };

  const showSystemAlertRuleToast = (senderEmail: string, ruleId: string): void => {
    if (systemAlertRuleToastTimerRef.current) {
      clearTimeout(systemAlertRuleToastTimerRef.current);
    }
    setSystemAlertRuleToast({ senderEmail, ruleId });
    systemAlertRuleToastTimerRef.current = setTimeout(() => {
      setSystemAlertRuleToast(null);
      systemAlertRuleToastTimerRef.current = null;
    }, 8000);
  };

  const handleMoveToSystemAlerts = async (email: Email): Promise<void> => {
    const source = email || selectedEmailToView;
    if (!source?.id) {
      alert('No email selected.');
      return;
    }

    const emailIds = collectThreadEmailIds(source);
    if (emailIds.length === 0) {
      alert('No email selected.');
      return;
    }

    try {
      const sourceById = new Map<string, Email>();
      [...(emails || []), ...(filteredEmails || []), ...(selectedEmailToView ? [selectedEmailToView] : [])].forEach((row) => {
        const id = String(row?.id || '').trim();
        if (!id || sourceById.has(id)) return;
        sourceById.set(id, row as Email);
      });

      await Promise.all(emailIds.map(async (emailId) => {
        const sourceEmail = sourceById.get(emailId) || source;
        await updateEmail(buildUpdatePayload({
          emailId,
          email: sourceEmail,
          updates: {
            folder: SYSTEM_ALERTS_FOLDER_LABEL,
            isDeleted: false,
            isArchived: false,
            isSpam: false,
          },
          source: 'move_to_system_alerts',
        })).unwrap();
      }));

      if (selectedEmailToView && emailIds.includes(selectedEmailToView.id)) {
        setSelectedEmailToView({
          ...selectedEmailToView,
          folder: SYSTEM_ALERTS_FOLDER_LABEL,
          isDeleted: false,
          isArchived: false,
          isSpam: false,
        });
      }

      setSelectedEmails(new Set());
      await loadEmails();
    } catch (err: any) {
      alert(`Failed to move email to ${SYSTEM_ALERTS_FOLDER_LABEL}: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleAlwaysMoveSenderToSystemAlerts = async (email: Email): Promise<void> => {
    const senderEmail = extractSenderEmail(email);
    if (!senderEmail) {
      alert('Could not determine sender email.');
      return;
    }

    const existingRule = systemAlertFilters.find(
      (rule) => String(rule?.condition?.from || '').toLowerCase().trim() === senderEmail
    );
    if (existingRule) {
      const previousRules = [...systemAlertFilters];
      const nextRules = previousRules.filter((rule) => rule.id !== existingRule.id);
      setSystemAlertFilters(nextRules);
      try {
        await persistSystemAlertFilters(nextRules);
        if (systemAlertRuleToast?.ruleId === existingRule.id) {
          if (systemAlertRuleToastTimerRef.current) {
            clearTimeout(systemAlertRuleToastTimerRef.current);
            systemAlertRuleToastTimerRef.current = null;
          }
          setSystemAlertRuleToast(null);
        }
        setBlockSenderMsg(`Stopped routing future emails from ${senderEmail} to ${SYSTEM_ALERTS_FOLDER_LABEL}.`);
        setTimeout(() => setBlockSenderMsg(''), 4000);
      } catch (err: any) {
        setSystemAlertFilters(previousRules);
        alert(`Failed to remove System Alerts sender rule: ${err?.message || 'Unknown error'}`);
      }
      return;
    }

    const nextRule: SystemAlertFilterRule = {
      id: `system-alert-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: 'system_alert_filter',
      enabled: true,
      condition: {
        from: senderEmail,
      },
      action: {
        move_to_folder: SYSTEM_ALERTS_FOLDER_API_VALUE,
      },
      createdAt: new Date().toISOString(),
    };
    const previousRules = [...systemAlertFilters];
    const nextRules = normalizeSystemAlertFilters([nextRule, ...previousRules]);
    setSystemAlertFilters(nextRules);

    try {
      await persistSystemAlertFilters(nextRules);
      await handleMoveToSystemAlerts(email);
      showSystemAlertRuleToast(senderEmail, nextRule.id);
    } catch (err: any) {
      setSystemAlertFilters(previousRules);
      alert(`Failed to save System Alerts sender rule: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleUndoSystemAlertRule = async (): Promise<void> => {
    if (!systemAlertRuleToast) return;
    const { ruleId } = systemAlertRuleToast;
    if (systemAlertRuleToastTimerRef.current) {
      clearTimeout(systemAlertRuleToastTimerRef.current);
      systemAlertRuleToastTimerRef.current = null;
    }
    setSystemAlertRuleToast(null);
    const previousRules = [...systemAlertFilters];
    const nextRules = previousRules.filter((rule) => rule.id !== ruleId);
    setSystemAlertFilters(nextRules);
    try {
      await persistSystemAlertFilters(nextRules);
    } catch (err: any) {
      setSystemAlertFilters(previousRules);
      alert(`Failed to undo System Alerts rule: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleBlockSender = async (email: Email): Promise<void> => {
    const senderEmail = extractSenderEmail(email);
    if (!senderEmail) { alert('Could not determine sender email.'); return; }

    const currentBlocklist: any[] = Array.isArray((userPreferencesData as any)?.preferences?.blocklist)
      ? (userPreferencesData as any).preferences.blocklist
      : [];

    if (currentBlocklist.some((e: any) => String(e.value || '').toLowerCase() === senderEmail)) {
      setBlockSenderMsg(`${senderEmail} is already on the block list.`);
      setTimeout(() => setBlockSenderMsg(''), 4000);
      return;
    }

    const newEntry = {
      id: `bl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      value: senderEmail,
      type: 'email' as const,
      addedAt: new Date().toISOString(),
      source: 'unibox',
    };

    try {
      await updateUserPreferences({
        ...preferencesTenantWriteSpread(tenantIdForPrefs),
        preferences: { blocklist: [newEntry, ...currentBlocklist] },
      }).unwrap();
      setBlockSenderMsg(`Blocked ${senderEmail}`);
      setTimeout(() => setBlockSenderMsg(''), 4000);
    } catch (err: any) {
      alert(`Failed to block sender: ${err?.message || 'Unknown error'}`);
    }
  };

  const fetchCrmRecordsForEmail = async (senderEmail: string) => {
    const [leadsRes, contactsRes, clientsRes] = await Promise.all([
      fetchLeadsLazy({}).unwrap().catch(() => []),
      fetchContactsLazy({}).unwrap().catch(() => []),
      fetchClientsLazy({}).unwrap().catch(() => []),
    ]);
    return {
      leads: (Array.isArray(leadsRes) ? leadsRes : (leadsRes as any)?.leads || (leadsRes as any)?.data?.leads || []) as any[],
      contacts: (Array.isArray(contactsRes) ? contactsRes : (contactsRes as any)?.contacts || (contactsRes as any)?.data?.contacts || []) as any[],
      clients: (Array.isArray(clientsRes) ? clientsRes : (clientsRes as any)?.clients || (clientsRes as any)?.data?.clients || []) as any[],
    };
  };

  const handleBlockContactDNP = async (email: Email): Promise<void> => {
    const senderEmail = extractSenderEmail(email);
    if (!senderEmail) { alert('Could not determine sender email.'); return; }

    await handleBlockSender(email);
    const { leads, contacts, clients } = await fetchCrmRecordsForEmail(senderEmail);
    const DNP = 'Do Not Prospect';
    let updated = false;

    for (const lead of leads) {
      if (String(lead.email || '').toLowerCase().trim() === senderEmail && lead.contactStage !== DNP) {
        try { await updateLead({ leadId: lead.id || lead.leadId, contactStage: DNP }).unwrap(); updated = true; }
        catch (err) { console.error('Failed to update lead contact stage:', err); }
      }
    }
    for (const contact of contacts) {
      if (String(contact.email || '').toLowerCase().trim() === senderEmail && contact.contactStage !== DNP) {
        try { await updateContact({ contactId: contact.id || contact.contactId, contactStage: DNP }).unwrap(); updated = true; }
        catch (err) { console.error('Failed to update contact stage:', err); }
      }
    }
    for (const client of clients) {
      if (String(client.email || '').toLowerCase().trim() === senderEmail && client.contactStage !== DNP) {
        try { await updateClient({ clientId: client.id || client.clientId, contactStage: DNP }).unwrap(); updated = true; }
        catch (err) { console.error('Failed to update client contact stage:', err); }
      }
    }

    setBlockSenderMsg(updated
      ? `Blocked ${senderEmail} — Contact → Do Not Prospect`
      : `Blocked ${senderEmail} (no CRM record found for contact stage)`);
    setTimeout(() => setBlockSenderMsg(''), 5000);
  };

  const handleBlockAccountDNP = async (email: Email): Promise<void> => {
    const senderEmail = extractSenderEmail(email);
    if (!senderEmail) { alert('Could not determine sender email.'); return; }

    await handleBlockSender(email);
    const { leads, contacts, clients } = await fetchCrmRecordsForEmail(senderEmail);
    const DNP = 'Do Not Prospect';
    let updated = false;

    for (const lead of leads) {
      if (String(lead.email || '').toLowerCase().trim() === senderEmail && lead.accountStage !== DNP) {
        try { await updateLead({ leadId: lead.id || lead.leadId, accountStage: DNP }).unwrap(); updated = true; }
        catch (err) { console.error('Failed to update lead account stage:', err); }
      }
    }
    for (const contact of contacts) {
      if (String(contact.email || '').toLowerCase().trim() === senderEmail && contact.accountStage !== DNP) {
        try { await updateContact({ contactId: contact.id || contact.contactId, accountStage: DNP }).unwrap(); updated = true; }
        catch (err) { console.error('Failed to update contact account stage:', err); }
      }
    }
    for (const client of clients) {
      if (String(client.email || '').toLowerCase().trim() === senderEmail && client.accountStage !== DNP) {
        try { await updateClient({ clientId: client.id || client.clientId, accountStage: DNP }).unwrap(); updated = true; }
        catch (err) { console.error('Failed to update client account stage:', err); }
      }
    }

    setBlockSenderMsg(updated
      ? `Blocked ${senderEmail} — Account → Do Not Prospect`
      : `Blocked ${senderEmail} (no CRM record found for account stage)`);
    setTimeout(() => setBlockSenderMsg(''), 5000);
  };

  // Single-email Mark as Spam / Not Spam — mirrors the toolbar batch handlers so
  // the overflow menu (3-dot detail actions) can offer the same action on the
  // currently viewed message. Also writes the sender to the per-tenant spam
  // sender list server-side (see update-email.ts → addSpamSender), so future
  // inbound from this exact address auto-routes to Spam.
  const handleMarkAsSpam = async (email: Email): Promise<void> => {
    if (!email?.id) return;
    const emailAccount =
      email.emailAccount || (Array.isArray(email.to) ? email.to[0] : email.to) || email.from;
    try {
      await updateEmail(buildUpdatePayload({
        emailId: email.id,
        emailAccount,
        updates: { isSpam: true, isArchived: false, isDeleted: false },
        email,
      })).unwrap();
      if (selectedEmailToView && selectedEmailToView.id === email.id) {
        setSelectedEmailToView({ ...selectedEmailToView, isSpam: true });
      }
      await loadEmails();
    } catch (err: any) {
      console.error('❌ Error marking email as spam:', err);
      alert(`Failed to mark as spam: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleMarkAsNotSpam = async (email: Email): Promise<void> => {
    if (!email?.id) return;
    const emailAccount =
      email.emailAccount || (Array.isArray(email.to) ? email.to[0] : email.to) || email.from;
    try {
      await updateEmail(buildUpdatePayload({
        emailId: email.id,
        emailAccount,
        updates: { isSpam: false },
        email,
      })).unwrap();
      if (selectedEmailToView && selectedEmailToView.id === email.id) {
        setSelectedEmailToView({ ...selectedEmailToView, isSpam: false });
      }
      await loadEmails();
    } catch (err: any) {
      console.error('❌ Error marking email as not spam:', err);
      alert(`Failed to mark as not spam: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleComposeChange = (field: keyof ComposeData, value: string): void => {
    setComposeData(prev => ({
      ...prev,
      [field]: value
    }));
    if (field === 'subject') {
      setComposeConfidentialMode(
        String(value || '').trimStart().toLowerCase().startsWith(COMPOSE_CONFIDENTIAL_SUBJECT_PREFIX.toLowerCase())
      );
    }
  };

  // sendAnywayOverride === true means the user explicitly clicked "Send anyway"
  // on the Rules Engine block confirmation modal. We then ship
  // manualSendAnywayOverride: true (NOT isManualReply: true — those are now
  // distinct flags) so the backend can apply the correct tier-aware policy:
  // Send-anyway only unblocks SOFT-BLOCK tier reasons (Active Opportunity /
  // Current Client). HARD-STOP-NEW-OUTREACH reasons (Dead Opportunity / Do Not
  // Prospect) cannot be overridden by Send-anyway — only an actual Reply
  // bypasses them. The backend's JWT gate still applies to both flags, so a
  // service token caller (kg-smtp / EA worker / Instantly) cannot trigger
  // either bypass even if it injects the flags into its payload.
  // See hotfix/rules-engine-harden.
  const handleSendEmail = async (sendAnywayOverride: boolean = false): Promise<void> => {
    try {
      // Resolve From to a full email (API requires user@domain). Dropdown value might be id or localpart if backend returned incomplete email.
      const fromRaw = String(composeData.fromAccount || '').trim();
      const fromEmail: string =
        fromRaw.includes('@')
          ? fromRaw
          : (() => {
              const acc = emailAccounts.find((a: EmailAccount) => a.email === fromRaw || a.id === fromRaw || (a.email && a.email.toLowerCase().startsWith(fromRaw.toLowerCase() + '@')));
              return acc?.email && acc.email.includes('@') ? acc.email : fromRaw;
            })();

      if (!fromEmail.includes('@')) {
        alert('Please select a full email address for From (e.g. user@domain.com). The selected value is not a valid email.');
        return;
      }
      if (composeBlockedRecipients.length > 0) {
        const blockedPreview = composeBlockedRecipients
          .slice(0, 6)
          .map((entry) => `${entry.address} (${entry.type})`)
          .join(', ');
        alert(`Send blocked: one or more recipients are in Block List. ${blockedPreview}`);
        return;
      }

      // Find the sender's account to get accountId and region
      const senderAccount = emailAccounts.find((acc: EmailAccount) => acc.email === fromEmail || acc.id === fromEmail);
      const senderDomain = fromEmail.split('@')[1]?.toLowerCase();

      // Get accountId and region from domain lookup
      let accountId: string | undefined;
      let region: string = 'us-east-1';

      // First, try to find domain in domains data to get accountId, region, and purpose (for messageClass)
      let domainObj: any = null;
      if (domainsData?.data?.domains && senderDomain) {
        domainObj = domainsData.data.domains.find((d: any) =>
          d.domain?.toLowerCase() === senderDomain
        );
        if (domainObj) {
          accountId = domainObj.accountId;
          region = domainObj.region || 'us-east-1';
          console.log(`🔍 Found domain ${senderDomain} in domains data: accountId=${accountId}, region=${region}`);
        }
      }

      // Fallback to selectedAccount if domain lookup didn't work
      if (!accountId) {
        accountId = selectedAccount?.accountId || selectedAccount?.id;
        region = selectedAccount?.region || 'us-east-1';
        console.log(`🔍 Using selectedAccount fallback: accountId=${accountId}, region=${region}`);
      }

      // messageClass = domain purpose so Send Pools router selects the correct relay (cold_outreach / transactional / newsletter)
      const rawPurpose = (domainObj?.domainType || domainObj?.domain_type || domainTypeByDomain.get(senderDomain) || 'cold_outreach') as string;
      const purposeLower = String(rawPurpose).toLowerCase();
      const messageClass: 'cold_outreach' | 'transactional' | 'newsletter' =
        purposeLower === 'newsletter' ? 'newsletter'
        : purposeLower === 'cold_outreach' ? 'cold_outreach'
        : 'transactional'; // business / transactional / unknown → transactional (change if you add a distinct business messageClass)

      const senderDisplayName = (() => {
        const explicit = String(senderAccount?.displayName || '').trim();
        if (explicit) return explicit;

        const username = String(senderAccount?.username || '').trim();
        if (username) {
          if (username.includes('@')) return deriveDisplayNameFromEmailAddress(username);
          const cleanedUsername = username.replace(/[._-]+/g, ' ').replace(/\s+/g, ' ').trim();
          if (cleanedUsername) return toTitleCaseWords(cleanedUsername);
        }

        return deriveDisplayNameFromEmailAddress(fromEmail);
      })();

      // Tier-aware Rules Engine bypass — two DISTINCT flags, never coerced.
      //
      //   isManualReply           → true ONLY for Reply / Reply All (real
      //                             reply to an existing thread). Backend
      //                             bypasses BOTH soft-block and
      //                             hard-stop-new-outreach tiers.
      //   manualSendAnywayOverride → true ONLY when the user just clicked
      //                             "Send anyway" on the block modal. Backend
      //                             bypasses ONLY soft-block tier — hard-stop
      //                             reasons remain blocked even with this flag.
      //
      // Forward and brand-new compose without a Send-anyway click set neither
      // flag. The backend additionally enforces a JWT gate that ignores both
      // flags from non-human callers (kg-smtp / Instantly / EA scheduled
      // worker), so this conflation cannot be exploited from automation paths.
      // See hotfix/rules-engine-allow-replies + hotfix/rules-engine-harden.
      const isManualReply =
        composeMode === 'reply'
        || composeMode === 'reply-all';
      const manualSendAnywayOverride = sendAnywayOverride;

      const emailPayload: EmailPayload = {
        from: fromEmail,
        ...(senderDisplayName ? { fromName: senderDisplayName } : {}),
        to: parseRecipientList(composeData.to),
        cc: parseRecipientList(composeData.cc),
        bcc: parseRecipientList(composeData.bcc),
        subject: composeData.subject,
        body: composeData.body,
        bodyType: 'text', // For now, we'll send as plain text
        messageClass,
        ...(composeAttachments.length > 0 && {
          attachments: composeAttachments.map((attachment) => ({
            name: attachment.name,
            contentType: attachment.contentType,
            contentBase64: attachment.contentBase64,
            size: attachment.size,
          })),
        }),
        ...(accountId && accountId !== 'all' && { accountId }),
        ...(region && { region }),
        ...(isManualReply && { isManualReply: true }),
        ...(manualSendAnywayOverride && { manualSendAnywayOverride: true }),
      };

      console.log('Sending email (summary):', {
        from: fromEmail,
        fromName: senderDisplayName || '(none)',
        toCount: emailPayload.to.length,
        ccCount: emailPayload.cc.length,
        bccCount: emailPayload.bcc.length,
        attachmentCount: composeAttachments.length,
        messageClass,
        accountId: accountId || 'default',
        region,
        composeMode,
        isManualReply,
        manualSendAnywayOverride,
      });
      
      await sendEmail(emailPayload).unwrap();

      // Clear any lingering block dialog (e.g. user just retried with override).
      setRulesEngineBlock(null);

      // Show success immediately after API ack so users don't wait on a full inbox refetch.
      alert('Email sent successfully!');
      handleComposeClose();

      // After a successful send, switch to Sent and refresh in the background.
      setActiveTab('sent');
      void loadEmails().catch((refreshError: any) => {
        console.warn('⚠️ Could not refresh emails after send:', refreshError);
      });
    } catch (error: any) {
      console.error('Error sending email:', error);
      const status = error?.status ?? error?.originalStatus ?? error?.data?.code;
      // Prefer message from API (error.data.message); do not use error.data.error (boolean) as display text
      const errorMessage =
        error?.data?.message ||
        error?.data?.details ||
        (typeof error?.data?.error === 'string' ? error?.data?.error : null) ||
        error?.error ||
        error?.message ||
        (typeof error === 'string' ? error : '');
      const msg = String(errorMessage || '').toLowerCase();

      // Rules Engine block: backend returned 400 with details.code === 'RULES_ENGINE_BLOCKED'.
      // Surface the reason in a confirmation modal. The backend tells us
      // whether this block is overridable via Send-anyway:
      //   * details.overridable === true  → SOFT-BLOCK tier (Active Opp /
      //     Current Client). Modal shows Cancel + Send-anyway. Send-anyway
      //     retries with manualSendAnywayOverride: true.
      //   * details.overridable === false → HARD-STOP-NEW-OUTREACH tier
      //     (Dead Opp / Do Not Prospect). Modal shows Cancel only.
      // We never show this dialog when the user already had Send-anyway on
      // (sendAnywayOverride === true); in that case the backend's JWT gate or
      // a hard-stop reason must be the cause, so fall through to the normal
      // error path so they see the message. See hotfix/rules-engine-harden.
      const detailsCode = String(error?.data?.details?.code || '').toUpperCase();
      const isRulesEngineBlock = status === 400 && detailsCode === 'RULES_ENGINE_BLOCKED';
      if (isRulesEngineBlock && !sendAnywayOverride) {
        const blockedRecipients = Array.isArray(error?.data?.details?.blockedRecipients)
          ? error.data.details.blockedRecipients
          : [];
        const reasons = blockedRecipients.map((r: any) => {
          const rawTier = String(r?.tier || '').toLowerCase();
          const tier: 'soft' | 'hard_stop_new_outreach' | undefined =
            rawTier === 'soft' || rawTier === 'hard_stop_new_outreach' ? rawTier : undefined;
          return {
            recipient: String(r?.recipient || ''),
            entityType: String(r?.entityType || ''),
            entityName: String(r?.entityName || ''),
            target: String(r?.target || ''),
            stage: String(r?.stage || ''),
            triggerNames: Array.isArray(r?.triggerNames)
              ? r.triggerNames.map((t: any) => String(t || '')).filter(Boolean)
              : [],
            ...(tier ? { tier } : {}),
          };
        });
        // Default overridable=false if backend doesn't include the field
        // (legacy / safety fallback — better to show no override than to
        // accidentally let a hard-stop be overridden).
        const overridable = error?.data?.details?.overridable === true;
        setRulesEngineBlock({
          message: String(errorMessage || 'Rules Engine blocked send.'),
          reasons,
          composeModeAtBlock: composeMode,
          overridable,
          isOverriding: false,
        });
        return;
      }

      let userMessage: string;
      if (status === 400 && ((msg.includes('outbound') && msg.includes('credential')) || msg.includes('not set') || msg.includes('smtp.com') || msg.includes('elastic email'))) {
        userMessage = msg.includes('elastic email')
          ? 'Elastic Email SMTP credentials are not set for this relay account. Go to Domains → select the domain → Outbound, select the Elastic Email pool, then save SMTP username + password.'
          : 'Outbound SMTP.com credentials are not set for this domain. Go to Domains → select the domain → Outbound, then enter Channel login + password and save.';
      } else if ((status === 401 || status === 403) || /535|authentication failed|invalid login|auth failed/i.test(msg)) {
        const errStr = String(errorMessage || '');
        userMessage = /mailchannels rejected/i.test(errStr)
          ? 'MailChannels rejected sender/domain.'
          : /mailgun rejected/i.test(errStr)
            ? (errorMessage && typeof errorMessage === 'string' ? errorMessage : 'Mailgun rejected sender/domain. Check domain verification and SMTP credentials in the Mailgun dashboard and send pool.')
            : /elastic email authentication failed/i.test(errStr)
              ? (errorMessage && typeof errorMessage === 'string' ? errorMessage : 'Elastic Email authentication failed. Check SMTP username/password in Domains → Outbound for the selected relay account.')
            : 'SMTP.com authentication failed. Check channel login and password in Domains → Outbound.';
      } else {
        const fallbackDetails = !errorMessage ? JSON.stringify(error?.data ?? error) : '';
        userMessage = errorMessage || fallbackDetails || 'Unknown error';
      }
      alert(`Failed to send email: ${userMessage}${status ? ` (${status})` : ''}`);
    }
  };

  const containerClasses = [
    'p-0 unibox-container d-flex flex-column',
    filterPanelOpen && 'unibox-filter-open',
    selectedEmailToView && isMobile && 'unibox-detail-open'
  ].filter(Boolean).join(' ');

  return (
    <Container fluid className={containerClasses} style={{ position: 'relative', overflow: 'hidden', height: 'calc(100vh - 56px)', maxHeight: 'calc(100vh - 56px)' }}>
      {/* Unibox Header - Always visible at top */}
      <div className="bg-white border-bottom flex-shrink-0 unibox-header-actions p-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            {isMobile && selectedEmailToView && (
              <Button
                variant="outline-secondary"
                size="sm"
                className="unibox-back-btn"
                onClick={() => setSelectedEmailToView(null)}
                aria-label="Back to list"
                style={{ padding: '0.25rem 0.5rem', minWidth: 'auto' }}
              >
                <ArrowLeft size={18} />
              </Button>
            )}
            {isMobileOrTablet && (
              <button
                type="button"
                onClick={() => setFilterPanelOpen(true)}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  setFilterPanelOpen(true);
                }}
                className="btn btn-outline-secondary btn-sm d-lg-none d-inline-flex align-items-center justify-content-center"
                aria-label="Open filters"
                style={{
                  padding: '0.25rem 0.5rem',
                  minWidth: 44,
                  minHeight: 44,
                  cursor: 'pointer',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <List size={18} />
              </button>
            )}
            <h5 className="mb-0 me-3">Unibox</h5>
          </div>
          <div className="d-flex align-items-center gap-2 ms-auto flex-shrink-0">
            {!isMobileOrTablet && (
              <div
                className="text-end"
                style={{
                  lineHeight: 1.15,
                  maxWidth: 'min(420px, 55vw)',
                }}
              >
                <div className="text-muted text-start d-flex align-items-center gap-1" style={{ fontSize: '0.66rem' }}>
                  <span>Unibox Emails Sent</span>
                  <OverlayTrigger
                    trigger="click"
                    rootClose
                    placement="top"
                    overlay={<Tooltip id="unibox-sent-stats-info-tooltip">These totals are not including warm up emails.</Tooltip>}
                  >
                    <button
                      type="button"
                      aria-label="Sent stats warm up exclusion info"
                      className="p-0 border-0 bg-transparent text-muted d-inline-flex align-items-center"
                    >
                      <InfoCircle size={10} />
                    </button>
                  </OverlayTrigger>
                </div>
                <div style={{ fontSize: '0.72rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                  {(isLoadingSentStats || isFetchingSentStats) ? (
                    sentStatsLabel
                  ) : sentStatsError ? (
                    sentStatsLabel
                  ) : (
                    <>
                      <OverlayTrigger
                        trigger="click"
                        rootClose
                        placement="bottom"
                        overlay={
                          <Popover id="sent-today-breakdown-popover" style={{ maxWidth: 420 }}>
                            <Popover.Header as="div" style={{ fontSize: '0.75rem', fontWeight: 600, padding: '6px 10px' }}>
                              Sent Today ({formatSentStatsCount(sentStatsParsed.today)})
                            </Popover.Header>
                            <Popover.Body style={{ padding: '6px 10px', fontSize: '0.72rem', maxHeight: 640, overflowY: 'auto' }}>
                              {sentStatsParsed.todayAccounts.length === 0 ? (
                                <span className="text-muted">No emails sent today</span>
                              ) : (
                                sentStatsParsed.todayAccounts.map((acct) => (
                                  <div key={acct.email} className="d-flex justify-content-between align-items-start" style={{ padding: '2px 0', gap: 8 }}>
                                    <span style={{ flex: 1, minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{acct.email}</span>
                                    <span className="fw-semibold" style={{ flexShrink: 0 }}>{wholeNumberFormatter.format(acct.count)}</span>
                                  </div>
                                ))
                              )}
                            </Popover.Body>
                          </Popover>
                        }
                      >
                        <span role="button" style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>
                          Today: {formatSentStatsCount(sentStatsParsed.today)}
                        </span>
                      </OverlayTrigger>
                      {'  '}This Week: {formatSentStatsCount(sentStatsParsed.week)}{'  '}This Month: {formatSentStatsCount(sentStatsParsed.month)}
                    </>
                  )}
                </div>
              </div>
            )}
            {/* Help button moved next to filter collapse */}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowHelpSidebar(true)}
              title="Help"
              style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
            >
              <QuestionCircle size={14} />
            </Button>
            {!isMobileOrTablet && (
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={() => setIsFilterSectionCollapsed(!isFilterSectionCollapsed)}
                title={isFilterSectionCollapsed ? 'Expand filters' : 'Collapse filters'}
                style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
              >
                {isFilterSectionCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter overlay backdrop */}
      {isMobileOrTablet && filterPanelOpen && (
        <div
          className="position-fixed bg-dark bg-opacity-50"
          style={{ inset: 0, top: 56, zIndex: 1035 }}
          onClick={() => setFilterPanelOpen(false)}
          onTouchEnd={(e) => {
            e.preventDefault();
            setFilterPanelOpen(false);
          }}
          aria-hidden="true"
        />
      )}
      <Row className="g-0 flex-nowrap" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Left Column - Filter Section */}
        {(!isFilterSectionCollapsed || isMobileOrTablet) && (
          <Col xs={12} lg={isFilterSectionCollapsed ? 0 : 4} className={`unibox-filter-col bg-white border-end d-flex flex-column ${isMobileOrTablet ? 'd-flex' : ''}`} style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
            {isMobileOrTablet && (
              <div className="d-flex align-items-center justify-content-between p-2 border-bottom">
                <span className="fw-bold" style={{ fontSize: '0.9rem' }}>Filters</span>
                <Button
                  variant="link"
                  size="sm"
                  className="p-1 text-secondary"
                  onClick={() => setFilterPanelOpen(false)}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    setFilterPanelOpen(false);
                  }}
                  aria-label="Close filters"
                >
                  <X size={20} />
                </Button>
              </div>
            )}
            {/* Header (non-scrolling): tabs + filter controls. When Folders is active, omit bottom padding so the blue header sits tight under the tab row (same as prod). */}
            <div
              className={filterMode === 'folders' ? 'px-3 pt-3 pb-0' : 'p-3'}
              style={{ flexShrink: 0 }}
            >
              {/* Filter mode tabs (match the Inbox/Sent/Archive/Spam/Trash styling); mb-2 aligns Filters row with Compose row */}
              <div className="mb-2">
                <Tabs
                  activeKey={filterMode}
                  onSelect={(k) => {
                    if (!k) return;
                    setFilterMode(k as any);
                    if (k === 'inboxes') setSelectedFolder(null);
                  }}
                  className="kg-unibox-filter-tabs mb-0"
                  style={{ fontSize: '0.75rem' }}
                >
                  <style>{`
                    /* Make this tab-strip look like the message tab-strip to the right,
                       but allow wrapping to a second row for growth. */
                    .kg-unibox-filter-tabs.nav-tabs {
                      flex-wrap: wrap !important;
                      border-bottom: 1px solid #dee2e6;
                      row-gap: 6px;
                    }
                    .kg-unibox-filter-tabs.nav-tabs .nav-link {
                      font-size: 0.75rem !important;
                      padding: 0.375rem 0.75rem !important;
                      white-space: nowrap;
                    }
                  `}</style>
                  <Tab eventKey="inboxes" title={<span style={{ fontSize: '0.75rem' }}>All Inboxes</span>} />
                  <Tab eventKey="folders" title={<span style={{ fontSize: '0.75rem' }}>Folders</span>} />
                  <Tab eventKey="status" title={<span style={{ fontSize: '0.75rem' }}>Status</span>} />
                  <Tab eventKey="labels" title={<span style={{ fontSize: '0.75rem' }}>Labels</span>} />
                </Tabs>
              </div>
                
                {/* Status Section */}
                {filterMode === 'status' && (
                  <div className="mb-3">
                    <div className="bg-primary text-white rounded px-3 py-2 mb-2" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      Status
                    </div>
                    <InputGroup size="sm" className="mb-2">
                      <InputGroup.Text>
                        <Search size={14} />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Search"
                        value={statusSearchTerm}
                        onChange={(e) => setStatusSearchTerm(e.target.value)}
                        style={{ fontSize: '0.75rem' }}
                      />
                    </InputGroup>
                    <ListGroup variant="flush">
                      {alphabeticalStatusOptions
                        .filter(status => status.name.toLowerCase().includes(statusSearchTerm.toLowerCase()))
                        .map((status) => (
                          <ListGroup.Item
                            key={status.name}
                            className={`d-flex align-items-center justify-content-between py-2 px-3 cursor-pointer ${
                              selectedStatus === status.name ? 'active' : ''
                            }`}
                            onClick={() => {
                              setSelectedStatus(status.name);
                              setFilterMode('status'); // Ensure we're in status filter mode
                            }}
                            style={{ fontSize: '0.75rem' }}
                          >
                            <div className="d-flex align-items-center flex-grow-1">
                              <Lightning size={14} className="me-2" style={{ color: status.color }} />
                              <span className="text-truncate">{status.name}</span>
                            </div>
                            {isStatusLabelDeletable(status.name) && (
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 ms-2"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void handleDeleteStatusLabel(status.name);
                                }}
                                disabled={isUpdatingHiddenStatuses}
                                title={`Delete ${status.name} status label`}
                                style={{
                                  fontSize: '0.7rem',
                                  color: selectedStatus === status.name ? 'rgba(255,255,255,0.75)' : '#6c757d',
                                  textDecoration: 'none',
                                }}
                              >
                                <Trash size={11} />
                              </Button>
                            )}
                          </ListGroup.Item>
                        ))}
                      <ListGroup.Item
                        className="d-flex align-items-center py-2 px-3 cursor-pointer"
                        onClick={() => {
                          openCreateStatusLabelModal();
                        }}
                        style={{ fontSize: '0.75rem' }}
                      >
                        <Plus size={14} className="me-2 text-primary" />
                        <span className="text-primary">Create new label</span>
                      </ListGroup.Item>
                    </ListGroup>
                  </div>
                )}
                
                {/* All Inboxes Section */}
                {filterMode === 'inboxes' && (
                  <div
                    // Make the divider span the full column width, like the middle column header.
                    className="border-bottom"
                    style={{
                      marginLeft: -16,
                      marginRight: -16,
                      paddingLeft: 16,
                      paddingRight: 16,
                      paddingBottom: 16,
                      marginBottom: 16,
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      {/* Purpose quick buttons: same as "All Purposes" in Filters dropdown; mb-3 aligns gray line with middle column */}
                      <div className="d-flex gap-1">
                        {[
                          { key: 'business' as const, label: 'TR', title: 'Transactional' },
                          { key: 'newsletter' as const, label: 'NL', title: 'Newsletter' },
                          { key: 'cold_outreach' as const, label: 'CO', title: 'Cold Outreach' },
                        ].map(({ key, label, title }) => (
                          <Button
                            key={key}
                            size="sm"
                            variant={selectedPurposeFilter === key ? 'primary' : 'outline-secondary'}
                            className="rounded-circle p-0 d-flex align-items-center justify-content-center"
                            style={{ width: 26, height: 26, fontSize: '0.65rem', minWidth: 26 }}
                            onClick={() => setSelectedPurposeFilter(selectedPurposeFilter === key ? 'all' : key)}
                            title={title}
                          >
                            {label}
                          </Button>
                        ))}
                        <Button
                          size="sm"
                          variant={hasAnyLeftFiltersActive ? 'outline-primary' : 'outline-secondary'}
                          className="rounded-circle p-0 d-flex align-items-center justify-content-center"
                          style={{ width: 26, height: 26, fontSize: '0.65rem', minWidth: 26 }}
                          onClick={handleClearAllFilters}
                          title="Clear All Filters"
                        >
                          CL
                        </Button>
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div className="text-muted small" style={{ fontSize: '0.75rem' }}>
                          {filteredInboxes.length} Agents
                        </div>
<Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowInboxFilters((v) => !v)}
                        title={showInboxFilters ? 'Hide filters' : 'Show filters'}
                        style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                      >
                        Filters
                      </Button>
                      </div>
                    </div>

                    {/* Match the middle column search styling exactly (InputGroup size="sm", no inline overrides). */}
                    <InputGroup size="sm">
                      <InputGroup.Text>
                        <Search size={14} />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Search Email Accounts"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>

                    {showInboxFilters && (
                      <div className="d-flex flex-column gap-2 mt-2">
                        <Form.Select
                          size="sm"
                          value={selectedDomainFilter}
                          onChange={(e) => setSelectedDomainFilter(e.target.value)}
                          style={{ fontSize: '0.75rem' }}
                          title="Filter by domain"
                        >
                          <option value="all">All domains</option>
                          {availableDomains.map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </Form.Select>

                        <Form.Select
                          size="sm"
                          value={selectedPurposeFilter}
                          onChange={(e) => setSelectedPurposeFilter(e.target.value as any)}
                          style={{ fontSize: '0.75rem' }}
                          title="Filter by domain purpose"
                        >
                          <option value="all">All purposes</option>
                          <option value="business">Transactional</option>
                          <option value="newsletter">Newsletter</option>
                          <option value="cold_outreach">Cold Outreach</option>
                        </Form.Select>

                        <Form.Select
                          size="sm"
                          value={selectedCampaign || 'all'}
                          onChange={(e) => {
                            const v = e.target.value;
                            setSelectedCampaign(v === 'all' ? null : v);
                          }}
                          style={{ fontSize: '0.75rem' }}
                          title="Filter by campaign"
                        >
                          <option value="all">All campaigns</option>
                          {availableCampaigns.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Labels Section */}
                {filterMode === 'labels' && (
                  <div className="mb-3">
                    <div className="bg-primary text-white rounded px-3 py-2 mb-2" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      Labels
                    </div>
                    <InputGroup size="sm" className="mb-2">
                      <InputGroup.Text>
                        <Search size={14} />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Search labels"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ fontSize: '0.75rem' }}
                      />
                    </InputGroup>
                    {labels.length === 0 ? (
                      <div className="text-muted small text-center py-2" style={{ fontSize: '0.75rem' }}>
                        No labels yet. Create one by moving emails.
                      </div>
                    ) : (
                      <ListGroup variant="flush">
                        {labels
                          .filter(label => label.toLowerCase().includes(searchTerm.toLowerCase()))
                          .map((label) => (
                            <ListGroup.Item
                              key={label}
                              className={`d-flex align-items-center py-2 px-3 cursor-pointer ${
                                selectedLabel === label ? 'active' : ''
                              }`}
                              onClick={() => {
                                setSelectedLabel(label);
                                // TODO: Filter emails by label
                              }}
                              style={{ fontSize: '0.75rem' }}
                            >
                              <Folder size={14} className="me-2" />
                              {label}
                            </ListGroup.Item>
                          ))}
                      </ListGroup>
                    )}
                  </div>
                )}
            </div>

            {/* Scrollable list/body (folders + inbox list + status/labels hints share this region) */}
          <div className="flex-grow-1 min-h-0 overflow-auto unibox-scroll">
            {filterMode === 'inboxes' && (
              <ListGroup variant="flush">
                {filteredInboxes.map((account) => (
                <ListGroup.Item
                  key={account.id}
                  className={`unibox-inbox-item d-flex align-items-center justify-content-between py-2 px-3 cursor-pointer ${
                    (selectedInbox === account.id || selectedInbox === account.email) ? 'active' : ''
                  }`}
                  onClick={() => {
                    console.log('🔄 Unibox - Selected inbox:', account.id, account.email);
                    // Use email address for selectedInbox so we can query by emailAccount
                    setSelectedInbox(account.email || account.id);
                    // Also try matching by email if accountId doesn't match
                    if (filterMode !== 'inboxes') {
                      setFilterMode('inboxes');
                    }
                    setFilterPanelOpen(false); // Close filter drawer on mobile when selecting inbox
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div className="me-2" style={{ position: 'relative', width: '32px', height: '32px', minWidth: '32px' }}>
                      {account.avatar && !failedAvatars.has(account.id) ? (
                        <img 
                          src={`${account.avatar}?t=${Date.now()}`}
                          alt={account.email}
                          className="unibox-avatar rounded-circle"
                          style={{ 
                            width: '32px', 
                            height: '32px', 
                            objectFit: 'cover',
                            display: 'block'
                          }}
                          onError={(e) => {
                            const img = e.currentTarget;
                            console.error('Avatar image failed to load:', account.avatar);
                            console.error('Image src:', img.src);
                            console.error('Image naturalWidth:', img.naturalWidth);
                            console.error('Image naturalHeight:', img.naturalHeight);
                            console.error('Image complete:', img.complete);
                            // Mark this avatar as failed
                            setFailedAvatars(prev => new Set(prev).add(account.id));
                          }}
                          onLoad={() => {
                            console.log('Avatar image loaded successfully:', account.avatar);
                          }}
                        />
                      ) : (
                        <div className={`unibox-avatar rounded-circle d-flex align-items-center justify-content-center ${
                          (selectedInbox === account.id || selectedInbox === account.email) ? 'bg-white text-primary' : 'bg-primary text-white'
                        }`}>
                          {account.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div style={(account as any).hideFromInbox ? { opacity: 0.55 } : undefined}>
                      <div className={`fw-bold ${(selectedInbox === account.id || selectedInbox === account.email) ? 'text-white' : ''}`} style={{ fontSize: '0.75rem' }}>
                        {account.email}
                        {(account as any).hideFromInbox && <span style={{ fontSize: '0.55rem', marginLeft: 4, fontWeight: 'normal' }} title="Hidden from All Inboxes">hidden</span>}
                      </div>
                      <small className={`${(selectedInbox === account.id || selectedInbox === account.email) ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '0.65rem' }}>
                        {account.lastActivity}
                      </small>
                    </div>
                  </div>
                  {(() => {
                    const accountUnreadCount = unreadCountsByAccount.counts[account.id] || 0;
                    const accountEmails = unreadCountsByAccount.accountEmails[account.id] || [];
                    const hasUnreadEmails = accountUnreadCount > 0;
                    const inboxIsEmpty = accountEmails.length === 0;
                    
                    // Only show badge if there are unread emails AND inbox is empty
                    if (hasUnreadEmails && inboxIsEmpty) {
                      return (
                        <Badge bg={(selectedInbox === account.id || selectedInbox === account.email) ? 'light' : 'primary'} text={(selectedInbox === account.id || selectedInbox === account.email) ? 'dark' : 'white'} className="unibox-badge">
                          {accountUnreadCount}
                        </Badge>
                      );
                    }
                    return null;
                  })()}
                </ListGroup.Item>
              ))}
                {filteredInboxes.length === 0 && (
                  <ListGroup.Item className="text-center text-muted py-4">
                    No inboxes found
                  </ListGroup.Item>
                )}
              </ListGroup>
            )}
            {filterMode === 'folders' && (
              <div className="px-3 pb-3 pt-0">
                <div className="bg-primary text-white rounded px-3 py-2 mb-2" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  Folders
                </div>
                <InputGroup size="sm" className="mb-2">
                  <InputGroup.Text>
                    <Search size={14} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search folders"
                    value={folderSearchTerm}
                    onChange={(e) => setFolderSearchTerm(e.target.value)}
                    style={{ fontSize: '0.75rem' }}
                  />
                </InputGroup>

                <div className="d-flex gap-2 mb-2">
                  <Form.Control
                    size="sm"
                    placeholder="New folder name"
                    value={newLabelName}
                    onChange={(e) => setNewLabelName(e.target.value)}
                    style={{ fontSize: '0.75rem' }}
                  />
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={async () => {
                      const raw = String(newLabelName || '').trim();
                      if (!raw) return;
                      if (isReservedFolderName(raw)) {
                        alert('That folder name is reserved. Please choose a different name.');
                        return;
                      }
                      if (folderNameExists(raw)) {
                        alert('A folder with that name already exists.');
                        return;
                      }
                      const next = mergeUniqueFolders([...(folders || []), raw]);
                      setFolders(next);
                      setNewLabelName('');
                      try {
                        await persistFolderPreferences(next);
                      } catch (e: any) {
                        console.warn('Failed to save folders preference:', e);
                      }
                    }}
                    disabled={!String(newLabelName || '').trim()}
                    title="Create folder"
                  >
                    <Plus size={14} />
                  </Button>
                </div>

                <ListGroup variant="flush">
                  <ListGroup.Item
                    className={`d-flex align-items-center py-2 px-3 cursor-pointer ${!selectedFolder ? 'active' : ''}`}
                    onClick={() => setSelectedFolder(null)}
                    style={{ fontSize: '0.75rem' }}
                  >
                    All (unfiled)
                  </ListGroup.Item>
                  <ListGroup.Item
                    className={`d-flex align-items-center justify-content-between py-2 px-3 cursor-pointer ${isAllEmailsVirtualFolderSelected ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedFolder(ALL_EMAILS_FOLDER_LABEL);
                      setActiveTab('primary');
                    }}
                    style={{ fontSize: '0.75rem' }}
                  >
                    <span>All Emails (inbound)</span>
                    <Badge bg="secondary">System</Badge>
                  </ListGroup.Item>
                  <ListGroup.Item
                    className={`d-flex align-items-center justify-content-between py-2 px-3 cursor-pointer ${isQuarantinedFolderSelected ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedFolder('quarantined');
                      setActiveTab('primary');
                    }}
                    style={{ fontSize: '0.75rem' }}
                  >
                    <span>Quarantined</span>
                    <Badge bg="secondary">System</Badge>
                  </ListGroup.Item>
                  {(folders || [])
                    .filter((f) => !isSystemFolderSelection(f))
                    .filter((f) => !folderSearchTerm || f.toLowerCase().includes(folderSearchTerm.toLowerCase()))
                    .map((f) => {
                      const isRenaming = normalizeFolderLabel(renamingFolderName || '') === normalizeFolderLabel(f);
                      const isLockedFolder = isLockedFolderName(f);
                      return (
                        <ListGroup.Item
                          key={f}
                          className={`d-flex align-items-center justify-content-between py-2 px-3 cursor-pointer ${selectedFolder === f ? 'active' : ''}`}
                          onClick={() => {
                            if (isRenaming) return;
                            setSelectedFolder(f);
                            setActiveTab('primary');
                          }}
                          style={{ fontSize: '0.75rem' }}
                        >
                          {isRenaming ? (
                            <InputGroup
                              size="sm"
                              className="me-2"
                              onClick={(ev) => ev.stopPropagation()}
                              style={{ minWidth: 0 }}
                            >
                              <Form.Control
                                value={renamingFolderDraft}
                                onChange={(ev) => setRenamingFolderDraft(ev.target.value)}
                                onKeyDown={(ev) => {
                                  if (ev.key === 'Enter') {
                                    ev.preventDefault();
                                    saveFolderRename();
                                  }
                                  if (ev.key === 'Escape') {
                                    ev.preventDefault();
                                    cancelFolderRename();
                                  }
                                }}
                                autoFocus
                                disabled={isRenamingFolderSaving}
                                style={{ fontSize: '0.75rem' }}
                              />
                            </InputGroup>
                          ) : (
                            <span className="text-truncate">{f}</span>
                          )}

                          <div className="d-flex align-items-center gap-1">
                            {isRenaming ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  style={{ fontSize: '0.7rem', padding: '0.1rem 0.35rem' }}
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    saveFolderRename();
                                  }}
                                  disabled={isRenamingFolderSaving || !String(renamingFolderDraft || '').trim()}
                                  title="Save folder name"
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  style={{ fontSize: '0.7rem', padding: '0.1rem 0.35rem' }}
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    cancelFolderRename();
                                  }}
                                  disabled={isRenamingFolderSaving}
                                  title="Cancel rename"
                                >
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              isLockedFolder ? (
                                <Badge bg="secondary">System</Badge>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    style={{ fontSize: '0.7rem', padding: '0.1rem 0.35rem' }}
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      startFolderRename(f);
                                    }}
                                    title="Edit folder name"
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    style={{ fontSize: '0.7rem', padding: '0.1rem 0.35rem' }}
                                    onClick={async (ev) => {
                                      ev.stopPropagation();
                                      if (!window.confirm(`Delete folder "${f}"? Emails will become unfiled.`)) return;
                                      const next = (folders || []).filter((x) => normalizeFolderLabel(x) !== normalizeFolderLabel(f));
                                      setFolders(next);
                                      if (selectedFolder === f) setSelectedFolder(null);
                                      try {
                                        await persistFolderPreferences(next);
                                      } catch (e: any) {
                                        console.warn('Failed to save folders preference:', e);
                                      }
                                    }}
                                    title="Delete folder"
                                  >
                                    <X size={12} />
                                  </Button>
                                </>
                              )
                            )}
                          </div>
                        </ListGroup.Item>
                      );
                    })}
                  {(folders || []).length === 0 && (
                    <ListGroup.Item className="text-center text-muted py-3" style={{ fontSize: '0.75rem' }}>
                      No folders yet. Create one above.
                    </ListGroup.Item>
                  )}
                </ListGroup>
                <div className="text-muted small mt-2" style={{ fontSize: '0.7rem' }}>
                  Tip: use “Move to” to file selected emails into a folder.
                </div>

                {/* Auto-Filters */}
                <div className="mt-3">
                  <div className="d-flex align-items-center justify-content-between mb-2" style={{ gap: 8 }}>
                    <div className="fw-bold" style={{ fontSize: '0.8rem' }}>Auto-Filters</div>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      style={{ fontSize: '0.7rem', padding: '0.15rem 0.45rem' }}
                      onClick={() => {
                        setEditingAutoFilterId(null);
                        setAutoFilterDraft({ enabled: true, field: 'subject_or_body', matchType: 'contains', pattern: '', folder: isAllEmailsVirtualFolderSelected ? '' : (selectedFolder || '') });
                        setShowAutoFilterModal(true);
                      }}
                    >
                      <Plus size={14} className="me-1" /> Add
                    </Button>
                  </div>
                  <div className="text-muted small mb-2" style={{ fontSize: '0.7rem' }}>
                    Define rules like “Subject contains X” → move to a folder automatically (shared across the team; runs in the backend).
                  </div>

                  {autoFilters.length === 0 ? (
                    <div className="text-muted small" style={{ fontSize: '0.75rem' }}>
                      No auto-filters yet.
                    </div>
                  ) : (
                    <ListGroup variant="flush">
                      {autoFilters.map((r) => (
                        <ListGroup.Item key={r.id} className="px-2 py-2">
                          <div className="d-flex align-items-center justify-content-between" style={{ gap: 8 }}>
                            <div style={{ minWidth: 0 }}>
                              <div className="fw-bold text-truncate" style={{ fontSize: '0.75rem' }}>
                                {r.enabled ? '✅ ' : '⏸️ '} {r.folder}
                              </div>
                              <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                → {r.field === 'subject' ? 'Subject' : r.field === 'body' ? 'Body' : 'Subject/Body'} {r.matchType === 'contains' ? 'contains' : 'matches'}{' '}
                                <span style={{ fontFamily: 'monospace' }}>{r.pattern}</span>
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0 }}>
                              <Button
                                size="sm"
                                variant={r.enabled ? 'success' : 'outline-secondary'}
                                style={{ fontSize: '0.7rem', padding: '0.1rem 0.35rem' }}
                                title={r.enabled ? 'Disable rule' : 'Enable rule'}
                                onClick={async () => {
                                  const next = autoFilters.map((x) => x.id === r.id ? { ...x, enabled: !x.enabled } : x);
                                  setAutoFilters(next);
                                  try {
                                    await updateUserPreferences({ ...preferencesTenantWriteSpread(tenantIdForPrefs), preferences: { uniboxAutoRefresh: autoRefresh, uniboxFolders: folders, uniboxAutoFilters: next } }).unwrap();
                                  } catch (e: any) {
                                    console.warn('Failed to save auto-filters:', e);
                                  }
                                }}
                              >
                                {r.enabled ? 'On' : 'Off'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                style={{ fontSize: '0.7rem', padding: '0.1rem 0.35rem' }}
                                title="Edit rule"
                                onClick={() => {
                                  setEditingAutoFilterId(r.id);
                                  setAutoFilterDraft({ ...r });
                                  setShowAutoFilterModal(true);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                style={{ fontSize: '0.7rem', padding: '0.1rem 0.35rem' }}
                                title="Delete rule"
                                onClick={async () => {
                                  if (!window.confirm('Delete this auto-filter?')) return;
                                  const next = autoFilters.filter((x) => x.id !== r.id);
                                  setAutoFilters(next);
                                  try {
                                    await updateUserPreferences({ ...preferencesTenantWriteSpread(tenantIdForPrefs), preferences: { uniboxAutoRefresh: autoRefresh, uniboxFolders: folders, uniboxAutoFilters: next } }).unwrap();
                                  } catch (e: any) {
                                    console.warn('Failed to save auto-filters:', e);
                                  }
                                }}
                              >
                                <X size={12} />
                              </Button>
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </div>
              </div>
            )}
            {filterMode === 'status' && (
              <div className="p-3 text-center text-muted" style={{ fontSize: '0.75rem' }}>
                {selectedStatus ? `Showing emails with status: ${selectedStatus}` : 'Select a status to filter emails'}
              </div>
            )}
            {filterMode === 'labels' && (
              <div className="p-3 text-center text-muted" style={{ fontSize: '0.75rem' }}>
                {selectedLabel ? `Showing emails with label: ${selectedLabel}` : 'Select a label to filter emails'}
              </div>
            )}
          </div>
          </Col>
        )}

        {/* Middle Column - Email List (slides left when filter section collapses) */}
        <Col xs={12} md={6} lg={4} className="unibox-list-col bg-white border-end d-flex flex-column" style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
          <div className="p-3 border-bottom" style={{ flexShrink: 0 }}>
            {/* Tabs - All on one line */}
            <div className="mb-2">
              <Tabs
                activeKey={activeTab}
                onSelect={(k: string | null) => {
                  if (k) {
                    setActiveTab(k);
                  }
                }}
                className="kg-unibox-message-tabs mb-0"
                style={{ fontSize: '0.75rem' }}
              >
                <style>{`
                  .kg-unibox-message-tabs.nav-tabs {
                    flex-wrap: nowrap !important;
                    border-bottom: 1px solid #dee2e6;
                  }
                  .kg-unibox-message-tabs.nav-tabs .nav-link {
                    font-size: 0.75rem !important;
                    padding: 0.375rem 0.75rem !important;
                    white-space: nowrap;
                  }
                `}</style>
                <Tab eventKey="primary" title={<span style={{ fontSize: '0.75rem' }}>Inbox</span>} />
                <Tab eventKey="sent" title={<span style={{ fontSize: '0.75rem' }}>Sent</span>} />
                <Tab eventKey="archive" title={<span style={{ fontSize: '0.75rem' }}>Archive</span>} />
                <Tab eventKey="spam" title={<span style={{ fontSize: '0.75rem' }}>Spam</span>} />
                <Tab eventKey="trash" title={<span style={{ fontSize: '0.75rem' }}>Trash</span>} />
              </Tabs>
            </div>
            
            {/* Auto Refresh, Refresh Button, Help Button, Unread/Read toggles, Email Count, Compose - single row; icon buttons shrunk for room */}
            <div className="d-flex align-items-center justify-content-between mb-3 gap-2" style={{ flexWrap: 'nowrap' }}>
              <div className="d-flex align-items-center gap-1 flex-shrink-0">
                {/* Auto Refresh Toggle - Compact with Dismissible Tooltip */}
                {showAutoRefreshTooltip ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip style={{ fontSize: '0.7rem', maxWidth: '200px' }}>
                        <div>
                          Toggle on to auto-refresh emails every 30 seconds
                          <br />
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowAutoRefreshTooltip(false);
                              localStorage.setItem('unibox-auto-refresh-tooltip-dismissed', 'true');
                            }}
                            style={{
                              marginTop: '4px',
                              fontSize: '0.65rem',
                              background: 'none',
                              border: 'none',
                              color: '#6c757d',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              padding: 0
                            }}
                          >
                            Don't show again
                          </button>
                        </div>
                      </Tooltip>
                    }
                    show={showAutoRefreshTooltip}
                  >
                    <div>
                      <Form.Check
                        type="switch"
                        id="auto-refresh-unibox"
                        label={<span style={{ fontSize: '0.7rem' }}>Auto</span>}
                        checked={autoRefresh}
                        disabled={autoRefreshPrefsBusy}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          setAutoRefresh(newValue);
                          // Save to backend immediately
                          updateUserPreferences({
                            ...preferencesTenantWriteSpread(tenantIdForPrefs),
                            preferences: { uniboxAutoRefresh: newValue }
                          }).catch((error) => {
                            console.error('❌ Error saving auto refresh preference:', error);
                          });
                        }}
                        style={{ margin: 0 }}
                      />
                    </div>
                  </OverlayTrigger>
                ) : (
                  <Form.Check
                    type="switch"
                    id="auto-refresh-unibox"
                    label={<span style={{ fontSize: '0.7rem' }}>Auto</span>}
                    checked={autoRefresh}
                    disabled={autoRefreshPrefsBusy}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setAutoRefresh(newValue);
                      // Save to backend immediately
                      updateUserPreferences({
                        ...preferencesTenantWriteSpread(tenantIdForPrefs),
                        preferences: { uniboxAutoRefresh: newValue }
                      }).catch((error) => {
                        console.error('❌ Error saving auto refresh preference:', error);
                      });
                    }}
                    style={{ margin: 0 }}
                  />
                )}
                {/* Refresh Button - Quick action (icons centered inside; shape unchanged) */}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="kg-unibox-refresh-toolbar-btn"
                  onClick={handleRefresh}
                  disabled={emailRefreshToolbarBusy}
                  title={
                    autoRefreshPrefsBusy && !isLoadingEmails && !isRefreshingEmails
                      ? 'Saving preferences…'
                      : 'Refresh emails'
                  }
                  aria-busy={emailRefreshToolbarBusy}
                  style={{ 
                    fontSize: '0.75rem', 
                    padding: '0.32rem 0.5rem',
                    minWidth: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <span
                    className={`d-inline-flex align-items-center justify-content-center kg-unibox-refresh-icon-wrap${emailRefreshIconSpinning ? ' kg-unibox-refresh-icon-wrap--spinning' : ''}`}
                  >
                    <ArrowClockwise size={14} />
                  </span>
                </Button>
                {/* Back to unified feed (All inboxes) */}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleShowAllEmailsFeed}
                  disabled={selectedInbox === 'all'}
                  title="Show all inboxes"
                  style={{ 
                    fontSize: '0.75rem', 
                    padding: '0.32rem 0.5rem',
                    minWidth: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Collection size={14} />
                </Button>
                {/* Unread toggle - Quick action */}
                <Button
                  variant={showUnreadOnly ? 'secondary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => {
                    setShowUnreadOnly(prev => !prev);
                    if (showReadOnly) setShowReadOnly(false);
                  }}
                  title={showUnreadOnly ? 'Show: Unread only — click to show all' : 'Show all — click to show unread only'}
                  style={{ 
                    fontSize: '0.75rem', 
                    padding: '0.32rem 0.5rem',
                    minWidth: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <EnvelopeFill size={14} />
                </Button>
                {/* Read toggle - Quick action */}
                <Button
                  variant={showReadOnly ? 'secondary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => {
                    setShowReadOnly(prev => !prev);
                    if (showUnreadOnly) setShowUnreadOnly(false);
                  }}
                  title={showReadOnly ? 'Show: Read only — click to show all' : 'Show all — click to show read only'}
                  style={{ 
                    fontSize: '0.75rem', 
                    padding: '0.32rem 0.5rem',
                    minWidth: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <EnvelopeOpen size={14} />
                </Button>
              </div>
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <div
                  className="text-muted small"
                  style={{
                    fontSize: '0.75rem',
                    lineHeight: 1.1,
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                  }}
                  title={totalEmailCountLabel}
                >
                  {totalEmailCountLabel}
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleComposeClick}
                  style={{
                    fontSize: isMobile ? '0.72rem' : '0.75rem',
                    padding: isMobile ? '0.3rem 0.6rem' : '0.375rem 0.75rem',
                    minWidth: isMobile ? 90 : undefined
                  }}
                >
                  Compose
                </Button>
              </div>
            </div>
            
            <div className="d-flex align-items-center gap-2">
              <InputGroup
                size="sm"
                style={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  maxWidth: isMobile ? '100%' : '80%',
                }}
              >
                <InputGroup.Text>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search mail"
                  value={emailSearchTerm}
                  onChange={(e) => setEmailSearchTerm(e.target.value)}
                />
              </InputGroup>
              <div className="d-flex align-items-center gap-1 flex-shrink-0">
                <div
                  className="text-muted small"
                  style={{
                    fontSize: '0.75rem',
                    lineHeight: 1.1,
                    textAlign: 'right',
                    minWidth: isMobile ? 22 : undefined,
                    whiteSpace: 'nowrap',
                    marginRight: '0.25rem',
                  }}
                >
                  {visibleEmailCountLabel}
                </div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handlePreviousEmailPage}
                  disabled={!canNavigatePreviousEmailPage || isLoadingEmails || isRefreshingEmails}
                  style={{ padding: '0.25rem 0.45rem', lineHeight: 1 }}
                  title="Previous page"
                >
                  <ChevronLeft size={13} />
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleNextEmailPage}
                  disabled={!canNavigateNextEmailPage || isLoadingEmails || isRefreshingEmails}
                  style={{ padding: '0.25rem 0.45rem', lineHeight: 1 }}
                  title="Next page"
                >
                  <ChevronRight size={13} />
                </Button>
              </div>
            </div>
          </div>

          <div
            ref={emailListScrollRef}
            className="flex-grow-1 overflow-auto unibox-scroll"
            style={{ minHeight: 0, maxHeight: '100%' }}
          >
            {visibleEmails.length === 0 ? (
              <div className="text-center text-muted py-5 px-4">
                <Envelope size={48} className="mb-3" />
                {!normalizedEmailAccount && !isAllAccountsSelected && !selectedFolder ? (
                  <>
                    <h6 style={{ fontSize: '0.9rem', fontWeight: 500 }}>Select an email account to view messages</h6>
                    <p className="text-muted small" style={{ fontSize: '0.8rem' }}>Choose an email account from the list to see your emails</p>
                  </>
                ) : selectedFolder ? (
                  <>
                    <h6 style={{ fontSize: '0.9rem', fontWeight: 500 }}>No emails in this folder</h6>
                    <p className="small" style={{ fontSize: '0.8rem' }}>
                      {isAllEmailsVirtualFolderSelected
                        ? 'All inbound emails should appear here (filed and unfiled).'
                        : `Emails filed into "${selectedFolder}" will appear here`}
                    </p>
                  </>
                ) : (
                  <>
                    <h6 style={{ fontSize: '0.9rem', fontWeight: 500 }}>No emails found</h6>
                    <p className="small" style={{ fontSize: '0.8rem' }}>Try adjusting your search or filters</p>
                  </>
                )}
              </div>
            ) : (
              <div className="p-2">
                {/* Select All + Bulk Actions (only after selection) */}
                {selectedEmails.size > 0 && (
                <div className="unibox-bulk-bar d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    checked={selectedEmails.size === visibleEmails.length && visibleEmails.length > 0}
                    onChange={handleSelectAll}
                    aria-label="Select all emails"
                    style={{ margin: 0 }}
                  />
                  <span className="ms-2 text-muted small">
                    {selectedEmails.size > 0 ? `${selectedEmails.size} selected` : 'Select all'}
                  </span>
                  
                  {/* Action Buttons - Show when emails are selected */}
                  {/* Order: Move to → Archive → Mark unread → Mark read → Block list → Spam → Trash */}
                  {selectedEmails.size > 0 && (
                    <div className="ms-auto d-flex align-items-center gap-1">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Move to</Tooltip>}
                      >
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowMoveToDropdown(!showMoveToDropdown)}
                            style={{ 
                              padding: '0.25rem 0.5rem',
                              border: 'none',
                              backgroundColor: 'transparent'
                            }}
                          >
                            <Folder size={16} />
                          </Button>
                          {showMoveToDropdown && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                zIndex: 1000,
                                backgroundColor: 'white',
                                border: '1px solid #dee2e6',
                                borderRadius: '0.25rem',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                minWidth: '150px',
                                marginTop: '0.25rem'
                              }}
                              onMouseLeave={() => setShowMoveToDropdown(false)}
                            >
                              <div
                                className="px-3 py-2 cursor-pointer hover-bg-light"
                                style={{ fontSize: '0.75rem' }}
                                onClick={async () => {
                                  // Move to Inbox: Restore from trash/archive (isDeleted = false, isArchived = false)
                                  console.log('📥 Move to Inbox:', Array.from(selectedEmails));
                                  
                                  if (selectedEmails.size === 0) {
                                    console.warn('⚠️ No emails selected for restore');
                                    alert('Please select at least one email to restore');
                                    setShowMoveToDropdown(false);
                                    return;
                                  }
                                  
                                  try {
                                    const emailIds = Array.from(selectedEmails);
                                    console.log('📥 Restoring emails to inbox:', emailIds);
                                    
                                    const updatePromises = emailIds.map(async (emailId) => {
                                      const email = filteredEmails.find(e => e.id === emailId);
                                      if (!email || !email.emailAccount) {
                                        console.warn(`⚠️ Email ${emailId} not found or missing emailAccount`);
                                        return;
                                      }
                                      
                                      try {
                                        const result = await updateEmail(buildUpdatePayload({
                                          emailId,
                                          emailAccount: email.emailAccount,
                                          updates: {
                                            isDeleted: false,
                                            isArchived: false,
                                            isSpam: false, // CRITICAL: Also remove spam flag when moving to inbox
                                            folder: '' // Clear custom folder (unfile)
                                          },
                                          email
                                        })).unwrap();
                                        
                                        console.log(`✅ Email ${emailId} restored to inbox, result:`, result);
                                      } catch (updateError: any) {
                                        console.error(`❌ Failed to restore email ${emailId}:`, updateError);
                                        throw updateError;
                                      }
                                    });
                                    
                                    await Promise.all(updatePromises);
                                    
                                    setOptimisticRemovedIds(prev => {
                                      const next = new Set(prev);
                                      emailIds.forEach(id => next.add(id));
                                      return next;
                                    });
                                    setSelectedEmails(new Set());
                                    loadEmails();
                                    setActiveTab('primary');
                                  } catch (error: any) {
                                    console.error('❌ Error restoring emails:', error);
                                    alert(`Failed to restore emails: ${error.message || 'Unknown error'}`);
                                  }
                                  
                                  setShowMoveToDropdown(false);
                                }}
                              >
                                Inbox
                              </div>
                              <div
                                className="px-3 py-2 cursor-pointer hover-bg-light"
                                style={{ fontSize: '0.75rem' }}
                                onClick={async () => {
                                  // Move to Spam: Set isSpam = true
                                  console.log('🚫 Move to Spam:', Array.from(selectedEmails));
                                  
                                  if (selectedEmails.size === 0) {
                                    setShowMoveToDropdown(false);
                                    return;
                                  }
                                  
                                  try {
                                    // Update all selected emails to mark as spam
                                    const updatePromises = Array.from(selectedEmails).map(async (emailId) => {
                                      const email = emails.find(e => e.id === emailId);
                                      if (!email) {
                                        console.error(`❌ Email not found in local state: ${emailId}`);
                                        return;
                                      }
                                      
                                      // CRITICAL: Use emailAccount (recipient) not from (sender)
                                      const emailAccount = email.emailAccount || (Array.isArray(email.to) ? email.to[0] : email.to) || email.from;
                                      const payload = buildUpdatePayload({
                                        emailId,
                                        emailAccount,
                                        updates: {
                                          isSpam: true,
                                          isArchived: false,
                                          isDeleted: false,
                                          folder: ''
                                        },
                                        email
                                      });
                                      const result = await updateEmail(payload).unwrap();
                                      return result;
                                    });
                                    
                                    await Promise.all(updatePromises);
                                    
                                    setOptimisticRemovedIds(prev => {
                                      const next = new Set(prev);
                                      Array.from(selectedEmails).forEach(id => next.add(id));
                                      return next;
                                    });
                                    setSelectedEmails(new Set());
                                    loadEmails();
                                    setActiveTab('spam');
                                  } catch (error: any) {
                                    console.error('❌ Error marking emails as spam:', error);
                                    alert(`Failed to mark emails as spam: ${error.message || 'Unknown error'}`);
                                  }
                                  
                                  setShowMoveToDropdown(false);
                                }}
                              >
                                Spam
                              </div>
                              <div
                                className="px-3 py-2 cursor-pointer hover-bg-light"
                                style={{ fontSize: '0.75rem' }}
                                onClick={() => {
                                  // TODO: Move to Trash
                                  console.log('Move to Trash:', Array.from(selectedEmails));
                                  setShowMoveToDropdown(false);
                                }}
                              >
                                Trash
                              </div>
                              {folders.length > 0 && (
                                <>
                                  <div className="border-top my-1"></div>
                                  {folders.map((f) => (
                                    <div
                                      key={f}
                                      className="px-3 py-2 cursor-pointer hover-bg-light"
                                      style={{ fontSize: '0.75rem' }}
                                      onClick={async () => {
                                        // Move to Folder: set folder and ensure not deleted/archived/spam
                                        try {
                                          const emailIds = Array.from(selectedEmails);
                                          const updatePromises = emailIds.map(async (emailId) => {
                                            const email = filteredEmails.find(e => e.id === emailId);
                                            const emailAccount = (email as any)?.emailAccount || (Array.isArray((email as any)?.to) ? (email as any).to[0] : (email as any)?.to) || (email as any)?.from;
                                            if (!emailAccount) return;
                                            await updateEmail(buildUpdatePayload({
                                              emailId,
                                              emailAccount,
                                              updates: {
                                                folder: f,
                                                isDeleted: false,
                                                isArchived: false,
                                                isSpam: false
                                              },
                                              email
                                            })).unwrap();
                                          });
                                          await Promise.all(updatePromises);
                                          setSelectedEmails(new Set());
                                          setSelectedFolder(f);
                                          setFilterMode('folders');
                                          loadEmails();
                                        } catch (err: any) {
                                          console.error('Failed to move emails to folder:', err);
                                          alert(`Failed to move emails to folder: ${err?.message || 'Unknown error'}`);
                                        } finally {
                                          setShowMoveToDropdown(false);
                                        }
                                      }}
                                    >
                                      {f}
                                    </div>
                                  ))}
                                </>
                              )}
                              <div className="border-top my-1"></div>
                              <div
                                className="px-3 py-2 cursor-pointer hover-bg-light d-flex align-items-center"
                                style={{ fontSize: '0.75rem' }}
                                onClick={() => {
                                  setShowMoveToDropdown(false);
                                  setShowCreateLabelModal(true);
                                }}
                              >
                                <FolderPlus size={14} className="me-2" />
                                Create folder
                              </div>
                            </div>
                          )}
                        </div>
                      </OverlayTrigger>
                      
                      {activeTab !== 'archive' && !isQuarantinedView && (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Archive (move to Archive folder)</Tooltip>}
                        >
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={async () => {
                              const emailIds = getActionEmailIds();
                              setOptimisticRemovedIds(prev => {
                                const next = new Set(prev);
                                emailIds.forEach(id => next.add(id));
                                return next;
                              });
                              setSelectedEmails(new Set());
                              
                              try {
                                const updatePromises = emailIds.map(async (emailId) => {
                                  const email = filteredEmails.find(e => e.id === emailId) || (selectedEmailToView?.id === emailId ? selectedEmailToView : null);
                                  if (!email || !email.emailAccount) return;
                                  
                                  await updateEmail(buildUpdatePayload({
                                    emailId,
                                    emailAccount: email.emailAccount,
                                    updates: {
                                      isArchived: true,
                                      isDeleted: false
                                    },
                                    email
                                  })).unwrap();
                                });
                                
                                await Promise.all(updatePromises);
                                loadEmails();
                              } catch (error: any) {
                                console.error('Error archiving emails:', error);
                                setOptimisticRemovedIds(prev => {
                                  const next = new Set(prev);
                                  emailIds.forEach(id => next.delete(id));
                                  return next;
                                });
                                alert(`Failed to archive emails: ${error.message || 'Unknown error'}`);
                              }
                            }}
                            disabled={isUpdatingEmail}
                            style={{ 
                              padding: '0.25rem 0.5rem',
                              border: 'none',
                              backgroundColor: 'transparent'
                            }}
                          >
                            <Archive size={16} />
                          </Button>
                        </OverlayTrigger>
                      )}
                      
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Mark as unread</Tooltip>}
                      >
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={async () => {
                            const emailIds = getActionEmailIds();
                            if (emailIds.length === 0) return;
                            
                            try {
                              // Update all selected emails to unread
                              const updatePromises = emailIds.map(async (emailId) => {
                                const email = emails.find(e => e.id === emailId) || (selectedEmailToView?.id === emailId ? selectedEmailToView : null);
                                if (!email) {
                                  console.error(`❌ Email not found in local state: ${emailId}`);
                                  return;
                                }
                                
                                // CRITICAL: Use emailAccount (recipient) not from (sender)
                                const emailAccount = email.emailAccount || (Array.isArray(email.to) ? email.to[0] : email.to) || email.from;
                                
                                console.log(`🔄 Marking email as unread:`, {
                                  emailId,
                                  emailAccount,
                                  from: email.from,
                                  to: email.to,
                                  emailAccountField: email.emailAccount,
                                  fullEmail: email
                                });
                                
                                const result = await updateEmail(buildUpdatePayload({
                                  emailId,
                                  emailAccount: emailAccount,
                                  updates: {
                                    isRead: false
                                  },
                                  email
                                })).unwrap();
                                
                                console.log(`✅ Mark as unread API response:`, result);
                                return result;
                              });
                              
                              await Promise.all(updatePromises);
                              
                              // Update local state for selected email if it's being viewed
                              if (selectedEmailToView && selectedEmails.has(selectedEmailToView.id)) {
                                setSelectedEmailToView({ ...selectedEmailToView, isRead: false });
                              }
                              
                              setSelectedEmails(new Set());
                              await loadEmails();
                            } catch (error: any) {
                              console.error('❌ Error marking emails as unread:', error);
                              alert(`Failed to mark emails as unread: ${error.message || 'Unknown error'}`);
                            }
                          }}
                          disabled={selectedEmails.size === 0 || isUpdatingEmail}
                          style={{ 
                            padding: '0.25rem 0.5rem',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: (selectedEmails.size === 0 || isUpdatingEmail) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          <EnvelopeFill size={16} />
                        </Button>
                      </OverlayTrigger>
                      
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Mark as read</Tooltip>}
                      >
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={async () => {
                            if (selectedEmails.size === 0) return;
                            
                            try {
                              // Update all selected emails to read
                              const updatePromises = getActionEmailIds().map(async (emailId) => {
                                const email = emails.find(e => e.id === emailId) || (selectedEmailToView?.id === emailId ? selectedEmailToView : null);
                                if (!email) {
                                  console.error(`❌ Email not found in local state: ${emailId}`);
                                  return;
                                }
                                
                                // CRITICAL: Use emailAccount (recipient) not from (sender)
                                const emailAccount = email.emailAccount || (Array.isArray(email.to) ? email.to[0] : email.to) || email.from;
                                
                                console.log(`🔄 Marking email as read:`, {
                                  emailId,
                                  emailAccount,
                                  from: email.from,
                                  to: email.to,
                                  emailAccountField: email.emailAccount,
                                  fullEmail: email
                                });
                                
                                const result = await updateEmail(buildUpdatePayload({
                                  emailId,
                                  emailAccount: emailAccount,
                                  updates: {
                                    isRead: true
                                  },
                                  email
                                })).unwrap();
                                
                                console.log(`✅ Mark as read API response:`, result);
                                return result;
                              });
                              
                              await Promise.all(updatePromises);
                              
                              // Update local state for selected email if it's being viewed
                              if (selectedEmailToView && selectedEmails.has(selectedEmailToView.id)) {
                                setSelectedEmailToView({ ...selectedEmailToView, isRead: true });
                              }
                              
                              // Clear selection and refetch emails
                              setSelectedEmails(new Set());
                              await loadEmails();
                              
                              console.log('✅ Marked emails as read');
                            } catch (error: any) {
                              console.error('❌ Error marking emails as read:', error);
                              alert(`Failed to mark emails as read: ${error.message || 'Unknown error'}`);
                            }
                          }}
                          disabled={selectedEmails.size === 0 || isUpdatingEmail}
                          style={{ 
                            padding: '0.25rem 0.5rem',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: (selectedEmails.size === 0 || isUpdatingEmail) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          <EnvelopeOpen size={16} />
                        </Button>
                      </OverlayTrigger>
                      
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Add to blocklist</Tooltip>}
                      >
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            // TODO: Implement blocklist functionality
                            console.log('Add to blocklist:', Array.from(selectedEmails));
                          }}
                          style={{ 
                            padding: '0.25rem 0.5rem',
                            border: 'none',
                            backgroundColor: 'transparent'
                          }}
                        >
                          <PersonDash size={16} />
                        </Button>
                      </OverlayTrigger>
                      
                      {activeTab !== 'spam' && !isQuarantinedView && (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Mark as spam</Tooltip>}
                        >
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={async () => {
                            if (selectedEmails.size === 0) return;
                            
                            const emailIds = getActionEmailIds();
                            setOptimisticRemovedIds(prev => {
                              const next = new Set(prev);
                              emailIds.forEach(id => next.add(id));
                              return next;
                            });
                            setSelectedEmails(new Set());
                            
                            try {
                              const updatePromises = emailIds.map(async (emailId) => {
                                const email = emails.find(e => e.id === emailId) || (selectedEmailToView?.id === emailId ? selectedEmailToView : null);
                                if (!email) return;
                                
                                const emailAccount = email.emailAccount || (Array.isArray(email.to) ? email.to[0] : email.to) || email.from;
                                
                                const result = await updateEmail(buildUpdatePayload({
                                  emailId,
                                  emailAccount: emailAccount,
                                  updates: {
                                    isSpam: true,
                                    isArchived: false,
                                    isDeleted: false
                                  },
                                  email
                                })).unwrap();
                                
                                return result;
                              });
                              
                              await Promise.all(updatePromises);
                              
                              if (selectedEmailToView && emailIds.includes(selectedEmailToView.id)) {
                                setSelectedEmailToView({ ...selectedEmailToView, isSpam: true });
                              }
                              
                              await loadEmails();
                            } catch (error: any) {
                              console.error('❌ Error marking emails as spam:', error);
                              setOptimisticRemovedIds(prev => {
                                const next = new Set(prev);
                                emailIds.forEach(id => next.delete(id));
                                return next;
                              });
                              alert(`Failed to mark emails as spam: ${error.message || 'Unknown error'}`);
                            }
                          }}
                          disabled={selectedEmails.size === 0 || isUpdatingEmail}
                          style={{ 
                            padding: '0.25rem 0.5rem',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: (selectedEmails.size === 0 || isUpdatingEmail) ? 'not-allowed' : 'pointer'
                          }}
                          >
                            <span style={{ fontSize: '14px' }}>🚫</span>
                          </Button>
                        </OverlayTrigger>
                      )}
                      
                      {activeTab === 'spam' && (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Move to inbox (not spam)</Tooltip>}
                        >
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={async () => {
                              if (selectedEmails.size === 0) return;
                              
                              try {
                                // Update all selected emails to not spam
                                const updatePromises = Array.from(selectedEmails).map(async (emailId) => {
                                  const email = emails.find(e => e.id === emailId);
                                  if (!email) {
                                    console.error(`❌ Email not found in local state: ${emailId}`);
                                    return;
                                  }
                                  
                                  // CRITICAL: Use emailAccount (recipient) not from (sender)
                                  const emailAccount = email.emailAccount || (Array.isArray(email.to) ? email.to[0] : email.to) || email.from;
                                  
                                  const result = await updateEmail(buildUpdatePayload({
                                    emailId,
                                    emailAccount: emailAccount,
                                    updates: {
                                      isSpam: false
                                    },
                                    email
                                  })).unwrap();
                                  
                                  return result;
                                });
                                
                                await Promise.all(updatePromises);
                                
                                // Update local state for selected email if it's being viewed
                                if (selectedEmailToView && selectedEmails.has(selectedEmailToView.id)) {
                                  setSelectedEmailToView({ ...selectedEmailToView, isSpam: false });
                                }
                                
                                // Clear selection and refetch emails
                                setSelectedEmails(new Set());
                                await loadEmails();
                                
                                console.log('✅ Moved emails to inbox');
                              } catch (error: any) {
                                console.error('❌ Error moving emails to inbox:', error);
                                alert(`Failed to move emails to inbox: ${error.message || 'Unknown error'}`);
                              }
                            }}
                            disabled={selectedEmails.size === 0 || isUpdatingEmail}
                            style={{ 
                              padding: '0.25rem 0.5rem',
                              border: 'none',
                              backgroundColor: 'transparent',
                              cursor: (selectedEmails.size === 0 || isUpdatingEmail) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <span style={{ fontSize: '14px' }}>📥</span>
                          </Button>
                        </OverlayTrigger>
                      )}
                      
                      {activeTab !== 'trash' && !isQuarantinedView && (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Delete (move to Trash folder)</Tooltip>}
                          trigger={['hover', 'focus']}
                        >
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={async (e) => {
                            console.log('🗑️ Delete button onClick triggered');
                            e.preventDefault();
                            e.stopPropagation();
                            
                            // Delete: Move to trash (isDeleted = true, isArchived = false)
                            if (selectedEmails.size === 0) {
                              alert('Please select at least one email to delete');
                              return;
                            }
                            
                            const emailIds = getActionEmailIds();
                            const emailSnapshots = emailIds.map((emailId) => {
                              const email = filteredEmails.find(e => e.id === emailId) || (selectedEmailToView?.id === emailId ? selectedEmailToView : null);
                              return email ? { emailId, emailAccount: email.emailAccount, email } : null;
                            }).filter(Boolean) as { emailId: string; emailAccount: string; email: Email }[];

                            setOptimisticRemovedIds(prev => {
                              const next = new Set(prev);
                              emailIds.forEach(id => next.add(id));
                              return next;
                            });
                            setSelectedEmails(new Set());
                            
                            try {
                              await Promise.all(emailSnapshots.map(({ emailId, emailAccount, email }) =>
                                updateEmail(buildUpdatePayload({
                                  emailId,
                                  emailAccount,
                                  updates: { isDeleted: true, isArchived: false },
                                  email
                                })).unwrap()
                              ));

                              loadEmails();
                            } catch (error: any) {
                              console.error('Error deleting emails:', error);
                              setOptimisticRemovedIds(prev => {
                                const next = new Set(prev);
                                emailIds.forEach(id => next.delete(id));
                                return next;
                              });
                              alert(`Failed to delete emails: ${(error as any).message || 'Unknown error'}`);
                            }
                          }}
                          disabled={isUpdatingEmail}
                          style={{ 
                            padding: '0.25rem 0.5rem',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: isUpdatingEmail ? 'not-allowed' : 'pointer'
                          }}
                          >
                            <Trash size={16} />
                          </Button>
                        </OverlayTrigger>
                      )}

                      {/* Trash tab only: permanent delete with filled icon */}
                      {activeTab === 'trash' && (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Delete permanently</Tooltip>}
                          trigger={['hover', 'focus']}
                        >
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowTrashActionsModal(true)}
                            disabled={selectedEmails.size === 0}
                            style={{
                              padding: '0.25rem 0.5rem',
                              border: 'none',
                              backgroundColor: 'transparent',
                              cursor: selectedEmails.size === 0 ? 'not-allowed' : 'pointer',
                              color: '#dc3545',
                            }}
                          >
                            <TrashFill size={16} />
                          </Button>
                        </OverlayTrigger>
                      )}
                      
                    </div>
                  )}
                </div>
                )}

                {/* Email List */}
                {visibleEmails.map((email) => {
                  const blockedMatch = getBlockedSenderMatch(email);
                  const spamMatch = getSpamMatch(email);
                  return (
                  <div
                    key={email.id}
                    className={`unibox-email-item d-flex align-items-center py-3 border-bottom cursor-pointer ${
                      selectedEmails.has(email.id) ? 'selected' : ''
                    }`}
                    style={{ 
                      paddingLeft: '0.25rem', 
                      paddingRight: '0.5rem',
                      backgroundColor: '#ffffff',
                      // Hover effect: subtle gray background on hover
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                    }}
                    onClick={(e) => {
                      // Only handle click if it's not on the checkbox
                      if (e.target instanceof HTMLElement && !e.target.closest('input[type="checkbox"]')) {
                        handleEmailSelect(email.id, e);
                      }
                    }}
                    onDoubleClick={() => handleOpenEmail(email)}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={selectedEmails.has(email.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange(email.id, e);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Don't let the click bubble up to the email row
                      }}
                      className="me-3"
                      style={{ margin: 0, pointerEvents: 'auto' }}
                    />
                    
                    {/* Read/Unread Icon Indicator - Subtle visual distinction */}
                    <div className="me-2" style={{ minWidth: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {email.isRead ? (
                        <EnvelopeOpen className="text-muted" size={16} style={{ opacity: 0.6 }} />
                      ) : (
                        <EnvelopeFill className="text-primary" size={16} />
                      )}
                    </div>
                    
                    {email.isImportant && (
                      <div className="me-2">
                        <ExclamationTriangle className="text-warning" size={16} />
                      </div>
                    )}
                    
                    <div className="flex-grow-1">
                      {/* Sender name/email on one line */}
                      <div style={{ marginBottom: '0.125rem' }}>
                        <span 
                          style={{ 
                            fontSize: '0.75rem',
                            fontWeight: email.isRead ? 'normal' : 'bold',
                            color: email.isRead ? '#6c757d' : '#212529',
                            lineHeight: '1.2'
                          }}
                        >
                          {email.sender}
                        </span>
                        {blockedMatch && (
                          <Badge
                            bg="danger"
                            className="ms-2"
                            style={{ fontSize: '0.64rem', verticalAlign: 'middle' }}
                            title={
                              blockedMatch.type === 'email'
                                ? `Blocked email: ${blockedMatch.value}`
                                : `Blocked domain: ${blockedMatch.value}`
                            }
                          >
                            Blocked
                          </Badge>
                        )}
                        {spamMatch && (
                          <Badge
                            bg="warning"
                            text="dark"
                            className="ms-2"
                            style={{ fontSize: '0.64rem', verticalAlign: 'middle' }}
                            title={
                              spamMatch.source === 'auto_sender_list'
                                ? `Spam — auto-routed (sender previously marked): ${spamMatch.value}`
                                : `Spam — manually marked${spamMatch.value ? `: ${spamMatch.value}` : ''}`
                            }
                          >
                            Spam
                          </Badge>
                        )}
                      </div>
                      {/* Date/time below sender */}
                      <div className="mb-1">
                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                          {email.date ? formatEmailDateTime(email.date, true) : ''}
                        </small>
                      </div>
                      
                      <div 
                        style={{ 
                          fontSize: '0.75rem',
                          fontWeight: email.isRead ? 'normal' : 'bold',
                          color: email.isRead ? '#6c757d' : '#212529',
                          marginBottom: '0.25rem',
                          lineHeight: '1.2'
                        }}
                      >
                        {email.subject}
                      </div>
                      
                      <div className="text-muted small" style={{ fontSize: '0.875rem', opacity: email.isRead ? 0.7 : 0.9 }}>
                        {email.preview || getPreviewSnippet({ text: email.text, html: email.html }, 200)}
                      </div>
                    </div>
                    
                    {email.hasAttachment && (
                      <div className="ms-2">
                        <Badge bg="secondary" className="rounded-circle unibox-badge">
                          📎
                        </Badge>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </Col>

        {/* Right Column - Email Detail View (grows when filter section collapses) */}
        <Col xs={12} md={6} lg={isFilterSectionCollapsed ? 8 : 4} className="unibox-detail-col bg-white d-flex flex-column" style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
          {selectedEmailToView ? (
            <>
              {selectedEmailToView.isQuarantined && (
                <div className="p-2 mx-3 mt-2 rounded bg-warning bg-opacity-25 border border-warning" style={{ fontSize: '0.75rem' }}>
                  <strong>Quarantined</strong> — This email was not delivered to Inbox because the recipient address is not in Kross Global. Add the address to email accounts to receive future mail. Contact support to replay quarantined messages to Inbox.
                </div>
              )}
              {/* Email Detail Header - Back button inside overlay so it's always visible on mobile */}
              <div className="p-3 border-bottom" style={{ fontSize: '0.875rem' }}>
                {isMobile && (
                  <div className="mb-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setSelectedEmailToView(null)}
                      aria-label="Back to list"
                      style={{ padding: '0.25rem 0.5rem', minWidth: 'auto' }}
                    >
                      <ArrowLeft size={18} className="me-1" />
                      Back
                    </Button>
                  </div>
                )}
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="flex-grow-1">
                    {/* From Email */}
                    <div className="mb-2" style={{ fontSize: '0.75rem' }}>
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-muted" style={{ fontSize: '0.7rem' }}>From:</span>
                        <span className="fw-bold" style={{ fontSize: '0.75rem' }}>{selectedEmailToView.sender || selectedEmailToView.from || 'Unknown'}</span>
                        {(() => {
                          const blockedMatch = getBlockedSenderMatch(selectedEmailToView);
                          if (!blockedMatch) return null;
                          return (
                            <Badge
                              bg="danger"
                              style={{ fontSize: '0.62rem' }}
                              title={
                                blockedMatch.type === 'email'
                                  ? `Blocked email: ${blockedMatch.value}`
                                  : `Blocked domain: ${blockedMatch.value}`
                              }
                            >
                              Blocked
                            </Badge>
                          );
                        })()}
                        {(() => {
                          const spamMatch = getSpamMatch(selectedEmailToView);
                          if (!spamMatch) return null;
                          return (
                            <Badge
                              bg="warning"
                              text="dark"
                              style={{ fontSize: '0.62rem' }}
                              title={
                                spamMatch.source === 'auto_sender_list'
                                  ? `Spam — auto-routed (sender previously marked): ${spamMatch.value}`
                                  : `Spam — manually marked${spamMatch.value ? `: ${spamMatch.value}` : ''}`
                              }
                            >
                              Spam
                            </Badge>
                          );
                        })()}
                      </div>
                      {selectedEmailToView.senderAddress && selectedEmailToView.senderAddress !== selectedEmailToView.sender && (
                        <div className="text-muted mt-1" style={{ fontSize: '0.68rem', marginLeft: '2.15rem' }}>
                          Sender Address: {selectedEmailToView.senderAddress}
                        </div>
                      )}
                      {selectedEmailToView.returnPath && (
                        <div className="text-muted mt-1" style={{ fontSize: '0.68rem', marginLeft: '2.15rem' }}>
                          Return-Path: {selectedEmailToView.returnPath}
                        </div>
                      )}
                      {selectedEmailToView.replyTo && (
                        <div className="text-muted mt-1" style={{ fontSize: '0.68rem', marginLeft: '2.15rem' }}>
                          Reply-To: {selectedEmailToView.replyTo}
                        </div>
                      )}
                    </div>
                    
                    {/* To Email */}
                    {selectedEmailToView.to && (Array.isArray(selectedEmailToView.to) ? selectedEmailToView.to.length > 0 : selectedEmailToView.to) && (
                      <div className="mb-2" style={{ fontSize: '0.75rem' }}>
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted" style={{ fontSize: '0.7rem' }}>To:</span>
                          <span style={{ fontSize: '0.75rem' }}>
                            {Array.isArray(selectedEmailToView.to) 
                              ? Array.from(
                                  new Set(
                                    selectedEmailToView.to
                                      .map((x: string) => String(x || '').toLowerCase().trim())
                                      .filter(Boolean)
                                  )
                                ).join(', ')
                              : selectedEmailToView.to}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Date/Time - Above Subject */}
                    {selectedEmailToView.date && (
                      <div className="mb-2" style={{ fontSize: '0.75rem' }}>
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted" style={{ fontSize: '0.7rem' }}>Date/Time:</span>
                          <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                            {formatEmailDateTime(selectedEmailToView.date, true)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Subject */}
                    <div className="mb-2" style={{ fontSize: '0.75rem' }}>
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-muted" style={{ fontSize: '0.7rem' }}>Subject:</span>
                        <span className="mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{selectedEmailToView.subject || '(No Subject)'}</span>
                      </div>
                    </div>
                    
                    {selectedEmailToView.cc && (Array.isArray(selectedEmailToView.cc) ? selectedEmailToView.cc.length > 0 : selectedEmailToView.cc) && (
                      <div className="mb-2" style={{ fontSize: '0.875rem' }}>
                        <div className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>CC:</div>
                        <div style={{ fontSize: '0.875rem' }}>
                          {Array.isArray(selectedEmailToView.cc) 
                            ? selectedEmailToView.cc.join(', ') 
                            : selectedEmailToView.cc}
                        </div>
                      </div>
                    )}
                    
                    {selectedEmailToView.bcc && (Array.isArray(selectedEmailToView.bcc) ? selectedEmailToView.bcc.length > 0 : selectedEmailToView.bcc) && (
                      <div className="mb-2" style={{ fontSize: '0.875rem' }}>
                        <div className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>BCC:</div>
                        <div style={{ fontSize: '0.875rem' }}>
                          {Array.isArray(selectedEmailToView.bcc) 
                            ? selectedEmailToView.bcc.join(', ') 
                            : selectedEmailToView.bcc}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div
                  className="d-flex position-relative align-items-center flex-wrap"
                  style={{ gap: '0.1rem', rowGap: '0.25rem' }}
                >
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => handleReply(selectedEmailToView)}
                    style={detailActionButtonStyle}
                  >
                    Reply
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => handleReplyAll(selectedEmailToView)}
                    style={detailActionButtonStyle}
                  >
                    Reply All
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => handleForward(selectedEmailToView)}
                    style={detailActionButtonStyle}
                  >
                    Forward
                  </Button>
                  <div style={{ position: 'relative', display: 'inline-flex' }}>
                    {/* Status Label Dropdown - Same size as other action buttons */}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setShowDetailActionsMenu(false);
                        setShowCreateEntityMenu(false);
                        setEmailStatusDropdown(prev => ({ ...prev, [selectedEmailToView.id]: !prev[selectedEmailToView.id] }));
                      }}
                      style={detailActionButtonStyle}
                    >
                      <Lightning size={8} className="me-1" style={{ color: '#0dcaf0' }} />
                      {selectedEmailToView.status || statusFallbackLabel}
                      <ChevronDown size={6} className="ms-1" />
                    </Button>
                    {emailStatusDropdown[selectedEmailToView.id] && (
                      <div 
                        className="position-absolute bg-white border rounded shadow-lg p-2"
                        style={{ 
                          top: '100%', 
                          left: 0, 
                          zIndex: 1000, 
                          minWidth: '200px',
                          marginTop: '4px'
                        }}
                        onMouseLeave={() => setEmailStatusDropdown(prev => ({ ...prev, [selectedEmailToView.id]: false }))}
                      >
                        <InputGroup size="sm" className="mb-2">
                          <InputGroup.Text>
                            <Search size={12} />
                          </InputGroup.Text>
                          <Form.Control
                            placeholder="Search..."
                            value={statusSearchTerm}
                            onChange={(e) => setStatusSearchTerm(e.target.value)}
                            style={{ fontSize: '0.75rem' }}
                          />
                        </InputGroup>
                        <ListGroup variant="flush" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {alphabeticalStatusOptions
                            .filter(status => status.name.toLowerCase().includes(statusSearchTerm.toLowerCase()))
                            .map((status) => (
                              <ListGroup.Item
                                key={status.name}
                                className="d-flex align-items-center justify-content-between py-2 px-2 cursor-pointer"
                                onClick={async () => {
                                  setEmailStatusDropdown(prev => ({ ...prev, [selectedEmailToView.id]: false }));
                                  
                                  if (!selectedEmailToView) return;
                                  
                                  try {
                                    // Update email status in backend
                                    const updateResult = await updateEmail(buildUpdatePayload({
                                      emailId: selectedEmailToView.id,
                                      emailAccount: selectedEmailToView.emailAccount || selectedEmailToView.from,
                                      updates: {
                                        status: status.name
                                      },
                                      email: selectedEmailToView
                                    })).unwrap();
                                    
                                    console.log('✅ Status update API response:', updateResult);
                                    console.log('✅ Updated email from API:', updateResult?.email);
                                    console.log('✅ Status in response:', updateResult?.email?.status);
                                    
                                    // Update the email's status in the view immediately
                                    setSelectedEmailToView({ ...selectedEmailToView, status: status.name });
                                    
                                    console.log('✅ Status updated for email:', selectedEmailToView.id, 'to:', status.name);
                                    
                                    // Refetch emails to update the list - wait for it to complete
                                    await loadEmails();
                                    
                                    // DO NOT switch to status filter mode - user should manually navigate to status folder
                                    // The status is saved and will appear when user manually clicks on that status
                                    
                                    console.log('✅ Emails refetched, status updated (no filter change)');
                                  } catch (error: any) {
                                    console.error('❌ Failed to update email status:', error);
                                    alert(`Failed to update status: ${error.data?.message || error.message}`);
                                  }
                                }}
                                style={{ fontSize: '0.75rem' }}
                              >
                                <div className="d-flex align-items-center flex-grow-1">
                                  <Lightning size={12} className="me-2" style={{ color: status.color }} />
                                  <span className="text-truncate">{status.name}</span>
                                </div>
                                {isStatusLabelDeletable(status.name) && (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 ms-2"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      void handleDeleteStatusLabel(status.name);
                                    }}
                                    disabled={isUpdatingHiddenStatuses}
                                    title={`Delete ${status.name} status label`}
                                    style={{
                                      fontSize: '0.7rem',
                                      color: '#6c757d',
                                      textDecoration: 'none',
                                    }}
                                  >
                                    <Trash size={11} />
                                  </Button>
                                )}
                              </ListGroup.Item>
                            ))}
                          <ListGroup.Item
                            className="d-flex align-items-center py-2 px-2 cursor-pointer"
                            onClick={() => {
                              setEmailStatusDropdown(prev => ({ ...prev, [selectedEmailToView.id]: false }));
                              openCreateStatusLabelModal();
                            }}
                            style={{ fontSize: '0.75rem' }}
                          >
                            <Plus size={12} className="me-2 text-primary" />
                            <span className="text-primary">Create Label</span>
                          </ListGroup.Item>
                        </ListGroup>
                      </div>
                    )}
                  </div>
                  <div style={{ position: 'relative', display: 'inline-flex' }}>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        setEmailStatusDropdown(prev => ({ ...prev, [selectedEmailToView.id]: false }));
                        setShowDetailActionsMenu(false);
                        setShowCreateEntityMenu(prev => !prev);
                      }}
                      style={createEntityButtonStyle}
                    >
                      <Lightning size={8} className="me-1" style={{ color: CREATE_ENTITY_COLORS[createEntityType] }} />
                      {`Create ${createEntityType}`}
                      <ChevronDown size={6} className="ms-1" />
                    </Button>
                    {showCreateEntityMenu && (
                      <div
                        className="position-absolute bg-white border rounded shadow-lg p-2"
                        style={{
                          top: '100%',
                          left: 0,
                          zIndex: 1000,
                          minWidth: '200px',
                          marginTop: '4px'
                        }}
                        onMouseLeave={() => setShowCreateEntityMenu(false)}
                      >
                        <ListGroup variant="flush">
                          {CREATE_ENTITY_OPTIONS.map((entityTypeOption) => (
                            <ListGroup.Item
                              key={entityTypeOption}
                              className={`d-flex align-items-center py-2 px-2 cursor-pointer ${
                                createEntityType === entityTypeOption ? 'fw-semibold' : ''
                              }`}
                              onClick={() => handleOpenCreateLeadModal(selectedEmailToView, entityTypeOption)}
                              style={{
                                fontSize: '0.75rem',
                                backgroundColor: createEntityType === entityTypeOption ? '#f8f9fa' : undefined,
                              }}
                            >
                              <Lightning
                                size={12}
                                className="me-2"
                                style={{ color: CREATE_ENTITY_COLORS[entityTypeOption] }}
                              />
                              {`Create ${entityTypeOption}`}
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      </div>
                    )}
                  </div>
                  <div style={{ position: 'relative', display: 'inline-flex' }}>
                    <Button
                      ref={moreActionsButtonRef}
                      variant="outline-secondary"
                      size="sm"
                      aria-label="More actions"
                      onClick={() => {
                        setEmailStatusDropdown(prev => ({ ...prev, [selectedEmailToView.id]: false }));
                        setShowCreateEntityMenu(false);
                        setShowDetailActionsMenu(prev => !prev);
                      }}
                      style={{
                        ...detailActionButtonStyle,
                        minWidth: '22px',
                        maxWidth: '22px',
                        padding: '0.02rem 0'
                      }}
                    >
                      <ThreeDotsVertical size={9} />
                    </Button>
                    {showDetailActionsMenu && (() => {
                      const rect = moreActionsButtonRef.current?.getBoundingClientRect();
                      const menuStyle: React.CSSProperties = {
                        position: 'fixed',
                        top: rect ? rect.bottom + 4 : 100,
                        right: rect ? (window.innerWidth - rect.right) : 20,
                        zIndex: 9999,
                        width: '220px',
                      };
                      const itemStyle: React.CSSProperties = {
                        display: 'block',
                        width: '100%',
                        textAlign: 'left' as const,
                        fontSize: '0.75rem',
                        padding: '6px 10px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        lineHeight: '1.3',
                      };
                      const detailSenderEmail = extractSenderEmail(selectedEmailToView);
                      const detailSenderHasSystemAlertRule = Boolean(
                        detailSenderEmail &&
                        systemAlertFilters.some(
                          (rule) => String(rule?.condition?.from || '').toLowerCase().trim() === detailSenderEmail
                        )
                      );
                      return (
                      <div
                        className="bg-white border rounded shadow-lg py-1"
                        style={menuStyle}
                        onMouseLeave={() => setShowDetailActionsMenu(false)}
                      >
                        <button
                          style={{ ...itemStyle, color: '#0d6efd' }}
                          onMouseOver={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa'; }}
                          onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                          onClick={() => { setShowDetailActionsMenu(false); void handleMoveToSystemAlerts(selectedEmailToView); }}
                        >
                          <Folder size={11} className="me-2" />Move to System Alerts
                        </button>
                        <button
                          style={{ ...itemStyle, color: '#0d6efd' }}
                          onMouseOver={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa'; }}
                          onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                          onClick={() => { setShowDetailActionsMenu(false); void handleAlwaysMoveSenderToSystemAlerts(selectedEmailToView); }}
                        >
                          <ArrowClockwise size={11} className="me-2" />
                          {detailSenderHasSystemAlertRule
                            ? 'Stop automatically moving emails from this sender to System Alerts'
                            : 'Automatically move emails from this sender to System Alerts'}
                        </button>
                        <hr style={{ margin: '4px 0' }} />
                        {selectedEmailToView.isSpam === true ? (
                          <button
                            style={{ ...itemStyle, color: '#0d6efd' }}
                            onMouseOver={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa'; }}
                            onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                            onClick={() => { setShowDetailActionsMenu(false); void handleMarkAsNotSpam(selectedEmailToView); }}
                          >
                            <EnvelopeOpen size={11} className="me-2" />Mark as Not Spam
                          </button>
                        ) : (
                          <button
                            style={{ ...itemStyle, color: '#bf8700' }}
                            onMouseOver={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa'; }}
                            onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                            onClick={() => { setShowDetailActionsMenu(false); void handleMarkAsSpam(selectedEmailToView); }}
                          >
                            <ExclamationTriangle size={11} className="me-2" />Mark as Spam
                          </button>
                        )}
                        <hr style={{ margin: '4px 0' }} />
                        <button
                          style={{ ...itemStyle, color: '#dc3545' }}
                          onMouseOver={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa'; }}
                          onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                          onClick={() => { setShowDetailActionsMenu(false); handleBlockSender(selectedEmailToView); }}
                        >
                          <SlashCircle size={11} className="me-2" />Block Sender
                        </button>
                        <button
                          style={{ ...itemStyle, color: '#dc3545' }}
                          onMouseOver={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa'; }}
                          onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                          onClick={() => { setShowDetailActionsMenu(false); handleBlockContactDNP(selectedEmailToView); }}
                        >
                          <SlashCircle size={11} className="me-2" />Block + Contact DNP
                        </button>
                        <button
                          style={{ ...itemStyle, color: '#dc3545' }}
                          onMouseOver={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa'; }}
                          onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                          onClick={() => { setShowDetailActionsMenu(false); handleBlockAccountDNP(selectedEmailToView); }}
                        >
                          <SlashCircle size={11} className="me-2" />Block + Account DNP
                        </button>
                        <hr style={{ margin: '4px 0' }} />
                        <button
                          style={{ ...itemStyle, color: '#495057' }}
                          onMouseOver={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa'; }}
                          onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                          onClick={() => { setShowDetailActionsMenu(false); handleShowOriginal(selectedEmailToView); }}
                        >
                          Show original
                        </button>
                      </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {blockSenderMsg && (
                <div className="px-3 pt-2">
                  <Badge bg={blockSenderMsg.includes('already') ? 'warning' : 'success'} className="py-1 px-2" style={{ fontSize: '0.72rem', fontWeight: 500 }}>
                    <SlashCircle size={10} className="me-1" />{blockSenderMsg}
                  </Badge>
                </div>
              )}

              {/*
                Persistent Spam classification banner — mirrors the blocked
                banner pattern (same placement, same Badge shape, same icon)
                but stays visible for the lifetime of the message rather than
                being a transient action toast. Driven entirely by the
                persisted `isSpam` flag via getSpamMatch, so:
                  - manual Mark-as-Spam shows it immediately
                  - inbound auto-routed by sender list inherits it
                  - it remains visible when viewing the message from the
                    Spam folder, satisfying the visibility contract
                Tooltip distinguishes manual vs auto-routed so admins can
                audit false-positive risk at a glance.
              */}
              {(() => {
                const spamMatch = getSpamMatch(selectedEmailToView);
                if (!spamMatch) return null;
                const isAutoRouted = spamMatch.source === 'auto_sender_list';
                return (
                  <div className="px-3 pt-2">
                    <Badge
                      bg="warning"
                      text="dark"
                      className="py-1 px-2"
                      style={{ fontSize: '0.72rem', fontWeight: 500 }}
                      title={
                        isAutoRouted
                          ? `Auto-routed to Spam — sender was previously marked${spamMatch.value ? `: ${spamMatch.value}` : ''}`
                          : `Manually marked as Spam${spamMatch.value ? `: ${spamMatch.value}` : ''}`
                      }
                    >
                      <SlashCircle size={10} className="me-1" />
                      Spam{spamMatch.value ? ` ${spamMatch.value}` : ''}
                      {isAutoRouted && (
                        <span className="ms-1" style={{ opacity: 0.85 }}>
                          (auto-routed)
                        </span>
                      )}
                    </Badge>
                  </div>
                );
              })()}

              {systemAlertRuleToast && (
                <div
                  style={{
                    position: 'fixed',
                    right: 16,
                    bottom: 16,
                    zIndex: 1090,
                    maxWidth: 420,
                  }}
                >
                  <div
                    className="bg-dark text-white rounded shadow d-flex align-items-center justify-content-between"
                    style={{
                      padding: '0.6rem 0.7rem',
                      gap: 10,
                      fontSize: '0.75rem',
                    }}
                  >
                    <span style={{ lineHeight: 1.35 }}>
                      This email was moved to {SYSTEM_ALERTS_FOLDER_LABEL}. Future emails from <strong>{systemAlertRuleToast.senderEmail}</strong> will also go there.
                    </span>
                    <Button
                      variant="outline-light"
                      size="sm"
                      style={{ fontSize: '0.7rem', padding: '0.1rem 0.45rem', whiteSpace: 'nowrap' }}
                      onClick={() => { void handleUndoSystemAlertRule(); }}
                    >
                      Undo
                    </Button>
                  </div>
                </div>
              )}

              {/* Email Content - scrollable body */}
              <div
                className="flex-grow-1 overflow-auto p-3 unibox-scroll"
                style={{ fontSize: '0.875rem', minHeight: 0, overflowX: 'hidden' }}
              >
                {selectedEmailToView.html ? (
                  <iframe
                    key={selectedEmailToView.id}
                    title={`Email content ${selectedEmailToView.id}`}
                    srcDoc={emailHtmlSrcDoc}
                    sandbox="allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-same-origin"
                    onLoad={handleEmailIframeLoad}
                    scrolling="no"
                    style={{
                      width: '100%',
                      height: `${emailIframeHeight}px`,
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: '#fff'
                    }}
                  />
                ) : selectedEmailToView.text ? (
                  <div 
                    style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem', lineHeight: '1.5' }}
                    dangerouslySetInnerHTML={{ __html: makeLinksClickable(selectedEmailToView.text) }}
                  />
                ) : (
                  <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                    {selectedEmailToView.preview || 'No content available'}
                  </div>
                )}
                
                {selectedEmailToView.hasAttachment && (
                  <div className="mt-3 pt-3 border-top">
                    <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>
                      <Paperclip className="me-1" size={12} />
                      Has attachments{selectedEmailToView.attachmentCount ? ` (${selectedEmailToView.attachmentCount})` : ''}
                    </Badge>
                    {Array.isArray(selectedEmailToView.attachments) && selectedEmailToView.attachments.length > 0 ? (
                      <div className="mt-2 d-flex flex-column gap-2">
                        {selectedEmailToView.attachments.map((attachment, index) => {
                          const attachmentKey = String(attachment.s3Key || `${attachment.name || 'attachment'}-${index}`);
                          const isDownloading = downloadingAttachmentKey === attachment.s3Key;
                          const displayName = String(attachment.name || `attachment-${index + 1}.bin`);
                          const sizeLabel = formatAttachmentSize(attachment.size);
                          return (
                            <div
                              key={attachmentKey}
                              className="d-flex align-items-center justify-content-between border rounded px-2 py-1"
                              style={{ background: '#fbfbfb' }}
                            >
                              <div className="me-2" style={{ minWidth: 0 }}>
                                <div
                                  className="text-truncate"
                                  style={{ fontSize: '0.75rem', fontWeight: 600, maxWidth: '420px' }}
                                  title={displayName}
                                >
                                  {displayName}
                                </div>
                                {(sizeLabel || attachment.contentType) && (
                                  <div className="text-muted" style={{ fontSize: '0.68rem' }}>
                                    {[sizeLabel, attachment.contentType].filter(Boolean).join(' · ')}
                                  </div>
                                )}
                              </div>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleDownloadAttachment(selectedEmailToView, attachment)}
                                disabled={isDownloading || isLoadingEmailAttachment}
                                style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                              >
                                {isDownloading ? 'Downloading...' : 'Download'}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-muted mt-2" style={{ fontSize: '0.72rem' }}>
                        Attachment metadata is unavailable for this message.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
              <div className="text-center">
                <Envelope size={48} className="mb-3" />
                <h5>Select an email to view</h5>
                <p className="small">Click on an email from the list to read it here</p>
              </div>
            </div>
          )}
        </Col>
      </Row>

      {/* Show Original Modal (Gmail-style raw header/source view) */}
      <Modal
        show={showOriginalModal}
        onHide={() => setShowOriginalModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>Original Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingOriginalEmail ? (
            <div className="text-muted" style={{ fontSize: '0.875rem' }}>
              Loading original message...
            </div>
          ) : originalEmailError ? (
            <div className="text-danger" style={{ fontSize: '0.875rem' }}>
              {originalEmailError}
            </div>
          ) : (
            <>
              <div className="table-responsive mb-3">
                <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.8rem' }}>
                  <tbody>
                    <tr>
                      <th style={{ width: '170px', background: '#f8f9fa' }}>Message ID</th>
                      <td>{originalEmailData?.messageId || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#f8f9fa' }}>Created at</th>
                      <td>{originalEmailData?.date || selectedEmailToView?.date || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#f8f9fa' }}>From</th>
                      <td>{originalEmailData?.from || selectedEmailToView?.from || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#f8f9fa' }}>To</th>
                      <td>{originalEmailData?.to || (Array.isArray(selectedEmailToView?.to) ? selectedEmailToView?.to?.join(', ') : selectedEmailToView?.to) || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#f8f9fa' }}>Subject</th>
                      <td>{originalEmailData?.subject || selectedEmailToView?.subject || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#f8f9fa' }}>SPF</th>
                      <td>{originalEmailData?.spf || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#f8f9fa' }}>DKIM</th>
                      <td>{originalEmailData?.dkim || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#f8f9fa' }}>Return-Path</th>
                      <td>{originalEmailData?.returnPath || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#f8f9fa' }}>Delivered-To</th>
                      <td>{originalEmailData?.deliveredTo || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#f8f9fa' }}>In-Reply-To</th>
                      <td>{originalEmailData?.inReplyTo || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#f8f9fa' }}>References</th>
                      <td>{originalEmailData?.references || '—'}</td>
                    </tr>
                    <tr>
                      <th style={{ background: '#f8f9fa' }}>DMARC</th>
                      <td>{originalEmailData?.dmarc || '—'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                className="border rounded p-3"
                style={{
                  maxHeight: '420px',
                  overflow: 'auto',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  background: '#fafafa'
                }}
              >
                {String(originalEmailData?.rawSource || '').trim() || 'No raw source available for this message.'}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleDownloadOriginal}
            disabled={isLoadingOriginalEmail || !String(originalEmailData?.rawSource || '').trim()}
          >
            Download Original
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleCopyOriginal}
            disabled={isLoadingOriginalEmail || !String(originalEmailData?.rawSource || '').trim()}
          >
            Copy to clipboard
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setShowOriginalModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create CRM record from Email Modal */}
      <Modal
        show={showCreateLeadModal}
        onHide={handleCloseCreateLeadModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>{`Create ${createEntityType} from Email`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3" style={{ fontSize: '0.8rem' }}>
            {`Auto-filled from the email body (including forwarded content). Confirm or edit details before adding to ${getCreateEntityCollectionLabel(createEntityType)}.`}
          </p>
          {isCheckingDuplicateCandidates && (
            <div className="small text-muted mb-3">
              Checking existing Leads, Contacts, and Clients for duplicates...
            </div>
          )}
          {leadDraftForm.sourceFromLine && (
            <div className="small text-muted mb-3">
              <strong>Detected sender line:</strong> {leadDraftForm.sourceFromLine}
            </div>
          )}

          <Row className="g-2">
            <Col xs={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.75rem' }}>First name</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={leadDraftForm.firstName}
                  onChange={(e) => handleLeadDraftChange('firstName', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.75rem' }}>Last name</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={leadDraftForm.lastName}
                  onChange={(e) => handleLeadDraftChange('lastName', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.75rem' }}>Email</Form.Label>
                <Form.Control
                  size="sm"
                  type="email"
                  placeholder="name@company.com"
                  value={leadDraftForm.email}
                  onChange={(e) => handleLeadDraftChange('email', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.75rem' }}>Office</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="(555) 123-4567"
                  value={leadDraftForm.officePhone}
                  onChange={(e) => handleLeadDraftChange('officePhone', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.75rem' }}>Cell</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="(555) 987-6543"
                  value={leadDraftForm.cellPhone}
                  onChange={(e) => handleLeadDraftChange('cellPhone', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.75rem' }}>Title</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={leadDraftForm.title}
                  onChange={(e) => handleLeadDraftChange('title', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.75rem' }}>Organization</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={leadDraftForm.organization}
                  onChange={(e) => handleLeadDraftChange('organization', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.75rem' }}>Visibility</Form.Label>
                <Form.Select
                  size="sm"
                  value={leadDraftForm.visibility}
                  onChange={(e) => handleLeadDraftChange('visibility', e.target.value)}
                >
                  <option value="global">Global (tenant-wide)</option>
                  <option value="personal">Personal (private)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.75rem' }}>Company address</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={leadDraftForm.address}
                  onChange={(e) => handleLeadDraftChange('address', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.75rem' }}>Website</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={leadDraftForm.website}
                  onChange={(e) => handleLeadDraftChange('website', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.75rem' }}>LinkedIn profile</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={leadDraftForm.linkedInProfile}
                  onChange={(e) => handleLeadDraftChange('linkedInProfile', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCloseCreateLeadModal}
            disabled={isCreatingEntityFromEmail}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleCreateLeadFromEmail}
            disabled={
              isCreatingEntityFromEmail ||
              isCheckingDuplicateCandidates ||
              (!String(leadDraftForm.firstName || '').trim() &&
                !String(leadDraftForm.lastName || '').trim() &&
                !String(leadDraftForm.email || '').trim())
            }
          >
            {isCreatingEntityFromEmail ? 'Creating...' : `Create ${createEntityType}`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Duplicate check modal for create-from-email */}
      <Modal
        show={showDuplicateLeadModal}
        onHide={() => setShowDuplicateLeadModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>Possible Existing Person Found</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3" style={{ fontSize: '0.8rem' }}>
            We found matching records in Leads, Contacts, or Clients. Confirm whether this is the same person, merge, or create as a different person.
          </p>
          <div className="border rounded">
            <ListGroup variant="flush">
              {duplicateCandidates.map((candidate) => (
                <ListGroup.Item
                  key={candidate.key}
                  action
                  active={candidate.key === selectedDuplicateCandidateKey}
                  onClick={() => setSelectedDuplicateCandidateKey(candidate.key)}
                  style={{ fontSize: '0.8rem' }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="fw-semibold">{candidate.displayName}</div>
                    <Badge bg={candidate.type === 'Lead' ? 'secondary' : candidate.type === 'Contact' ? 'info' : 'primary'}>
                      {candidate.type}
                    </Badge>
                  </div>
                  <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                    {candidate.reason}
                    {candidate.email ? ` • ${candidate.email}` : ''}
                    {candidate.organization ? ` • ${candidate.organization}` : ''}
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowDuplicateLeadModal(false)}
            disabled={isCreatingEntityFromEmail}
          >
            Back
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleKeepExistingPersonNoAdd}
            disabled={isCreatingEntityFromEmail}
          >
            Same Person (Do Not Add)
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleMergeWithExistingRecord}
            disabled={isCreatingEntityFromEmail || !selectedDuplicateCandidate}
          >
            Merge Selected
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAddAsDifferentPerson}
            disabled={isCreatingEntityFromEmail}
          >
            Different Person, Add Anyway
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Compose Email Modal */}
      <Modal 
        show={showComposeModal} 
        onHide={handleComposeClose}
        size="lg"
        centered
        className="compose-modal"
      >
        <Modal.Header className="d-flex align-items-center justify-content-between">
          <Modal.Title>New Message</Modal.Title>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" size="sm" onClick={handleComposeClose}>
              <X size={16} />
            </Button>
          </div>
        </Modal.Header>
        <input
          ref={composeAttachmentInputRef}
          type="file"
          className="d-none"
          multiple
          onChange={handleComposeAttachmentInputChange}
          aria-hidden="true"
          tabIndex={-1}
        />
        
        <Modal.Body className="p-0">
          {/* From Account Selection */}
          <div className="p-3 border-bottom">
            <Form.Group>
              <Form.Label className="small fw-bold">From:</Form.Label>
              <Form.Select
                value={composeData.fromAccount}
                onChange={(e) => handleComposeChange('fromAccount', e.target.value)}
                size="sm"
              >
                {composeFromOptions.map((account: EmailAccount) => (
                  <option key={account.id} value={account.email}>
                    {account.displayName ? `${account.displayName} <${account.email}>` : account.email}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>

          {/* Recipients */}
          <div className="p-3 border-bottom">
            <Form.Group className="mb-2">
              <Form.Label className="small fw-bold">To:</Form.Label>
              <Form.Control
                type="email"
                placeholder="Recipients"
                value={composeData.to}
                onChange={(e) => handleComposeChange('to', e.target.value)}
                size="sm"
              />
            </Form.Group>
            
            <div className="d-flex gap-3">
              <Form.Group className="flex-grow-1">
                <Form.Label className="small fw-bold">Cc:</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Cc"
                  value={composeData.cc}
                  onChange={(e) => handleComposeChange('cc', e.target.value)}
                  size="sm"
                />
              </Form.Group>
              <Form.Group className="flex-grow-1">
                <Form.Label className="small fw-bold">Bcc:</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Bcc"
                  value={composeData.bcc}
                  onChange={(e) => handleComposeChange('bcc', e.target.value)}
                  size="sm"
                />
              </Form.Group>
            </div>
            {composeBlockedRecipients.length > 0 && (
              <div className="mt-2">
                <Badge bg="danger" style={{ fontSize: '0.68rem' }}>
                  Send Blocked
                </Badge>
                <div className="text-danger mt-1" style={{ fontSize: '0.72rem' }}>
                  Recipient(s) are on Block List:{' '}
                  {composeBlockedRecipients
                    .slice(0, 8)
                    .map((entry) => `${entry.address}${entry.type === 'domain' ? ` [domain ${entry.value}]` : ''}`)
                    .join(', ')}
                </div>
              </div>
            )}
          </div>

          {/* Subject */}
          <div className="p-3 border-bottom">
            <Form.Group>
              <div className="d-flex align-items-center justify-content-between">
                <Form.Label className="small fw-bold mb-0">Subject:</Form.Label>
                {composeConfidentialMode && (
                  <Badge bg="dark" style={{ fontSize: '0.65rem' }}>
                    Confidential
                  </Badge>
                )}
              </div>
              <Form.Control
                type="text"
                placeholder="Subject"
                value={composeData.subject}
                onChange={(e) => handleComposeChange('subject', e.target.value)}
                size="sm"
              />
            </Form.Group>
          </div>

          {/* Script Library */}
          <ScriptLibraryPanel
            tenantId={tenantIdForPrefs || ''}
            draft={{ subject: composeData.subject, body: composeData.body }}
            placeholderContext={{
              recipientFirstName: extractFirstNameFromAddressOrName(composeData.to),
              senderFullName:
                composeFromOptions.find(
                  (a: EmailAccount) => a.email === composeData.fromAccount
                )?.displayName || '',
              senderFirstName: extractFirstNameFromAddressOrName(
                composeFromOptions.find(
                  (a: EmailAccount) => a.email === composeData.fromAccount
                )?.displayName ||
                  composeData.fromAccount ||
                  ''
              ),
            }}
            senderContextLabel="From account above"
            resetSignal={showComposeModal}
            onLoad={({ subject, body }) => {
              handleComposeChange('subject', subject);
              handleComposeChange('body', body);
            }}
          />

          {/* Email Body */}
          <div className="p-3">
            <Form.Group>
              <Form.Control
                as="textarea"
                rows={8}
                ref={composeBodyRef}
                placeholder="Compose your message..."
                value={composeData.body}
                onChange={(e) => handleComposeChange('body', e.target.value)}
                className="border-0"
                style={{resize: 'none'}}
              />
            </Form.Group>
          </div>

          {composeAttachments.length > 0 && (
            <div className="px-3 pb-3 border-top">
              <div className="small fw-bold mb-2">
                Attachments ({composeAttachments.length})
              </div>
              <div className="d-flex flex-column gap-2">
                {composeAttachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="d-flex align-items-center justify-content-between border rounded px-2 py-1 bg-light"
                  >
                    <div className="text-truncate me-2" style={{ maxWidth: '85%' }}>
                      <Paperclip size={12} className="me-1" />
                      <span>{attachment.name}</span>
                      <span className="text-muted ms-1" style={{ fontSize: '0.72rem' }}>
                        ({formatAttachmentSize(attachment.size)})
                      </span>
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      type="button"
                      onClick={() => handleRemoveComposeAttachment(attachment.id)}
                      title="Remove attachment"
                    >
                      <X size={11} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>

        {/* Formatting Toolbar */}
        <div className="p-2 border-top bg-light">
          <div className="d-flex align-items-center gap-2">
            <Button variant="outline-secondary" size="sm" type="button" onClick={handleComposeBold} title="Bold">
              <strong>B</strong>
            </Button>
            <Button variant="outline-secondary" size="sm" type="button" onClick={handleComposeItalic} title="Italic">
              <em>I</em>
            </Button>
            <Button variant="outline-secondary" size="sm" type="button" onClick={handleComposeUnderline} title="Underline">
              <u>U</u>
            </Button>
            <div className="vr"></div>
            <Button
              variant="outline-secondary"
              size="sm"
              type="button"
              onClick={() => openComposeAttachmentPicker('*/*')}
              title="Attach files"
            >
              <Paperclip size={14} />
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              type="button"
              onClick={handleComposeInsertLink}
              title="Insert link"
            >
              <Link size={14} />
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              type="button"
              onClick={handleComposeInsertEmoji}
              title="Insert emoji"
            >
              <EmojiSmile size={14} />
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              type="button"
              onClick={() => openComposeAttachmentPicker('image/*')}
              title="Attach image"
            >
              <Image size={14} />
            </Button>
            <Button
              variant={composeConfidentialMode ? 'dark' : 'outline-secondary'}
              size="sm"
              type="button"
              onClick={handleComposeToggleConfidential}
              title="Toggle confidential subject prefix"
            >
              <Lock size={14} />
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              type="button"
              onClick={handleComposeInsertScheduleNote}
              title="Insert schedule note"
            >
              <Calendar size={14} />
            </Button>
            <div className="vr ms-auto"></div>
            <Button
              variant="outline-secondary"
              size="sm"
              type="button"
              onClick={handleComposeClearDraft}
              title="Clear draft"
            >
              <Trash size={14} />
            </Button>
          </div>
        </div>

        {/* Action Bar */}
        <Modal.Footer className="d-flex justify-content-between">
          <div className="d-flex gap-2">
            <Button 
              variant="primary" 
              onClick={() => { void handleSendEmail(); }}
              disabled={isSendingEmail || !composeData.to || !composeData.subject || composeBlockedRecipients.length > 0}
            >
              {isSendingEmail ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="me-2" size={16} />
                  Send
                </>
              )}
            </Button>
            <Button variant="outline-secondary" onClick={handleComposeClose}>
              Cancel
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Help Sidebar */}
      <Offcanvas
        show={showHelpSidebar}
        onHide={() => setShowHelpSidebar(false)}
        placement="end"
        style={{ width: '350px' }}
      >
        <Offcanvas.Header className="border-bottom">
          <Offcanvas.Title style={{ fontSize: '1rem', fontWeight: 600 }}>
            Unibox Help
          </Offcanvas.Title>
          <Button
            variant="link"
            onClick={() => setShowHelpSidebar(false)}
            style={{ padding: 0, marginLeft: 'auto', color: '#000' }}
          >
            <X size={20} />
          </Button>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ fontSize: '0.875rem' }}>
          <div className="mb-4">
            <h6 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.75rem' }}>
              Auto-Refresh
            </h6>
            <p style={{ fontSize: '0.875rem', lineHeight: '1.5', marginBottom: '0.5rem' }}>
              The <strong>Auto</strong> toggle switch enables automatic email refreshing.
            </p>
            <ul style={{ fontSize: '0.875rem', lineHeight: '1.6', paddingLeft: '1.25rem', marginBottom: '0.5rem' }}>
              <li>When <strong>ON</strong>: Emails refresh automatically every 30 seconds</li>
              <li>When <strong>OFF</strong>: Emails only refresh when you click the refresh button or manually reload</li>
            </ul>
            <p style={{ fontSize: '0.875rem', lineHeight: '1.5', color: '#6c757d', marginBottom: 0 }}>
              This is useful for monitoring incoming emails in real-time without manually refreshing the page.
            </p>
          </div>

          <div className="mb-4">
            <h6 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.75rem' }}>
              Refresh Button
            </h6>
            <p style={{ fontSize: '0.875rem', lineHeight: '1.5', marginBottom: 0 }}>
              Click the refresh button (circular arrow icon) to manually refresh your emails immediately. 
              The icon will spin while refreshing.
            </p>
          </div>

          <div className="mb-4">
            <h6 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.75rem' }}>
              Email Folders
            </h6>
            <ul style={{ fontSize: '0.875rem', lineHeight: '1.6', paddingLeft: '1.25rem', marginBottom: 0 }}>
              <li><strong>Inbox</strong>: Received emails</li>
              <li><strong>Sent</strong>: Emails you've sent</li>
              <li><strong>Archive</strong>: Archived emails</li>
              <li><strong>Spam</strong>: Unwanted or system emails</li>
              <li><strong>Trash</strong>: Deleted emails</li>
            </ul>
          </div>

          <div className="mb-4">
            <h6 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.75rem' }}>
              Internal mail delivery
            </h6>
            <p style={{ fontSize: '0.875rem', lineHeight: '1.5', marginBottom: '0.75rem' }}>
              Emails sent between your Unibox accounts appear in both Sent and the recipient's Inbox (e.g. Instantly warmup).
            </p>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => { handleTestInternalSend(); setShowHelpSidebar(false); }}
              title="Open compose with another internal account as recipient (quick diagnostic)"
              style={{ fontSize: '0.8rem' }}
            >
              Test internal send
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Rules Engine block confirmation Modal — tier-aware.
          Shown when the backend returns 400 RULES_ENGINE_BLOCKED on a send.
          The backend includes `details.overridable: boolean` to tell us which
          tier the block is in:
          - overridable === true  → SOFT-BLOCK (Active Opportunity / Current
            Client). Show Cancel + Send-anyway. Send-anyway retries with
            manualSendAnywayOverride: true.
          - overridable === false → HARD-STOP-NEW-OUTREACH (Dead Opportunity /
            Do Not Prospect). Show Cancel only — no Send-anyway path. The user
            must change the recipient or wait for them to reply first (Reply
            still bypasses everything via isManualReply).
          Reply / Reply All shouldn't normally reach this modal because they
          auto-bypass on the backend, but if they do the modal still displays
          safely (Send-anyway is hidden when overridable === false).
          The future hotfix/rules-engine-dnc-hardstop will add a third tier for
          Do Not Contact / Blocked compliance labels using a distinct
          HARD_STOP_BLOCKED error code that will block even Replies.
          See hotfix/rules-engine-harden. */}
      <Modal
        show={!!rulesEngineBlock}
        onHide={() => setRulesEngineBlock(null)}
        centered
        backdrop="static"
        keyboard={!rulesEngineBlock?.isOverriding}
        aria-labelledby="rules-engine-block-modal-title"
      >
        <Modal.Header closeButton={!rulesEngineBlock?.isOverriding}>
          <Modal.Title id="rules-engine-block-modal-title" style={{ fontSize: '1rem' }}>
            {rulesEngineBlock?.overridable
              ? 'Rules Engine blocked this send'
              : 'Rules Engine blocked this send (no override)'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: '0.875rem', marginBottom: '0.75rem' }}>
            {rulesEngineBlock?.overridable
              ? (rulesEngineBlock?.composeModeAtBlock === 'forward'
                  ? 'Forwarding to this recipient is blocked by your campaign-protection rules. Review the reason below before deciding.'
                  : 'Your campaign-protection rules block this recipient. Review the reason below before deciding.')
              : 'This recipient is in a hard-stop lifecycle stage. New outreach is not allowed — only a reply to a message they sent you can go through.'}
          </p>
          {rulesEngineBlock?.reasons && rulesEngineBlock.reasons.length > 0 ? (
            <ul style={{ fontSize: '0.8125rem', paddingLeft: '1.25rem', marginBottom: '0.75rem' }}>
              {rulesEngineBlock.reasons.slice(0, 8).map((r, idx) => {
                const trig = r.triggerNames.length > 0
                  ? ` via trigger${r.triggerNames.length > 1 ? 's' : ''} ${r.triggerNames.join(', ')}`
                  : '';
                const entityLabel = r.entityName
                  ? `${r.entityType}: ${r.entityName}`
                  : r.entityType;
                const tierLabel = r.tier === 'hard_stop_new_outreach'
                  ? ' [hard stop]'
                  : r.tier === 'soft'
                    ? ' [soft block]'
                    : '';
                return (
                  <li key={`${r.recipient}-${idx}`} style={{ marginBottom: '0.25rem' }}>
                    <strong>{r.recipient}</strong>
                    {entityLabel ? ` (${entityLabel})` : ''} matched {r.target} stage
                    {' '}<em>"{r.stage}"</em>{tierLabel}
                    {trig}
                  </li>
                );
              })}
              {rulesEngineBlock.reasons.length > 8 && (
                <li>(+{rulesEngineBlock.reasons.length - 8} more)</li>
              )}
            </ul>
          ) : (
            <p style={{ fontSize: '0.8125rem', color: '#6c757d', marginBottom: '0.75rem' }}>
              {rulesEngineBlock?.message}
            </p>
          )}
          {rulesEngineBlock?.overridable ? (
            <p style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: 0 }}>
              "Send anyway" sends this single email despite the rule. Campaigns and
              automated outreach will still be blocked.
            </p>
          ) : (
            <p style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: 0 }}>
              Hard-stop stages cannot be overridden from here. If this recipient replies
              to you, you can reply back — replies always bypass these rules.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setRulesEngineBlock(null)}
            disabled={rulesEngineBlock?.isOverriding}
          >
            Cancel
          </Button>
          {rulesEngineBlock?.overridable && (
            <Button
              variant="warning"
              size="sm"
              disabled={rulesEngineBlock?.isOverriding || isSendingEmail}
              onClick={() => {
                setRulesEngineBlock((prev) => (prev ? { ...prev, isOverriding: true } : prev));
                void handleSendEmail(true).finally(() => {
                  setRulesEngineBlock((prev) => (prev ? { ...prev, isOverriding: false } : prev));
                });
              }}
            >
              {rulesEngineBlock?.isOverriding ? 'Sending…' : 'Send anyway'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Create Status Label Modal */}
      <Modal show={showCreateStatusLabelModal} onHide={() => {
        setShowCreateStatusLabelModal(false);
        setNewStatusLabelName('');
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '0.875rem' }}>Create Status Label</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label style={{ fontSize: '0.75rem' }}>Status Label Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter status label name"
              value={newStatusLabelName}
              onChange={(e) => setNewStatusLabelName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && String(newStatusLabelName || '').trim()) {
                  (document.getElementById('kg-create-status-label-submit') as any)?.click?.();
                }
              }}
              style={{ fontSize: '0.75rem' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowCreateStatusLabelModal(false);
              setNewStatusLabelName('');
            }}
            style={{ fontSize: '0.75rem' }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            id="kg-create-status-label-submit"
            onClick={() => {
              void handleCreateStatusLabel();
            }}
            disabled={!String(newStatusLabelName || '').trim() || isSavingStatusLabel}
            style={{ fontSize: '0.75rem' }}
          >
            {isSavingStatusLabel ? 'Creating...' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Folder Modal */}
      <Modal show={showCreateLabelModal} onHide={() => {
        setShowCreateLabelModal(false);
        setNewLabelName('');
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '0.875rem' }}>Create Folder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label style={{ fontSize: '0.75rem' }}>Folder Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter folder name"
              value={newLabelName}
              onChange={(e) => setNewLabelName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newLabelName.trim()) {
                  // Submit via button handler for consistency
                  (document.getElementById('kg-create-folder-submit') as any)?.click?.();
                }
              }}
              style={{ fontSize: '0.75rem' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowCreateLabelModal(false);
              setNewLabelName('');
            }}
            style={{ fontSize: '0.75rem' }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            id="kg-create-folder-submit"
            onClick={() => {
              (async () => {
                const raw = String(newLabelName || '').trim();
                if (!raw) return;
                if (isReservedFolderName(raw)) {
                  alert('That folder name is reserved. Please choose a different name.');
                  return;
                }
                if (folderNameExists(raw)) {
                  alert('A folder with that name already exists.');
                  return;
                }
                const next = mergeUniqueFolders([...(folders || []), raw]);
                setFolders(next);
                setNewLabelName('');
                setShowCreateLabelModal(false);
                try {
                  await persistFolderPreferences(next);
                } catch (e: any) {
                  console.warn('Failed to save folders preference:', e);
                }
                // Optionally file currently selected emails into the new folder
                if (selectedEmails.size > 0) {
                  try {
                    const emailIds = Array.from(selectedEmails);
                    const updatePromises = emailIds.map(async (emailId) => {
                      const email = filteredEmails.find(e => e.id === emailId);
                      const emailAccount = (email as any)?.emailAccount || (Array.isArray((email as any)?.to) ? (email as any).to[0] : (email as any)?.to) || (email as any)?.from;
                      if (!emailAccount) return;
                      const payload = buildUpdatePayload({
                        emailId,
                        emailAccount,
                        updates: { folder: raw, isDeleted: false, isArchived: false, isSpam: false },
                        email,
                      });
                      await updateEmail(payload).unwrap();
                    });
                    await Promise.all(updatePromises);
                    setSelectedEmails(new Set());
                    setSelectedFolder(raw);
                    setFilterMode('folders');
                    loadEmails();
                  } catch (err: any) {
                    console.error('Failed to move emails to new folder:', err);
                    alert(`Failed to move emails to folder: ${err?.message || 'Unknown error'}`);
                  }
                } else {
                  setSelectedFolder(raw);
                  setFilterMode('folders');
                }
              })();
            }}
            disabled={!newLabelName.trim() || folderNameExists(newLabelName)}
            style={{ fontSize: '0.75rem' }}
          >
            Create
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Auto-Filter Modal */}
      <Modal
        show={showAutoFilterModal}
        onHide={() => {
          setShowAutoFilterModal(false);
          setEditingAutoFilterId(null);
          setAutoFilterDraft({ enabled: true, field: 'subject_or_body', matchType: 'contains', pattern: '', folder: '' });
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '0.875rem' }}>
            {editingAutoFilterId ? 'Edit Auto-Filter' : 'Create Auto-Filter'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '0.75rem' }}>Match</Form.Label>
            <div className="d-flex gap-2">
              <Form.Select
                size="sm"
                value={String(autoFilterDraft.field || 'subject_or_body')}
                onChange={(e) => setAutoFilterDraft((d) => ({ ...d, field: e.target.value as AutoFilterField }))}
                style={{ fontSize: '0.75rem' }}
              >
                <option value="subject_or_body">Subject or Body</option>
                <option value="subject">Subject only</option>
                <option value="body">Body only</option>
              </Form.Select>
              <Form.Select
                size="sm"
                value={String(autoFilterDraft.matchType || 'contains')}
                onChange={(e) => setAutoFilterDraft((d) => ({ ...d, matchType: e.target.value as AutoFilterMatchType }))}
                style={{ fontSize: '0.75rem' }}
              >
                <option value="contains">Contains</option>
                <option value="regex">Regex</option>
              </Form.Select>
            </div>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '0.75rem' }}>
              Pattern {autoFilterDraft.matchType === 'regex' ? '(regex)' : '(text)'}
            </Form.Label>
            <Form.Control
              size="sm"
              type="text"
              placeholder={autoFilterDraft.matchType === 'regex' ? '[A-Z0-9]{6,10}' : 'Warmup'}
              value={String(autoFilterDraft.pattern || '')}
              onChange={(e) => setAutoFilterDraft((d) => ({ ...d, pattern: e.target.value }))}
              style={{ fontSize: '0.75rem', fontFamily: autoFilterDraft.matchType === 'regex' ? 'monospace' : undefined }}
            />
            <div className="text-muted small mt-1" style={{ fontSize: '0.7rem' }}>
              "Contains" matches the exact text you type (case-insensitive, no auto token splitting). Regex uses case-insensitive matching too.
            </div>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '0.75rem' }}>Move to folder</Form.Label>
            <Form.Select
              size="sm"
              value={String(autoFilterDraft.folder || '')}
              onChange={(e) => setAutoFilterDraft((d) => ({ ...d, folder: e.target.value }))}
              style={{ fontSize: '0.75rem' }}
            >
              <option value="" disabled>Select a folder…</option>
              {(folders || []).map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Check
            type="switch"
            id="kg-auto-filter-enabled"
            label="Enabled"
            checked={Boolean(autoFilterDraft.enabled !== false)}
            onChange={(e) => setAutoFilterDraft((d) => ({ ...d, enabled: e.target.checked }))}
            style={{ fontSize: '0.8rem' }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowAutoFilterModal(false);
              setEditingAutoFilterId(null);
              setAutoFilterDraft({ enabled: true, field: 'subject_or_body', matchType: 'contains', pattern: '', folder: '' });
            }}
            style={{ fontSize: '0.75rem' }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!String(autoFilterDraft.pattern || '').trim() || !String(autoFilterDraft.folder || '').trim() || (folders || []).length === 0}
            onClick={async () => {
              const pattern = String(autoFilterDraft.pattern || '').trim();
              const folder = String(autoFilterDraft.folder || '').trim();
              const field = (autoFilterDraft.field || 'subject_or_body') as AutoFilterField;
              const matchType = (autoFilterDraft.matchType || 'contains') as AutoFilterMatchType;
              const enabled = Boolean(autoFilterDraft.enabled !== false);

              if (!pattern || !folder) return;

              // Validate regex early so rule doesn't "silently do nothing"
              if (matchType === 'regex') {
                try {
                  // eslint-disable-next-line no-new
                  new RegExp(pattern, 'i');
                } catch (e) {
                  alert('Invalid regex pattern. Please fix it and try again.');
                  return;
                }
              }

              const id = editingAutoFilterId || `rule_${Date.now()}_${Math.random().toString(16).slice(2)}`;
              const nextRule: AutoFilterRule = { id, enabled, field, matchType, pattern, folder };

              const next = editingAutoFilterId
                ? autoFilters.map((r) => (r.id === editingAutoFilterId ? nextRule : r))
                : [nextRule, ...autoFilters];

              setAutoFilters(next);
              setShowAutoFilterModal(false);
              setEditingAutoFilterId(null);
              setAutoFilterDraft({ enabled: true, field: 'subject_or_body', matchType: 'contains', pattern: '', folder: '' });
              try {
                await updateUserPreferences({ ...preferencesTenantWriteSpread(tenantIdForPrefs), preferences: { uniboxAutoRefresh: autoRefresh, uniboxFolders: folders, uniboxAutoFilters: next } }).unwrap();
              } catch (e: any) {
                console.warn('Failed to save auto-filters:', e);
              }
            }}
            style={{ fontSize: '0.75rem' }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Trash Actions Modal — permanently delete SELECTED emails only */}
      <Modal
        show={showTrashActionsModal}
        onHide={() => setShowTrashActionsModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>
            <TrashFill size={16} className="me-2 text-danger" />
            Delete selected permanently?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.875rem' }}>
          <p className="mb-2 fw-semibold">
            Permanently delete {selectedEmails.size} selected email{selectedEmails.size !== 1 ? 's' : ''}?
          </p>
          <p className="mb-3 text-danger" style={{ fontSize: '0.8rem' }}>
            This cannot be undone. Selected emails will be removed forever.
          </p>
          <hr className="my-3" />
          <div className="d-flex flex-column gap-2">
            <Button
              variant="link"
              size="sm"
              className="text-start text-danger p-0"
              style={{ fontSize: '0.8rem', textDecoration: 'none' }}
              onClick={() => {
                setShowTrashActionsModal(false);
                setShowEmptyTrashConfirm(true);
              }}
            >
              <TrashFill size={12} className="me-2" />
              Empty entire Trash
            </Button>
            <Button
              variant="link"
              size="sm"
              className="text-start text-muted p-0"
              style={{ fontSize: '0.8rem', textDecoration: 'none' }}
              onClick={() => {
                setShowTrashActionsModal(false);
                setShowTrashSettingsModal(true);
              }}
            >
              <GearFill size={12} className="me-2" />
              Trash settings
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowTrashActionsModal(false)}
            style={{ fontSize: '0.75rem' }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              const emailIds = getActionEmailIds();
              if (emailIds.length === 0) return;

              const keysToDelete = emailIds.map((emailId) => {
                const email: any = filteredEmails.find((e: any) => e.id === emailId)
                  || (selectedEmailToView?.id === emailId ? selectedEmailToView : null);
                const pk = String(email?.PK || email?.pk || email?.unifiedPk || '').trim();
                const sk = String(email?.SK || email?.sk || email?.unifiedSk || '').trim();
                return pk && sk ? { PK: pk, SK: sk } : null;
              }).filter((k): k is { PK: string; SK: string } => k !== null);

              if (keysToDelete.length === 0) {
                alert('Could not resolve email keys for deletion.');
                return;
              }

              // Flush any previous pending delete immediately
              if (pendingDeleteRef.current) flushPendingDelete();

              setOptimisticRemovedIds((prev) => {
                const next = new Set(prev);
                emailIds.forEach((id) => next.add(id));
                return next;
              });
              setSelectedEmails(new Set());
              setShowTrashActionsModal(false);

              const count = emailIds.length;
              const capturedKeys = [...keysToDelete];
              const capturedIds = [...emailIds];

              const toastId = toast(
                ({ closeToast }) => (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span>{count} email{count !== 1 ? 's' : ''} permanently deleted</span>
                    <button
                      onClick={() => { closeToast?.(); cancelPendingDelete(); }}
                      style={{
                        background: 'none', border: 'none', color: '#3b82f6',
                        fontWeight: 600, cursor: 'pointer', padding: '2px 8px',
                        fontSize: '0.875rem', whiteSpace: 'nowrap', textDecoration: 'underline'
                      }}
                    >
                      Undo
                    </button>
                  </div>
                ),
                { autoClose: 5000, hideProgressBar: false, closeOnClick: false, pauseOnHover: false, type: 'info' }
              );

              const deleteRef = permanentDeleteEmails;
              const refetchRef = loadEmails;

              const timerId = setTimeout(async () => {
                pendingDeleteRef.current = null;
                try {
                  await deleteRef({ emailIds: capturedKeys }).unwrap();
                  refetchRef();
                } catch (e: any) {
                  setOptimisticRemovedIds((prev) => {
                    const next = new Set(prev);
                    capturedIds.forEach((id) => next.delete(id));
                    return next;
                  });
                  const errorMsg = e?.data?.message || e?.error || e?.message || e?.status || 'Unknown error';
                  alert('Failed to permanently delete: ' + errorMsg);
                }
              }, 5000);

              pendingDeleteRef.current = { timerId, toastId, idsToRestore: capturedIds, type: 'permanent', capturedKeys };
            }}
            style={{ fontSize: '0.75rem' }}
          >
            Yes, Delete Permanently
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Empty Trash Confirmation Modal — GUARDRAIL G5: explicit wording */}
      <Modal
        show={showEmptyTrashConfirm}
        onHide={() => setShowEmptyTrashConfirm(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>
            <TrashFill size={16} className="me-2 text-danger" />
            Empty Trash?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.875rem' }}>
          <p className="mb-1 fw-semibold">This will permanently delete all trashed emails across all of your connected email accounts.</p>
          <p className="mb-0 text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowEmptyTrashConfirm(false)}
            style={{ fontSize: '0.75rem' }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              // Flush any previous pending delete immediately
              if (pendingDeleteRef.current) flushPendingDelete();

              // Capture all visible trash email IDs for optimistic removal
              const trashIds = filteredEmails.map((e: any) => e.id).filter(Boolean) as string[];

              setOptimisticRemovedIds((prev) => {
                const next = new Set(prev);
                trashIds.forEach((id) => next.add(id));
                return next;
              });
              setShowEmptyTrashConfirm(false);
              setSelectedEmails(new Set());

              const toastId = toast(
                ({ closeToast }) => (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span>Trash emptied</span>
                    <button
                      onClick={() => { closeToast?.(); cancelPendingDelete(); }}
                      style={{
                        background: 'none', border: 'none', color: '#3b82f6',
                        fontWeight: 600, cursor: 'pointer', padding: '2px 8px',
                        fontSize: '0.875rem', whiteSpace: 'nowrap', textDecoration: 'underline'
                      }}
                    >
                      Undo
                    </button>
                  </div>
                ),
                { autoClose: 5000, hideProgressBar: false, closeOnClick: false, pauseOnHover: false, type: 'info' }
              );

              const capturedIds = [...trashIds];
              const purgeRef = purgeTrash;
              const refetchRef = loadEmails;

              const timerId = setTimeout(async () => {
                pendingDeleteRef.current = null;
                try {
                  await purgeRef({}).unwrap();
                  refetchRef();
                } catch (e: any) {
                  setOptimisticRemovedIds((prev) => {
                    const next = new Set(prev);
                    capturedIds.forEach((id) => next.delete(id));
                    return next;
                  });
                  alert('Failed to empty trash: ' + (e?.data?.message || e?.message || 'Unknown error'));
                }
              }, 5000);

              pendingDeleteRef.current = { timerId, toastId, idsToRestore: capturedIds, type: 'purge' };
            }}
            style={{ fontSize: '0.75rem' }}
          >
            Yes, Empty Trash
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Trash Retention Settings Modal */}
      <Modal
        show={showTrashSettingsModal}
        onHide={() => setShowTrashSettingsModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>
            Trash Settings
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.875rem' }}>
          <Form.Group>
            <Form.Label className="fw-semibold" style={{ fontSize: '0.8rem' }}>
              Auto-delete trashed emails after
            </Form.Label>
            <Form.Select
              size="sm"
              value={
                ((userPreferencesData as any)?.preferences?.trashRetentionDays === null)
                  ? 'never'
                  : String((userPreferencesData as any)?.preferences?.trashRetentionDays || 14)
              }
              onChange={async (e) => {
                const val = e.target.value;
                const retentionDays = val === 'never' ? null : Number(val);
                try {
                  await updateUserPreferences({
                    ...preferencesTenantWriteSpread(tenantIdForPrefs),
                    preferences: { trashRetentionDays: retentionDays },
                  }).unwrap();
                  setShowTrashSettingsModal(false);
                } catch (err: any) {
                  console.error('Failed to save trash retention:', err);
                  alert('Failed to save: ' + (err?.data?.message || err?.message || 'Unknown error'));
                }
              }}
              style={{ fontSize: '0.8rem' }}
            >
              <option value="7">7 days</option>
              <option value="14">14 days (recommended)</option>
              <option value="30">30 days</option>
              <option value="never">Never auto-delete</option>
            </Form.Select>
            <Form.Text className="text-muted" style={{ fontSize: '0.72rem' }}>
              Emails in Trash older than this will be permanently deleted automatically.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
      </Modal>

    </Container>
  );
};

export default Unibox;
